/** @format */

import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { firstValueFrom } from 'rxjs';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  CreateWorkflowAssignmentRequest,
  WorkflowAssignmentResponse,
  WorkflowAuditLogEntry,
  WorkflowGroupSchedule,
  WorkflowGroupType,
  WorkflowResponse,
  WorkflowService,
} from 'src/app/services/workflow/workflow.service';
import { RoleRegistryService } from 'src/app/services/workflow/role-registry.service';
import { WorkflowRecoveryRecord, WorkflowRecoveryService } from 'src/app/services/workflow/workflow-recovery.service';
import { RefreshStateService } from 'src/app/services/refresh-state/refresh-state.service';
import { CompanyService } from 'src/app/services/company/company.service';
import { Company } from 'src/app/models/company.model';
import { PlantService } from 'src/app/services/plant/plant.service';
import { GroupService } from 'src/app/services/group/group.service';
import { Plant } from 'src/app/models/plant.model';

/** A selectable group in the Assign Workflow modal (user or temp-user group). */
interface AssignGroup {
  id: number;
  name: string;
  type: WorkflowGroupType;
}

interface GroupScheduleUi {
  enabled: boolean;
  expanded: boolean;
  unit: string;
  interval: string;
  days: string[];
  monthMode: 'specific' | 'firstlast';
  specificDay: string;
  firstOrLast: string;
  startDate: string;
  // Time fields hold whatever owl-date-time's timer emits (a Date), or '' when unset.
  startTime: any;
  endDate: string;
  endTime: any;
  overdueInterval: string;
  overdueUnit: string;
}
import {
  ACTION_TYPES,
  ActionBtn,
  ActionType,
  AuditAction,
  AuditEntry,
  blankStage,
  Condition,
  DropdownVariant,
  Field,
  FIELD_TYPES,
  FieldType,
  makeAction,
  makeField,
  makeNotificationRule,
  MirrorEntry,
  panelFieldTypes,
  NativeEntry,
  NotificationRule,
  Routing,
  Stage,
  StageEntry,
  ValidationResult,
  ValidationRule,
  Workflow,
  WorkflowMeta,
} from './workflow.models';

type DragPayload =
  | { source: 'palette'; kind: 'field' | 'action'; type: FieldType | ActionType }
  | { source: 'canvas'; entryKey: string }
  | null;

interface AuditChange {
  path: string;
  label: string;
  kind: 'snapshot' | 'added' | 'removed' | 'changed';
  before: any;
  after: any;
  /** Canvas location used by the draft review inspector. */
  target?: ReviewTarget;
}

type ReviewTargetKind =
  | 'workflow'
  | 'stages'
  | 'stage'
  | 'field'
  | 'form'
  | 'action'
  | 'actions'
  | 'routing'
  | 'final-output'
  | 'hidden-fields';

interface ReviewPosition {
  row: number;
  column: number;
}

interface ReviewTarget {
  kind: ReviewTargetKind;
  stage?: number;
  entityKey?: string;
  beforeEntity?: any;
  afterEntity?: any;
  beforePosition?: ReviewPosition;
  afterPosition?: ReviewPosition;
}

interface ReviewChangeCard {
  id: string;
  label: string;
  kind: 'added' | 'removed' | 'changed';
  target: ReviewTarget;
  changes: AuditChange[];
  view?: ReviewCardView;
  originalIndex?: number;
}

interface ReviewDetailRow {
  label: string;
  value?: string;
  values?: string[];
}

interface ReviewCardView {
  structured: boolean;
  summaryLines: string[];
  details: ReviewDetailRow[];
}

interface ReviewChangeGroup {
  id: string;
  label: string;
  cards: ReviewChangeCard[];
}

const AUDIT_ENTITY_LABELS: Record<string, string> = {
  workflow_template: 'Workflow Template',
  workflow_stage: 'Stage',
  routing_rule: 'Routing Rule',
  notification_config: 'Notifications',
};

const AUDIT_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  sku: 'SKU',
  tier: 'Tier',
  status: 'Status',
  version: 'Version',
  stageCount: 'Stage count',
  stage_count: 'Stage count',
  activeStage: 'Active stage',
  'routing.actorRoles': 'Actor roles',
  'routing.escalationTarget': 'Escalation target',
  'routing.timeBudget.value': 'Time budget',
  'routing.timeBudget.unit': 'Time budget unit',
  'routing.immutable': 'Immutable',
  'routing.secondApprover': '2nd approver',
  'routing.approvalTarget.type': 'Approval routing',
  'routing.approvalTarget.stage': 'Approval target stage',
  'routing.rejectionTarget.type': 'Rejection routing',
  'routing.conditionalRoute.enabled': 'Conditional routing',
  'routing.conditionalRoute.conditions': 'Conditional rules',
  'routing.notifications.transition': 'Transition notification',
  'routing.notifications.perItem': 'Per-item notification',
  'routing.notifications.reminder': 'Reminder notification',
  'routing.notifications.escalation': 'Auto-escalation',
  'routing.notifications.perItemRecipientField': 'Per-item recipient field',
  'routing.notifications.perItemEventLabel': 'Per-item event label',
  'routing.notifications.reminderSchedule': 'Reminder schedule',
  'routing.notifications.reminderTime': 'Reminder time',
  'routing.retention.store': 'Store records',
  'routing.retention.days': 'Retention (days)',
  'routing.retention.autoPurge': 'Auto-purge',
  'finalOutputs.pdf.enabled': 'PDF output',
  'finalOutputs.pdf.template': 'PDF template',
  'finalOutputs.email.enabled': 'Email output',
  'finalOutputs.email.recipients': 'Email recipients',
  rows: 'Form fields',
  actions: 'Stage actions',
  hiddenInherited: 'Hidden mirrored fields',
  inFlightCount: 'In-flight items',
  format: 'Format',
  count: 'Entries',
  filter: 'Filter',
};

const REVIEW_PROPERTY_LABELS: Record<string, string> = {
  name: 'Stage name',
  'routing.actorRoles': 'Actor role',
  'routing.escalationTarget': 'Escalation target role',
  'routing.timeBudget.value': 'Time budget',
  'routing.timeBudget.unit': 'Time budget unit',
  'routing.immutable': 'Submitted records immutable',
  'routing.secondApprover': 'Second approver stage',
  'routing.approvalTarget.type': 'Approval routing',
  'routing.approvalTarget.stage': 'Approval target stage',
  'routing.rejectionTarget.type': 'Rejection routing',
  'routing.conditionalRoute.enabled': 'Conditional routing',
  'routing.notifications.transition': 'Stage transition notification',
  'routing.notifications.perItem': 'Per-item notification',
  'routing.notifications.perItemRecipientField': 'Recipient field',
  'routing.notifications.perItemEventLabel': 'Trigger event label',
  'routing.notifications.reminder': 'Reminder notification',
  'routing.notifications.reminderSchedule': 'Reminder schedule',
  'routing.notifications.reminderTime': 'Reminder time',
  'routing.notifications.escalation': 'Escalation notification',
  'routing.notifications.rules': 'Notification channels',
  'routing.retention.store': 'Store records',
  'routing.retention.days': 'Retention window (days)',
  'routing.retention.autoPurge': 'Auto-purge',
  'finalOutputs.pdf.enabled': 'PDF report',
  'finalOutputs.pdf.template': 'PDF template',
  'finalOutputs.email.enabled': 'Email notification',
  'finalOutputs.email.recipients': 'Email recipients',
};

const REVIEW_FIELD_REF_PROPERTIES = new Set<string>([
  'routing.notifications.perItemRecipientField',
]);

interface ConfirmState {
  open: boolean;
  title: string;
  body: string;
  okLabel: string;
  okClass: string;
  kind: 'plain' | 'deactivate';
  inFlight: number;
  details: string[];
  onConfirm: (() => void) | null;
}

interface ReviewRevertDependency {
  label: string;
  clear: () => void;
}

@Component({
  standalone: false,
  selector: 'app-workflow-builder',
  templateUrl: './workflow-builder.component.html',
  styleUrls: ['./workflow-builder.component.css'],
})
export class WorkflowBuilderComponent implements OnInit, OnDestroy {
  /* ---- catalogues exposed to the template ---- */
  fieldTypes = FIELD_TYPES;
  actionTypes = ACTION_TYPES;
  /** Active actor roles offered when assigning stage actors (US-05 AC-01). */
  roles: string[] = [];

  /* ---- notification channel options ---- */
  notificationChannels: { value: NotificationRule['channel']; label: string }[] = [
    { value: 'in_app', label: 'In-app' },
    { value: 'email', label: 'Email' },
  ];
  notificationTriggers: { value: NotificationRule['trigger']; label: string }[] = [
    { value: 'entry', label: 'Stage entry' },
    { value: 'exit', label: 'Stage exit' },
    { value: 'escalation', label: 'Escalation' },
  ];
  /** True when the notification template service can't be reached. */
  notificationTemplatesUnavailable = false;
  /** Recipient roles (used by email rules) that currently resolve to no users — drives a live warning. */
  unresolvableEmailRoles = new Set<string>();

  paletteInput = panelFieldTypes('input');
  paletteAuto = panelFieldTypes('auto');
  paletteActions = (Object.keys(ACTION_TYPES) as ActionType[]).map((key) => ({
    key,
    meta: ACTION_TYPES[key],
  }));

  /* ---- core state ---- */
  wf!: Workflow;
  lastValidationPassed = false;
  workflowTemplateId: number | null = null;
  workflowVersionId: number | null = null;
  concurrencyToken: string | null = null;
  saving = false;

  /* ---- persisted-server baseline + in-memory working state ---- */
  private serverBaselineJson = '';
  private serverBaselineSnapshotJson = '';
  private serverBaselineValidationPassed = false;
  private lastValidatedWorkflowJson = '';
  private lastValidatedWorkflowPassed = false;

  /* ---- transient UI state ---- */
  drag: DragPayload = null;
  hover: string | null = null;
  paletteOpen = false; // mobile drawer
  loadMenuOpen = false;
  overflowMenuOpen = false;
  saveMenuOpen = false;

  /* slide-over panels */
  fieldPanelOpen = false;
  routingPanelOpen = false;
  validationPanelOpen = false;
  reviewPanelOpen = false;
  reviewChanges: AuditChange[] = [];
  reviewGroups: ReviewChangeGroup[] = [];
  reviewPreview: ReviewChangeCard | null = null;
  reviewPreviewPinned = false;
  private reviewStageBeforePreview: number | null = null;
  private reviewOpener: HTMLElement | null = null;
  private confirmOpener: HTMLElement | null = null;
  /* leave-with-unsaved-changes guard modal */
  leaveModalOpen = false;
  leaveChangeCount = 0;
  private pendingLeave: {
    promise: Promise<boolean>;
    resolve: (leave: boolean) => void;
  } | null = null;
  /** AC-06: true when the last Validate attempt could not reach the backend. */
  validationServiceDown = false;
  historyPanelOpen = false;
  cfgField: Field | null = null;
  actionCommentModalOpen = false;
  actionCommentText = '';
  actionCommentTarget: ActionBtn | null = null;

  histFilter: 'ALL' | AuditAction = 'ALL';
  histUser = '';
  histFrom = '';
  histTo = '';
  private readonly viewAuditLogPermission = 'View Audit Log';
  serverAuditLog: WorkflowAuditLogEntry[] = [];
  historyUserOptions: string[] = [];
  auditLoading = false;
  auditLoadError = '';

  /* confirm modal + toast */
  confirm: ConfirmState = this.emptyConfirm();
  deactivateReason = '';
  toastMsg = '';
  toastIcon = '';
  toastShow = false;
  toastAction: { label: string; run: () => void } | null = null;
  private toastTimer: any = null;
  isOffline = !navigator.onLine;
  recoveryStatus: 'none' | 'recovered' | 'conflict' = 'none';
  private recoveryRecord: WorkflowRecoveryRecord<Workflow> | null = null;
  private recoveryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly recoveryDebounceMs = 750;

  /* ---- Assign workflow modal ---- */
  assignModalOpen = false;
  scheduleModalOpen = false;
  assignSaving = false;
  companies: Company[] = [];
  companiesLoading = false;
  companyLoadError = '';
  accountHolderId: number | null = null;
  /** Clients = the account holder's plants. */
  clients: Plant[] = [];
  clientsLoading = false;
  clientsError = '';
  clientPlantId: number | null = null;
  /** Searchable multi-select of the client's groups (user + temp user groups). */
  assignGroups: AssignGroup[] = [];
  groupSearch = '';
  groupDropdownOpen = false;
  groupsLoading = false;
  groupsError = '';
  /** Composite keys ("<type>:<id>") of the selected groups. */
  selectedGroupKeys = new Set<string>();
  /** True once this workflow has been assigned, for the toolbar button indicator. */
  workflowAssigned = false;
  /**
   * All assignments already saved for this workflow, loaded when the modal opens.
   * Used to detect a re-save of the same account-holder/client pair (which replaces
   * that one assignment) versus a new pair (which adds another) — so multiple
   * account holder groups can be assigned without overwriting each other.
   */
  private existingAssignments: WorkflowAssignmentResponse[] = [];

  constructor(
    private workflowService: WorkflowService,
    private roleRegistry: RoleRegistryService,
    private companyService: CompanyService,
    private plantService: PlantService,
    private groupService: GroupService,
    private refreshState: RefreshStateService,
    private workflowRecovery: WorkflowRecoveryService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.roleRegistry.load().subscribe({
      next: () => (this.roles = this.roleRegistry.getActiveRoles()),
      error: () => (this.roles = this.roleRegistry.getActiveRoles()),
    });
    void this.initWorkflow();
  }

  private async initWorkflow(): Promise<void> {
    const meta = (history.state && history.state.workflowMeta) as WorkflowMeta | undefined;
    const workflowTemplateId = (history.state && history.state.workflowTemplateId) as number | undefined;
    const workflowSku = (history.state && history.state.workflowSku) as string | undefined;
    const shouldOpenValidationPanel = history.state?.openValidationPanel === true;
    const shouldOpenRoleAssignment = history.state?.openRoleAssignment === true;

    if (workflowTemplateId) {
      await this.loadWorkflowById(workflowTemplateId, meta);
    } else if (workflowSku) {
      await this.loadWorkflowBySku(workflowSku, meta);
    } else {
      this.startWorkflow(
        meta || { name: 'New Workflow', sku: this.generateSku(), tier: 'Managed', stageCount: 2 }
      );
    }

    this.clearLegacyWorkflowRecovery();
    // Reloading the builder is an explicit abandon action after the browser's
    // native confirmation. Always reopen the last server-saved workflow.
    await this.clearRecoveryCopy();

    this.openValidationPanelFromRoute(shouldOpenValidationPanel);
    this.openRoleAssignmentFromRoute(shouldOpenRoleAssignment);
  }

  ngOnDestroy(): void {
    this.clearReviewPreview();
    if (this.wf?.dirty) void this.clearRecoveryCopy();
  }

  private openValidationPanelFromRoute(shouldOpenValidationPanel: boolean): void {
    if (shouldOpenValidationPanel) {
      this.validateAndOpen();
    }
  }

  private openRoleAssignmentFromRoute(shouldOpenRoleAssignment: boolean): void {
    if (shouldOpenRoleAssignment && this.workflowTemplateId != null) {
      this.openRoleModal();
    }
  }

  private generateSku(): string {
    const suffix = Math.random().toString(36).slice(2, 8).toUpperCase();
    return `WF-${suffix}`;
  }

  /* ============================================================
     Workflow bootstrap
     ============================================================ */
  private startWorkflow(meta: WorkflowMeta): void {
    this.workflowTemplateId = null;
    this.workflowVersionId = null;
    this.wf = {
      name: meta.name,
      sku: meta.sku,
      tier: meta.tier,
      stageCount: meta.stageCount,
      activeStage: 1,
      stages: {},
      status: 'DRAFT',
      version: 1,
      finalOutputs: {
        pdf: { enabled: false, template: 'Default' },
        email: { enabled: false, recipients: [] },
      },
      inFlightCount: 0,
      lastValidation: null,
      dirty: false,
    };
    for (let i = 1; i <= meta.stageCount; i++) this.wf.stages[i] = blankStage(i);
    this.wf.dirty = false;
    this.captureServerBaseline();
  }

  private async loadWorkflowById(id: number, fallbackMeta?: WorkflowMeta): Promise<void> {
    try {
      const response = await firstValueFrom(this.workflowService.getById(id));
      this.applyWorkflowResponse(response);
    } catch {
      // A freshly created template has no saved version yet; seed the builder from
      // the handoff metadata and keep targeting the created template so the first
      // save creates version 1.
      this.startWorkflow(fallbackMeta || { name: 'New Workflow', sku: 'NEW-WORKFLOW', tier: 'Managed', stageCount: 2 });
      this.workflowTemplateId = id;
      if (!fallbackMeta) this.showToast('Unable to load workflow.', 'alert-triangle');
    }
  }

  private async loadWorkflowBySku(sku: string, fallbackMeta?: WorkflowMeta): Promise<void> {
    try {
      const response = await firstValueFrom(this.workflowService.getBySku(sku));
      this.applyWorkflowResponse(response);
    } catch {
      this.startWorkflow(fallbackMeta || { name: 'New Workflow', sku, tier: 'Managed', stageCount: 2 });
      if (!fallbackMeta) this.showToast('Unable to load workflow.', 'alert-triangle');
    }
  }

  private applyWorkflowResponse(response: WorkflowResponse): void {
    this.workflowTemplateId = response.workflowTemplateId;
    this.workflowVersionId = response.workflowVersionId;
    this.concurrencyToken = response.updatedAt;
    this.wf = this.reviveWorkflow(response.definition);
    this.wf.name = response.name;
    this.wf.sku = response.sku;
    this.wf.tier = response.tier as any;
    this.wf.version = response.version;
    this.wf.status = response.status as any;
    this.wf.stageCount = response.stageCount;
    this.wf.activeStage = response.activeStage || 1;
    this.wf.lastValidation = response.lastValidation;
    this.wf.inFlightCount = response.inFlightCount || 0;
    this.wf.dirty = false;
    this.lastValidationPassed = !!response.lastValidation?.passed;
    this.captureServerBaseline();
    this.clearLegacyWorkflowRecovery();
    if (this.historyPanelOpen) void this.loadAuditLog();
  }

  private reviveWorkflow(definition: any): Workflow {
    const workflow = definition as Workflow;
    workflow.stages = workflow.stages || {};
    for (const key of Object.keys(workflow.stages)) {
      const stage = workflow.stages[key as any];
      stage.hiddenInherited = new Set(Array.isArray(stage.hiddenInherited) ? stage.hiddenInherited : []);
      const routing: any = stage.routing || {};
      if (!Array.isArray(routing.actorRoles)) {
        routing.actorRoles = routing.actorRole ? [routing.actorRole] : [];
        delete routing.actorRole;
      }
      if (!routing.approvalTarget) routing.approvalTarget = { type: 'next', stage: null };
      if (!routing.rejectionTarget) routing.rejectionTarget = { type: '' };
      if (!routing.notifications) routing.notifications = {};
      if (!Array.isArray(routing.notifications.rules)) routing.notifications.rules = [];
      stage.routing = routing;

      // Carry over per-field guard/visibility config so saved submission guards rehydrate
      // (older definitions predating these properties default cleanly instead of crashing
      // the field options panel).
      for (const row of Array.isArray(stage.rows) ? stage.rows : []) {
        for (const entry of Array.isArray(row) ? row : []) {
          const field: any = entry && (entry as any).kind ? (entry as any).field : entry;
          if (!field || typeof field !== 'object') continue;
          if (!field.guard || typeof field.guard !== 'object') field.guard = { enabled: false, keys: [] };
          if (!Array.isArray(field.guard.keys)) field.guard.keys = [];
          if (!field.visibility || typeof field.visibility !== 'object') {
            field.visibility = { enabled: false, conditions: [] };
          }
          if (!Array.isArray(field.visibility.conditions)) field.visibility.conditions = [];
        }
      }
    }
    return workflow;
  }

  /* ============================================================
     Audit + dirty tracking
     ============================================================ */
  private nowISO(): string {
    return new Date().toISOString();
  }
  markDirty(): void {
    const currentWorkflowJson = this.currentWorkflowJson();
    const dirty = currentWorkflowJson !== this.serverBaselineJson;
    this.wf.dirty = dirty;
    this.lastValidationPassed =
      currentWorkflowJson === this.lastValidatedWorkflowJson && this.lastValidatedWorkflowPassed;
    this.stampChangeTimes(currentWorkflowJson);
    if (this.routingPanelOpen && this.routingOpenedFromReview) this.routingEditedInReview = true;
    // A tablet/mobile review can be pinned after the drawer closes. Keep that
    // preview synchronized as the user edits or restores the highlighted item.
    if (this.reviewPanelOpen || this.reviewPreviewPinned) this.refreshReviewChanges();
    if (dirty) this.scheduleRecoveryCopy();
    else void this.clearRecoveryCopy();
  }

  private recoveryIdentity(): { userId: string; tenantId?: string; workflowId: string; baselineVersion: number | string } {
    return {
      userId: localStorage.getItem('email')?.trim().toLowerCase() || 'anonymous',
      tenantId: localStorage.getItem('companyId') || localStorage.getItem('accountHolderId') || undefined,
      workflowId: String(this.workflowTemplateId ?? this.wf?.sku ?? 'new'),
      baselineVersion: this.wf?.version ?? 0,
    };
  }

  private scheduleRecoveryCopy(): void {
    if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
    this.recoveryTimer = setTimeout(() => void this.flushRecoveryCopy(), this.recoveryDebounceMs);
  }

  private async flushRecoveryCopy(): Promise<void> {
    if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
    this.recoveryTimer = null;
    if (!this.wf?.dirty) return;
    const identity = this.recoveryIdentity();
    try {
      await this.workflowRecovery.put({
        ...identity,
        savedAt: new Date().toISOString(),
        workingSnapshot: this.serializeWorkflow(this.wf) as Workflow,
      });
    } catch {
      // Recovery is best-effort; editing must remain available if device storage is unavailable.
    }
  }

  private async restoreRecoveryCopy(): Promise<void> {
    if (!this.wf) return;
    const identity = this.recoveryIdentity();
    try {
      const records = await this.workflowRecovery.find<Workflow>(identity.userId, identity.tenantId, identity.workflowId);
      const record = records[0];
      if (!record) return;
      this.recoveryRecord = record;
      if (String(record.baselineVersion) !== String(identity.baselineVersion)) {
        this.recoveryStatus = 'conflict';
        return;
      }
      this.wf = this.reviveWorkflow(record.workingSnapshot);
      this.wf.dirty = true;
      this.recoveryStatus = 'recovered';
      this.stampChangeTimes(this.currentWorkflowJson());
      this.showToast('Unsaved changes recovered from this device.', 'alert-triangle', {
        label: 'Review changes', run: () => this.openReview(),
      });
    } catch {
      // A corrupt/unavailable recovery store must not block the saved server workflow.
    }
  }

  async discardRecoveryConflict(): Promise<void> {
    if (this.recoveryRecord) await this.workflowRecovery.remove(this.recoveryRecord.key).catch(() => undefined);
    this.recoveryRecord = null;
    this.recoveryStatus = 'none';
  }

  reviewRecoveryConflict(): void {
    if (!this.recoveryRecord) return;
    this.wf = this.reviveWorkflow(this.recoveryRecord.workingSnapshot);
    this.wf.dirty = true;
    this.recoveryStatus = 'recovered';
    this.openReview();
  }

  private async clearRecoveryCopy(): Promise<void> {
    const identity = this.recoveryIdentity();
    const key = this.workflowRecovery.buildKey(identity.userId, identity.tenantId, identity.workflowId, identity.baselineVersion);
    await this.workflowRecovery.remove(key).catch(() => undefined);
    if (this.recoveryRecord?.key === key) this.recoveryRecord = null;
    this.recoveryStatus = 'none';
  }

  /* ============================================================
     In-memory review timing + legacy recovery-key cleanup
     ============================================================ */
  private changeTimes: Record<string, number> = {};
  private changeValueCache: Record<string, string> = {};
  private lastStampedJson = '';

  private stampChangeTimes(currentJson: string): void {
    if (!this.wf || !this.serverBaselineJson || currentJson === this.lastStampedJson) return;
    this.lastStampedJson = currentJson;
    const now = Date.now();
    const seen = new Set<string>();
    for (const change of this.unsavedChanges()) {
      seen.add(change.path);
      const sig = JSON.stringify(change.after ?? null);
      if (this.changeValueCache[change.path] !== sig) {
        this.changeTimes[change.path] = now;
        this.changeValueCache[change.path] = sig;
      }
    }
    for (const key of Object.keys(this.changeTimes)) {
      if (!seen.has(key)) {
        delete this.changeTimes[key];
        delete this.changeValueCache[key];
      }
    }
  }

  private legacyWorkflowRecoveryKey(): string {
    return `workflow-builder:${this.wf?.sku ?? 'new'}`;
  }

  private captureServerBaseline(): void {
    this.serverBaselineValidationPassed = !!this.wf?.lastValidation?.passed;
    this.serverBaselineSnapshotJson = this.wf ? JSON.stringify(this.serializeWorkflow(this.wf)) : '';
    this.serverBaselineJson = this.currentWorkflowJson();
    this.lastValidatedWorkflowJson = this.serverBaselineJson;
    this.lastValidatedWorkflowPassed = this.serverBaselineValidationPassed;
    this.changeTimes = {};
    this.changeValueCache = {};
    this.lastStampedJson = '';
  }

  private currentWorkflowJson(): string {
    return this.wf ? JSON.stringify(this.comparableWorkflow(this.wf)) : '';
  }

  private recordValidationResult(passed: boolean): void {
    this.lastValidatedWorkflowJson = this.currentWorkflowJson();
    this.lastValidatedWorkflowPassed = passed;
    this.lastValidationPassed = passed;
  }

  private serializeWorkflow(workflow: Workflow): any {
    const cleaned = this.cleanForSnapshot(workflow);
    if (cleaned && typeof cleaned === 'object') delete cleaned.dirty;
    return cleaned;
  }

  private comparableWorkflow(workflow: Workflow): any {
    const cleaned = this.serializeWorkflow(workflow);
    if (!cleaned || typeof cleaned !== 'object') return cleaned;

    delete cleaned.activeStage;
    delete cleaned.inFlightCount;
    delete cleaned.lastValidation;
    delete cleaned.auditLog;

    for (const stage of Object.values(cleaned.stages || {}) as any[]) {
      stage.name = stage.name ?? '';
      stage.hiddenInherited = this.sortedUniqueStrings(stage.hiddenInherited);

      const routing = stage.routing;
      if (routing) {
        routing.actorRoles = this.sortedUniqueStrings(routing.actorRoles);
        routing.immutable = !!routing.immutable;
        routing.secondApprover = !!routing.secondApprover;
        routing.escalationTarget = routing.escalationTarget ?? '';
        if (routing.approvalTarget?.type !== 'stage' && routing.approvalTarget) {
          routing.approvalTarget.stage = null;
        }
        if (routing.rejectionTarget) routing.rejectionTarget.type = routing.rejectionTarget.type ?? '';
        if (routing.conditionalRoute) {
          routing.conditionalRoute.enabled = !!routing.conditionalRoute.enabled;
          if (!routing.conditionalRoute.enabled) routing.conditionalRoute.conditions = [];
        }
        if (routing.notifications) {
          routing.notifications.transition = !!routing.notifications.transition;
          routing.notifications.perItem = !!routing.notifications.perItem;
          routing.notifications.reminder = !!routing.notifications.reminder;
          routing.notifications.escalation = !!routing.notifications.escalation;
          if (!routing.notifications.perItem) {
            routing.notifications.perItemRecipientField = '';
            routing.notifications.perItemEventLabel = '';
          }
          if (!routing.notifications.reminder) {
            routing.notifications.reminderSchedule = '';
            routing.notifications.reminderTime = '';
          }
        }
        if (routing.retention) {
          routing.retention.store = !!routing.retention.store;
          routing.retention.autoPurge = !!routing.retention.autoPurge;
          if (!routing.retention.store) {
            routing.retention.days = 0;
            routing.retention.autoPurge = false;
          }
        }
      }

      for (const action of Array.isArray(stage.actions) ? stage.actions : []) {
        action.requireComment = !!action.requireComment;
        action.commentPrompt = action.requireComment ? (action.commentPrompt ?? '') : '';
      }

      for (const row of Array.isArray(stage.rows) ? stage.rows : []) {
        for (const entry of Array.isArray(row) ? row : []) {
          const field = entry?.kind === 'native' ? entry.field : entry?.kind === 'mirror' ? null : entry;
          if (!field || typeof field !== 'object') continue;

          field.label = field.label ?? '';
          field.placeholder = field.placeholder ?? '';
          field.required = !!field.required;
          if (field.type === 'dropdown') field.dropdownVariant = field.dropdownVariant || 'dropdown';
          else delete field.dropdownVariant;

          if (field.fileCfg) field.fileCfg.perFileNotes = !!field.fileCfg.perFileNotes;

          field.guard = field.guard || { enabled: false, keys: [] };
          field.guard.enabled = !!field.guard.enabled;
          field.guard.keys = field.guard.enabled ? this.sortedUniqueStrings(field.guard.keys) : [];

          field.visibility = field.visibility || { enabled: false, conditions: [] };
          field.visibility.enabled = !!field.visibility.enabled;
          if (!field.visibility.enabled) field.visibility.conditions = [];
        }
      }
    }

    if (cleaned.finalOutputs?.email) {
      cleaned.finalOutputs.email.enabled = !!cleaned.finalOutputs.email.enabled;
      cleaned.finalOutputs.email.recipients = cleaned.finalOutputs.email.enabled
        ? this.sortedUniqueStrings(cleaned.finalOutputs.email.recipients)
        : [];
    }
    if (cleaned.finalOutputs?.pdf) {
      cleaned.finalOutputs.pdf.enabled = !!cleaned.finalOutputs.pdf.enabled;
      if (!cleaned.finalOutputs.pdf.enabled) cleaned.finalOutputs.pdf.template = 'Default';
    }

    return this.sortObjectKeys(cleaned);
  }

  private sortedUniqueStrings(values: any): string[] {
    if (!Array.isArray(values)) return [];
    return Array.from(new Set(values.map((value) => String(value)))).sort((a, b) => a.localeCompare(b));
  }

  private sortObjectKeys(value: any): any {
    if (Array.isArray(value)) return value.map((item) => this.sortObjectKeys(item));
    if (value && typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce((acc, key) => {
          acc[key] = this.sortObjectKeys(value[key]);
          return acc;
        }, {} as any);
    }
    return value;
  }

  private cleanForSnapshot(value: any): any {
    if (value instanceof Set) return Array.from(value);
    if (typeof value === 'function') return undefined;
    if (typeof value === 'number' && !Number.isFinite(value)) return null;
    if (Array.isArray(value)) return value.map((item) => this.cleanForSnapshot(item));
    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((acc, key) => {
        const next = this.cleanForSnapshot(value[key]);
        if (next !== undefined) acc[key] = next;
        return acc;
      }, {} as any);
    }
    return value;
  }

  private clearLegacyWorkflowRecovery(): void {
    if (!this.wf) return;
    this.refreshState.remove(this.legacyWorkflowRecoveryKey());
  }

  hasUnsavedChanges(): boolean {
    if (!this.wf) return false;
    return this.currentWorkflowJson() !== this.serverBaselineJson;
  }

  unsavedChanges(): AuditChange[] {
    if (!this.wf || !this.serverBaselineJson) return [];
    let before: any;
    try {
      before = JSON.parse(this.serverBaselineJson);
    } catch {
      return [];
    }
    const after = this.comparableWorkflow(this.wf);
    return this.diffObjects('', before, after).filter((c) => c.kind !== 'snapshot');
  }

  openReview(): void {
    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.reviewOpener = document.getElementById('wf-more-options') || activeElement;
    this.reviewPreviewPinned = false;
    this.refreshReviewChanges();
    this.reviewPanelOpen = true;
    this.overflowMenuOpen = false;
    this.changeDetector.detectChanges();
    const firstControl = document.querySelector<HTMLElement>('.review-card-main, .review-close');
    firstControl?.focus();
  }

  closeReview(): void {
    this.clearReviewPreview();
    this.reviewPanelOpen = false;
    this.reviewOpener?.focus();
    this.reviewOpener = null;
  }

  saveFromReview(): void {
    this.closeReview();
    this.saveDraftFromMenu();
  }

  discardFromReview(): void {
    this.closeReview();
    void this.discardUnsavedChanges();
  }

  private refreshReviewChanges(): void {
    const targetedChanges = this.unsavedChanges().map((change) => ({
      ...change,
      target: change.target || this.inferReviewTarget(change.path, change),
    }));
    this.reviewChanges = targetedChanges.filter((change) => {
      if (change.target?.kind !== 'hidden-fields') return true;
      return !targetedChanges.some(
        (candidate) => candidate.target?.kind === 'field' &&
          candidate.target.stage === change.target?.stage &&
          (candidate.target.beforeEntity?.kind === 'mirror' || candidate.target.afterEntity?.kind === 'mirror')
      );
    });
    this.reviewChanges = this.reviewChanges.filter((change) => {
      if (change.target?.kind !== 'stages') return true;
      return !this.reviewChanges.some(
        (candidate) => candidate.target?.kind === 'stage' &&
          /^stages\.\d+$/.test(candidate.path)
      );
    });
    this.reviewGroups = this.buildReviewGroups(this.reviewChanges);
    this.reviewGroups.forEach((group) =>
      group.cards.forEach((card) => (card.view = this.buildReviewCardView(card)))
    );

    if (this.reviewPreview) {
      const current = this.reviewGroups
        .flatMap((group) => group.cards)
        .find((card) => card.id === this.reviewPreview?.id);
      if (current) this.reviewPreview = current;
      else this.clearReviewPreview();
    }
  }

  private buildReviewGroups(changes: AuditChange[]): ReviewChangeGroup[] {
    const cards = new Map<string, ReviewChangeCard>();
    let cardOrder = 0;
    changes.forEach((change) => {
      const target = change.target || this.inferReviewTarget(change.path, change);
      const key = this.reviewCardKey(change, target);
      const existing = cards.get(key);
      if (existing) {
        existing.target = this.mergeReviewTargets(existing.target, target);
        const previous = existing.changes.length === 1 ? existing.changes[0] : null;
        const isReplacement =
          (target.kind === 'field' || target.kind === 'action') &&
          previous &&
          ((previous.kind === 'removed' && change.kind === 'added') ||
            (previous.kind === 'added' && change.kind === 'removed'));
        if (isReplacement) {
          const removed = previous.kind === 'removed' ? previous : change;
          const added = previous.kind === 'added' ? previous : change;
          existing.changes = this.reviewReplacementChanges(added.path, existing.target, removed, added);
        } else {
          existing.changes.push(change);
        }
        existing.kind = this.reviewCardKind(existing.changes);
        existing.label = this.reviewTargetLabel(existing.target, existing.changes);
        return;
      }
      cards.set(key, {
        id: key,
        label: this.reviewTargetLabel(target, [change]),
        kind: change.kind === 'snapshot' ? 'changed' : change.kind,
        target,
        changes: [change],
        originalIndex: cardOrder++,
      });
    });

    const groups = new Map<string, ReviewChangeGroup>();
    cards.forEach((card) => {
      const stage = card.target.stage;
      const groupId = stage ? `stage-${stage}` : 'workflow';
      const group = groups.get(groupId) || {
        id: groupId,
        label: stage ? this.reviewStageGroupLabel(stage) : 'Workflow settings',
        cards: [],
      };
      group.cards.push(card);
      groups.set(groupId, group);
    });

    return Array.from(groups.values())
      .sort((a, b) => {
        if (a.id === 'workflow') return -1;
        if (b.id === 'workflow') return 1;
        return Number(a.id.replace('stage-', '')) - Number(b.id.replace('stage-', ''));
      })
      .map((group) => ({
        ...group,
        cards: [...group.cards].sort((a, b) => this.compareReviewCards(a, b)),
      }));
  }

  private compareReviewCards(a: ReviewChangeCard, b: ReviewChangeCard): number {
    const aTime = this.reviewCardTimestamp(a);
    const bTime = this.reviewCardTimestamp(b);
    if (aTime !== null && bTime !== null && aTime !== bTime) return bTime - aTime;
    if (aTime !== null && bTime === null) return -1;
    if (aTime === null && bTime !== null) return 1;
    return (a.originalIndex ?? 0) - (b.originalIndex ?? 0);
  }

  private reviewCardTimestamp(card: ReviewChangeCard): number | null {
    let latest: number | null = null;
    for (const change of card.changes) {
      const time = this.changeTimes[change.path];
      if (typeof time === 'number' && !Number.isNaN(time) && (latest === null || time > latest)) {
        latest = time;
      }
    }
    return latest;
  }

  trackByReviewGroup(_index: number, group: ReviewChangeGroup): string {
    return group.id;
  }

  trackByReviewCard(_index: number, card: ReviewChangeCard): string {
    return card.id;
  }

  private reviewCardKey(change: AuditChange, target: ReviewTarget): string {
    const stage = target.stage || 0;
    if (target.kind === 'field' || target.kind === 'action') {
      if (target.beforeEntity && target.afterEntity) {
        return `${target.kind}:${stage}:${target.entityKey || change.path}`;
      }
      // A delete + add at the same saved position represents one component edit,
      // even when a client regenerated its id during the edit.
      const position = target.beforePosition || target.afterPosition;
      if (position) return `${target.kind}:${stage}:${position.row}:${position.column}`;
      return `${target.kind}:${stage}:${target.entityKey || change.path}`;
    }
    return `${target.kind}:${stage}`;
  }

  private mergeReviewTargets(a: ReviewTarget, b: ReviewTarget): ReviewTarget {
    return {
      ...a,
      ...b,
      beforeEntity: a.beforeEntity ?? b.beforeEntity,
      afterEntity: a.afterEntity ?? b.afterEntity,
      beforePosition: a.beforePosition ?? b.beforePosition,
      afterPosition: a.afterPosition ?? b.afterPosition,
      entityKey: b.afterEntity ? b.entityKey : a.entityKey || b.entityKey,
    };
  }

  private reviewReplacementChanges(
    path: string,
    target: ReviewTarget,
    removed: AuditChange,
    added: AuditChange
  ): AuditChange[] {
    const beforeEntity = this.reviewEntityForComparison(target.beforeEntity);
    const afterEntity = this.reviewEntityForComparison(target.afterEntity);
    const propertyChanges = this.diffObjects(path, beforeEntity, afterEntity).filter(
      (change) => !change.path.endsWith('.id') && !change.path.endsWith('.kind')
    );
    propertyChanges.forEach((change) => (change.target = target));
    if (propertyChanges.length > 0) return propertyChanges;
    return [{
      path: `${path}.component`,
      label: 'Component',
      kind: 'changed',
      before: removed.before,
      after: added.after,
      target,
    }];
  }

  private reviewEntityForComparison(entity: any): any {
    if (!entity || typeof entity !== 'object') return entity;
    const comparable = this.cleanForSnapshot(entity);
    delete comparable.id;
    delete comparable.kind;
    return comparable;
  }

  private reviewCardKind(changes: AuditChange[]): 'added' | 'removed' | 'changed' {
    if (changes.every((change) => change.kind === 'added')) return 'added';
    if (changes.every((change) => change.kind === 'removed')) return 'removed';
    return 'changed';
  }

  private reviewStageGroupLabel(stage: number): string {
    const name = this.wf?.stages?.[stage]?.name?.trim();
    return name && name !== `Stage ${stage}` ? `Stage ${stage} · ${name}` : `Stage ${stage}`;
  }

  private reviewTargetLabel(target: ReviewTarget, changes: AuditChange[]): string {
    const entity = target.afterEntity || target.beforeEntity;
    if (target.kind === 'field') {
      if (entity?.kind === 'mirror') {
        const inherited = target.afterEntity
          ? this.findStage1FieldById(entity.refId)
          : this.savedStageOneField(entity.refId);
        return inherited?.label ? `${inherited.label} (inherited)` : 'Inherited field';
      }
      return entity?.label || changes[0]?.label || 'Form field';
    }
    if (target.kind === 'action') return entity?.label || changes[0]?.label || 'Stage action';
    if (target.kind === 'stages') return 'Workflow stages';
    if (target.kind === 'form') return 'Form layout';
    if (target.kind === 'actions') return 'Stage actions';
    if (target.kind === 'routing') return 'Routing and recipients';
    if (target.kind === 'hidden-fields') return 'Hidden inherited fields';
    if (target.kind === 'final-output') return 'Final outputs';
    if (target.kind === 'stage') return entity?.name || `Stage ${target.stage || ''}`.trim();
    return 'Workflow details';
  }

  expandedReviewCards = new Set<string>();
  revertingCardId: string | null = null;

  toggleReviewCardDetails(card: ReviewChangeCard, event: Event): void {
    event.stopPropagation();
    if (this.expandedReviewCards.has(card.id)) this.expandedReviewCards.delete(card.id);
    else this.expandedReviewCards.add(card.id);
  }

  isReviewCardExpanded(card: ReviewChangeCard): boolean {
    return this.expandedReviewCards.has(card.id);
  }

  private buildReviewCardView(card: ReviewChangeCard): ReviewCardView {
    const plain: ReviewCardView = { structured: false, summaryLines: [], details: [] };
    if (card.changes.length !== 1) return plain;
    const change = card.changes[0];
    if (change.kind !== 'added' && change.kind !== 'removed') return plain;
    const entity = change.kind === 'added' ? change.after : change.before;
    if (!entity || typeof entity !== 'object') return plain;
    if (card.target.kind === 'stage') return this.buildStageReviewView(entity);
    if (card.target.kind === 'field') return this.buildComponentReviewView(card, entity);
    if (card.target.kind === 'action') return this.buildActionReviewView(entity);
    return this.buildGenericReviewView(entity);
  }

  private buildStageReviewView(stage: any): ReviewCardView {
    const routing = stage.routing || {};
    const roles = (Array.isArray(routing.actorRoles) ? routing.actorRoles : []).filter((r: any) => !!r);
    const actor = roles.length ? roles.join(', ') : '—';
    const actions = (Array.isArray(stage.actions) ? stage.actions : [])
      .map((a: any) => this.reviewActionLabel(a))
      .filter((label: string) => !!label);
    const timeBudget = this.formatTimeBudgetValue(routing.timeBudget);
    const notifications = routing.notifications || {};
    const details: ReviewDetailRow[] = [];
    details.push({ label: 'Actor role', value: actor });
    if (timeBudget) details.push({ label: 'Time budget', value: timeBudget });
    if (actions.length) details.push({ label: 'Actions', values: actions });
    const approval = this.formatApprovalTarget(routing.approvalTarget);
    if (approval) details.push({ label: 'Approval routing', value: approval });
    const rejection = this.formatRejectionTarget(routing.rejectionTarget);
    if (rejection) details.push({ label: 'Rejection routing', value: rejection });
    if (routing.escalationTarget) details.push({ label: 'Escalation target role', value: routing.escalationTarget });
    if (routing.immutable) details.push({ label: 'Submitted records immutable', value: 'Yes' });
    details.push({
      label: 'Notifications',
      values: [
        `Stage transition ${this.enabledLabel(notifications.transition)}`,
        `Per-item ${this.enabledLabel(notifications.perItem)}`,
        `Reminder ${this.enabledLabel(notifications.reminder)}`,
        `Escalation ${this.enabledLabel(notifications.escalation)}`,
      ],
    });
    const summaryLines = [
      `Actor: ${actor}`,
      `${actions.length} action${actions.length === 1 ? '' : 's'}${timeBudget ? ` · ${timeBudget} time budget` : ''}`,
    ];
    return { structured: true, summaryLines, details };
  }

  private buildComponentReviewView(card: ReviewChangeCard, entity: any): ReviewCardView {
    const details: ReviewDetailRow[] = [];
    let name = '';
    let typeLabel = '';
    let inherited = 'No';
    if (entity.kind === 'mirror') {
      const source = card.kind === 'added'
        ? this.findStage1FieldById(entity.refId)
        : this.savedStageOneField(entity.refId);
      name = source?.label || 'Inherited field';
      typeLabel = source ? this.reviewFieldTypeLabel(source) : '';
      inherited = 'Yes — inherited from Stage 1';
    } else {
      const field = entity.kind === 'native' ? (entity.field || entity) : entity;
      name = field.label || '';
      typeLabel = this.reviewFieldTypeLabel(field);
    }
    if (name) details.push({ label: 'Name', value: name });
    if (typeLabel) details.push({ label: 'Type', value: typeLabel });
    details.push({ label: 'Inherited', value: inherited });
    const summary = [typeLabel, name].filter((part) => !!part).join(' · ') || 'Component';
    return { structured: true, summaryLines: [summary], details };
  }

  private buildActionReviewView(entity: any): ReviewCardView {
    const details: ReviewDetailRow[] = [];
    const label = this.reviewActionLabel(entity);
    if (label) details.push({ label: 'Action', value: label });
    if (entity.type) details.push({ label: 'Type', value: this.humanizePath(entity.type) });
    if (entity.requireComment != null) {
      details.push({ label: 'Require comment', value: entity.requireComment ? 'Yes' : 'No' });
    }
    return { structured: true, summaryLines: [label || 'Action'], details };
  }

  private buildGenericReviewView(entity: any): ReviewCardView {
    const details: ReviewDetailRow[] = [];
    Object.keys(entity).forEach((key) => {
      if (key === 'id' || key === 'kind') return;
      const value = entity[key];
      if (this.isEmptyReviewValue(value)) return;
      const label = this.humanizePath(key);
      if (Array.isArray(value)) {
        const items = value.map((item) => this.reviewScalarValue(item)).filter((item) => !!item);
        if (items.length) details.push({ label, values: items });
      } else if (value && typeof value === 'object') {
        const inline = this.reviewInlineObject(value);
        if (inline) details.push({ label, value: inline });
      } else {
        details.push({ label, value: this.reviewScalarValue(value) });
      }
    });
    return {
      structured: details.length > 0,
      summaryLines: [`${details.length} propert${details.length === 1 ? 'y' : 'ies'} changed`],
      details,
    };
  }

  private reviewActionLabel(action: any): string {
    if (!action || typeof action !== 'object') return '';
    return action.label || (action.type ? this.humanizePath(action.type) : '');
  }

  private enabledLabel(flag: any): string {
    return flag ? 'enabled' : 'disabled';
  }

  private formatTimeBudgetValue(budget: any): string {
    if (!budget || typeof budget !== 'object' || !budget.value || budget.value <= 0) return '';
    const unit = budget.value === 1 ? String(budget.unit || '').replace(/s$/, '') : String(budget.unit || '');
    return unit ? `${budget.value} ${unit}` : String(budget.value);
  }

  private formatApprovalTarget(target: any): string {
    if (!target || !target.type) return '';
    switch (target.type) {
      case 'next': return 'Next stage';
      case 'close': return 'Close as Approved';
      case 'output': return 'Trigger final output';
      case 'stage': return target.stage ? `Advance to Stage ${target.stage}` : 'Advance to a specific stage';
      default: return this.humanizePath(target.type);
    }
  }

  private formatRejectionTarget(target: any): string {
    if (!target || !target.type) return '';
    switch (target.type) {
      case 'previous': return 'Return to previous stage';
      case 'close': return 'Close as Rejected';
      default: return this.humanizePath(target.type);
    }
  }

  private isEmptyReviewValue(value: any): boolean {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }

  private reviewScalarValue(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return this.reviewInlineObject(value);
    return String(value);
  }

  private reviewInlineObject(value: any): string {
    if (!value || typeof value !== 'object') return this.reviewScalarValue(value);
    if (value.label) return String(value.label);
    if (value.type) return this.humanizePath(String(value.type));
    return '';
  }

  private inferReviewTarget(path: string, change?: AuditChange): ReviewTarget {
    const stageMatch = path.match(/^stages\.(\d+)(?:\.(.+))?$/);
    if (stageMatch) {
      const stage = Number(stageMatch[1]);
      const rest = stageMatch[2] || '';
      if (rest.startsWith('routing')) return { kind: 'routing', stage };
      if (rest.startsWith('hiddenInherited')) return { kind: 'hidden-fields', stage };
      if (rest.startsWith('rows')) return { kind: 'form', stage };
      if (rest.startsWith('actions')) return { kind: 'actions', stage };
      return {
        kind: 'stage',
        stage,
        beforeEntity: rest ? undefined : change?.before,
        afterEntity: rest ? undefined : change?.after,
      };
    }
    if (path === 'stageCount') return { kind: 'stages' };
    if (path.startsWith('finalOutputs')) {
      return { kind: 'final-output', stage: this.wf?.stageCount || 1 };
    }
    return { kind: 'workflow' };
  }

  reviewCardStatus(card: ReviewChangeCard): string {
    return card.kind === 'added' ? 'Added' : card.kind === 'removed' ? 'Removed' : 'Changed';
  }

  reviewCardTime(card: ReviewChangeCard): string {
    let ts = 0;
    for (const change of card.changes) {
      const t = this.changeTimes[change.path];
      if (t && t > ts) ts = t;
    }
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const sameDay = d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    return sameDay ? time : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
  }

  requestRevertChange(card: ReviewChangeCard, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.revertingCardId) return;

    const effectiveCard = this.authoritativeRevertCard(card);
    const blockedReason = this.reviewRevertBlockedReason(effectiveCard);
    if (blockedReason) {
      this.showConfirm({
        title: 'This change cannot be reverted yet',
        body: blockedReason,
        okLabel: 'Close',
        okClass: '',
        onConfirm: () => undefined,
      });
      return;
    }
    const dependencies = this.reviewRevertDependencies(effectiveCard);
    const wording = this.reviewRevertWording(card, effectiveCard, dependencies);
    this.showConfirm({
      title: wording.title,
      body: wording.body,
      okLabel: wording.okLabel,
      okClass: 'danger',
      details: wording.details,
      onConfirm: () => this.applyReviewCardRevert(effectiveCard, dependencies),
    });
  }

  private authoritativeRevertCard(card: ReviewChangeCard): ReviewChangeCard {
    const entity = card.target.afterEntity || card.target.beforeEntity;
    let sourceId: string | null = null;
    if (card.target.kind === 'field' && entity?.kind === 'mirror') sourceId = entity.refId || null;
    if (card.target.kind === 'hidden-fields') {
      for (const change of card.changes) {
        const before = new Set(Array.isArray(change.before) ? change.before : []);
        const after = new Set(Array.isArray(change.after) ? change.after : []);
        sourceId = Array.from(new Set([...before, ...after])).find(id => before.has(id) !== after.has(id)) || null;
        if (sourceId) break;
      }
    }
    if (!sourceId) return card;
    return this.reviewGroups
      .flatMap(group => group.cards)
      .find(candidate => candidate.target.kind === 'field' && candidate.target.stage === 1 &&
        candidate.target.entityKey === `field:${sourceId}`) || card;
  }

  private reviewRevertBlockedReason(card: ReviewChangeCard): string | null {
    if (card.target.kind === 'stage' && card.target.stage) {
      if (card.kind === 'added' && card.target.stage !== this.wf.stageCount) {
        return `Remove Stage ${this.wf.stageCount} first so later stages are not left incomplete.`;
      }
      if (card.kind === 'removed' && card.target.stage !== this.wf.stageCount + 1) {
        return `Restore Stage ${this.wf.stageCount + 1} first so the workflow stages remain in order.`;
      }
    }

    if (card.target.kind === 'field' && card.kind === 'removed') {
      const stage = this.wf.stages[card.target.stage || 1];
      if (stage && stage.rows.length >= 10 && stage.rows.every(row => row.length >= 3)) {
        return `This stage is full. Remove another unsaved input field before restoring this saved field.`;
      }
    }

    const missingReferences = this.missingSavedReferencesForRevert(card);
    if (missingReferences.fieldIds.length) {
      const names = missingReferences.fieldIds.map(id => `“${this.savedFieldLabel(id)}”`).join(', ');
      return `The saved configuration uses ${names}, which ${missingReferences.fieldIds.length === 1 ? 'is' : 'are'} not currently in this draft. Restore ${missingReferences.fieldIds.length === 1 ? 'that input field' : 'those input fields'} before reverting this change.`;
    }
    if (missingReferences.stageNumbers.length) {
      const stages = missingReferences.stageNumbers.map(stage => `Stage ${stage}`).join(', ');
      return `The saved configuration routes to ${stages}, which ${missingReferences.stageNumbers.length === 1 ? 'is' : 'are'} not currently in this draft. Restore ${missingReferences.stageNumbers.length === 1 ? 'that stage' : 'those stages'} before reverting this change.`;
    }

    for (const change of card.changes) {
      if (change.path.endsWith('routing.notifications.perItemRecipientField') && change.before &&
        !this.workflowHasField(String(change.before))) {
        return `The saved recipient field is not currently in this draft. Restore that input field before reverting this routing change.`;
      }
      if (change.path.endsWith('routing.approvalTarget.stage') && change.before != null &&
        !this.wf.stages[Number(change.before)]) {
        return `Stage ${change.before} is not currently in this draft. Restore that stage before reverting this routing change.`;
      }
    }
    return null;
  }

  private workflowHasField(fieldId: string): boolean {
    for (let stageNumber = 1; stageNumber <= this.wf.stageCount; stageNumber++) {
      for (const entry of this.wf.stages[stageNumber]?.rows?.flat() || []) {
        if ((entry as NativeEntry).kind === 'native' && (entry as NativeEntry).field?.id === fieldId) return true;
        if ((entry as Field).id === fieldId) return true;
      }
    }
    return false;
  }

  private missingSavedReferencesForRevert(card: ReviewChangeCard): {
    fieldIds: string[];
    stageNumbers: number[];
  } {
    const fieldIds = new Set<string>();
    const stageNumbers = new Set<number>();
    const restoredFieldIds = new Set<string>();
    const restoredStageNumbers = new Set<number>();
    const isReplacement = card.target.kind === 'field' && card.kind === 'changed' &&
      this.reviewEntityFieldId(card.target.beforeEntity) !== this.reviewEntityFieldId(card.target.afterEntity);

    if (card.target.kind === 'field' && (card.kind === 'removed' || isReplacement)) {
      const entry = this.savedFieldEntryForTarget(card.target) || card.target.beforeEntity;
      const id = this.reviewEntityFieldId(entry);
      if (id) restoredFieldIds.add(id);
      this.collectSavedFieldReferences(entry, fieldIds);
    } else if (card.target.kind === 'stage' && card.kind === 'removed' && card.target.stage) {
      const saved = this.savedReviewSnapshot();
      const stage = saved?.stages?.[card.target.stage] ?? saved?.stages?.[String(card.target.stage)];
      restoredStageNumbers.add(card.target.stage);
      for (const entry of stage?.rows?.flat() || []) {
        if (entry?.kind !== 'mirror') {
          const id = this.reviewEntityFieldId(entry);
          if (id) restoredFieldIds.add(id);
        }
      }
      this.collectSavedStageReferences(stage, fieldIds, stageNumbers);
    } else {
      card.changes.forEach(change => this.collectSavedPathReferences(change.path, change.before, fieldIds, stageNumbers));
    }

    return {
      fieldIds: Array.from(fieldIds).filter(id => !restoredFieldIds.has(id) && !this.workflowHasField(id)),
      stageNumbers: Array.from(stageNumbers).filter(stage =>
        !restoredStageNumbers.has(stage) && !this.wf.stages[stage]
      ),
    };
  }

  private collectSavedPathReferences(
    path: string,
    value: any,
    fieldIds: Set<string>,
    stageNumbers: Set<number>
  ): void {
    if (path.endsWith('perItemRecipientField') || path.endsWith('startFieldId') || path.endsWith('endFieldId')) {
      if (value) fieldIds.add(String(value));
    }
    if (path.endsWith('conditions') && Array.isArray(value)) {
      value.forEach(condition => {
        if (condition?.fieldId) fieldIds.add(String(condition.fieldId));
      });
    }
    if (path.endsWith('guard.keys') && Array.isArray(value)) {
      value.forEach(id => id && fieldIds.add(String(id)));
    }
    if (path.endsWith('approvalTarget.stage') && value != null) stageNumbers.add(Number(value));
  }

  private collectSavedFieldReferences(entity: any, fieldIds: Set<string>): void {
    const field = entity?.kind === 'native' ? (entity.field || entity) : entity;
    if (!field || field.kind === 'mirror') {
      if (field?.refId) fieldIds.add(String(field.refId));
      return;
    }
    if (field.durationCfg?.startFieldId) fieldIds.add(String(field.durationCfg.startFieldId));
    if (field.durationCfg?.endFieldId) fieldIds.add(String(field.durationCfg.endFieldId));
    (field.visibility?.conditions || []).forEach((condition: any) => {
      if (condition?.fieldId) fieldIds.add(String(condition.fieldId));
    });
    (field.guard?.keys || []).forEach((id: any) => id && fieldIds.add(String(id)));
  }

  private collectSavedStageReferences(stage: any, fieldIds: Set<string>, stageNumbers: Set<number>): void {
    if (!stage) return;
    for (const entry of stage.rows?.flat() || []) {
      if (entry?.kind === 'mirror' && entry.refId) fieldIds.add(String(entry.refId));
      else this.collectSavedFieldReferences(entry, fieldIds);
    }
    const routing = stage.routing;
    if (routing?.notifications?.perItemRecipientField) {
      fieldIds.add(String(routing.notifications.perItemRecipientField));
    }
    (routing?.conditionalRoute?.conditions || []).forEach((condition: any) => {
      if (condition?.fieldId) fieldIds.add(String(condition.fieldId));
    });
    if (routing?.approvalTarget?.type === 'stage' && routing.approvalTarget.stage != null) {
      stageNumbers.add(Number(routing.approvalTarget.stage));
    }
  }

  private savedFieldLabel(fieldId: string): string {
    const saved = this.savedReviewSnapshot();
    for (const stage of Object.values(saved?.stages || {}) as any[]) {
      for (const entry of stage?.rows?.flat() || []) {
        const field = entry?.kind === 'native' ? entry.field : entry;
        if (field?.id === fieldId) return field.label || 'input field';
      }
    }
    return fieldId;
  }

  private reviewRevertWording(
    selectedCard: ReviewChangeCard,
    card: ReviewChangeCard,
    dependencies: ReviewRevertDependency[]
  ): { title: string; body: string; okLabel: string; details: string[] } {
    const version = this.wf.version;
    const otherChanges = 'Your other draft changes will remain unchanged.';
    const entity = card.target.afterEntity || card.target.beforeEntity;
    const fieldName = this.reviewRevertFieldName(card);
    const dependencyDetails = dependencies.map(dependency => dependency.label);
    const inheritedSource = selectedCard !== card;

    if (card.target.kind === 'field') {
      if (card.kind === 'added') {
        return {
          title: inheritedSource ? `Remove “${fieldName}” and its inherited copies?` : 'Remove this unsaved input field?',
          body: inheritedSource
            ? `This inherited field comes from “${fieldName}” in Stage 1. Reverting will remove the source field and its inherited copies. ${otherChanges}`
            : `“${fieldName}” was added in the current draft. Reverting this change will remove it from the workflow. ${otherChanges}`,
          okLabel: dependencies.length ? 'Remove field and clear references' : 'Remove field',
          details: dependencyDetails,
        };
      }
      if (card.kind === 'removed') {
        return {
          title: 'Restore removed input field?',
          body: `“${fieldName}” will be restored using its configuration and position from saved version v${version}. ${otherChanges}`,
          okLabel: 'Restore field',
          details: [],
        };
      }
      return {
        title: `Revert changes to “${fieldName}”?`,
        body: dependencies.length
          ? `This will restore the input field from saved version v${version} and clear the listed draft references to its replacement. ${otherChanges}`
          : `This will restore the displayed input-field values from saved version v${version}. ${otherChanges}`,
        okLabel: dependencies.length ? 'Revert change and clear references' : 'Revert change',
        details: [...this.reviewRevertPropertyLabels(card), ...dependencyDetails],
      };
    }

    if (card.target.kind === 'stage' && card.kind === 'added') {
      const stage = card.target.stage || this.wf.stageCount;
      return {
        title: `Remove unsaved Stage ${stage}?`,
        body: `Reverting this change will remove Stage ${stage} and its unsaved fields, actions, routing, and notification settings. ${otherChanges}`,
        okLabel: dependencies.length ? 'Remove stage and update routes' : 'Remove stage',
        details: dependencyDetails,
      };
    }
    if (card.target.kind === 'stage' && card.kind === 'removed') {
      return {
        title: `Restore removed Stage ${card.target.stage}?`,
        body: `Stage ${card.target.stage} will be restored from saved version v${version}. ${otherChanges}`,
        okLabel: 'Restore stage',
        details: [],
      };
    }

    if (card.target.kind === 'routing') {
      return {
        title: 'Revert routing and recipient changes?',
        body: `The following will return to their values from saved version v${version}. ${otherChanges}`,
        okLabel: 'Revert change',
        details: this.reviewRevertPropertyLabels(card),
      };
    }

    const itemLabel = entity?.label || card.label;
    if (card.kind === 'added') {
      return {
        title: `Remove unsaved “${itemLabel}”?`,
        body: `“${itemLabel}” was added in the current draft. Reverting will remove it. ${otherChanges}`,
        okLabel: card.target.kind === 'action' ? 'Remove action' : 'Revert change',
        details: dependencyDetails,
      };
    }
    if (card.kind === 'removed') {
      return {
        title: `Restore removed “${itemLabel}”?`,
        body: `“${itemLabel}” will be restored from saved version v${version}. ${otherChanges}`,
        okLabel: card.target.kind === 'action' ? 'Restore action' : 'Revert change',
        details: [],
      };
    }
    return {
      title: 'Revert this change?',
      body: `This will restore “${card.label}” to the displayed values from saved version v${version}. ${otherChanges}`,
      okLabel: 'Revert change',
      details: card.changes.length > 1 ? this.reviewRevertPropertyLabels(card) : [],
    };
  }

  private reviewRevertPropertyLabels(card: ReviewChangeCard): string[] {
    return Array.from(new Set(card.changes.map(change => this.reviewPropertyLabel(change, card))));
  }

  private reviewRevertFieldName(card: ReviewChangeCard): string {
    const entity = card.target.afterEntity || card.target.beforeEntity;
    if (entity?.kind === 'mirror') {
      return (card.target.afterEntity ? this.findStage1FieldById(entity.refId) : this.savedStageOneField(entity.refId))?.label || card.label;
    }
    return entity?.label || card.label;
  }

  private reviewRevertDependencies(card: ReviewChangeCard): ReviewRevertDependency[] {
    if (card.target.kind === 'field') {
      const entity = card.target.afterEntity || card.target.beforeEntity;
      if (entity?.kind === 'mirror') return [];
      const isReplacement = card.kind === 'changed' &&
        this.reviewEntityFieldId(card.target.beforeEntity) !== this.reviewEntityFieldId(card.target.afterEntity);
      if (card.kind !== 'added' && !isReplacement) return [];
      const fieldId = isReplacement
        ? this.reviewEntityFieldId(card.target.afterEntity)
        : this.reviewRevertFieldId(card);
      return fieldId ? this.fieldRevertDependencies(fieldId) : [];
    }
    if (card.kind === 'added' && card.target.kind === 'stage' && card.target.stage) {
      return this.stageRevertDependencies(card.target.stage);
    }
    return [];
  }

  private reviewRevertFieldId(card: ReviewChangeCard): string | null {
    const entity = card.target.afterEntity || card.target.beforeEntity;
    if (entity?.kind === 'mirror') return entity.refId || null;
    return entity?.id || card.target.entityKey?.replace(/^(field|native):/, '') || null;
  }

  private fieldRevertDependencies(fieldId: string): ReviewRevertDependency[] {
    const dependencies: ReviewRevertDependency[] = [];
    const add = (label: string, clear: () => void) => dependencies.push({ label, clear });

    for (let stageNumber = 1; stageNumber <= this.wf.stageCount; stageNumber++) {
      const stage = this.wf.stages[stageNumber];
      if (!stage) continue;
      if (stageNumber > 1 && stage.rows.flat().some(entry =>
        (entry as MirrorEntry).kind === 'mirror' && (entry as MirrorEntry).refId === fieldId)) {
        add(`Stage ${stageNumber} inherited field`, () => this.removeFieldEntryByKey(stageNumber, `mirror:${fieldId}`));
      }
      if (stage.routing.notifications.perItemRecipientField === fieldId) {
        add(`${this.stageDisplayName(stageNumber)} per-item notification recipient`, () => {
          stage.routing.notifications.perItemRecipientField = '';
        });
      }
      if (stage.routing.conditionalRoute?.conditions?.some(condition => condition.fieldId === fieldId)) {
        add(`${this.stageDisplayName(stageNumber)} conditional routing`, () => {
          stage.routing.conditionalRoute.conditions = stage.routing.conditionalRoute.conditions
            .filter(condition => condition.fieldId !== fieldId);
          if (stage.routing.conditionalRoute.conditions.length === 0) stage.routing.conditionalRoute.enabled = false;
        });
      }

      for (const row of stage.rows) {
        for (const entry of row) {
          const field = stageNumber === 1
            ? entry as Field
            : (entry as NativeEntry).kind === 'native' ? (entry as NativeEntry).field : null;
          if (!field || field.id === fieldId) continue;
          if (field.durationCfg?.startFieldId === fieldId || field.durationCfg?.endFieldId === fieldId) {
            add(`${field.label || 'Duration field'} duration calculation`, () => {
              if (field.durationCfg?.startFieldId === fieldId) field.durationCfg.startFieldId = '';
              if (field.durationCfg?.endFieldId === fieldId) field.durationCfg.endFieldId = '';
            });
          }
          if (field.visibility?.conditions?.some(condition => condition.fieldId === fieldId)) {
            add(`${field.label || 'Input field'} visibility rule`, () => {
              field.visibility.conditions = field.visibility.conditions.filter(condition => condition.fieldId !== fieldId);
              if (field.visibility.conditions.length === 0) field.visibility.enabled = false;
            });
          }
          if (field.guard?.keys?.includes(fieldId)) {
            add(`${field.label || 'Input field'} submission guard`, () => {
              field.guard.keys = field.guard.keys.filter(key => key !== fieldId);
              if (field.guard.keys.length === 0) field.guard.enabled = false;
            });
          }
        }
      }
    }
    return this.uniqueReviewDependencies(dependencies);
  }

  private stageRevertDependencies(stageNumber: number): ReviewRevertDependency[] {
    const dependencies: ReviewRevertDependency[] = [];
    for (let index = 1; index <= this.wf.stageCount; index++) {
      if (index === stageNumber) continue;
      const target = this.wf.stages[index]?.routing?.approvalTarget;
      if (target?.type === 'stage' && target.stage === stageNumber) {
        dependencies.push({
          label: `${this.stageDisplayName(index)} approval route`,
          clear: () => {
            target.type = index < stageNumber - 1 ? 'next' : 'close';
            target.stage = null;
          },
        });
      }
    }
    return dependencies;
  }

  private uniqueReviewDependencies(dependencies: ReviewRevertDependency[]): ReviewRevertDependency[] {
    const grouped = new Map<string, Array<() => void>>();
    dependencies.forEach(dependency => {
      const callbacks = grouped.get(dependency.label) || [];
      callbacks.push(dependency.clear);
      grouped.set(dependency.label, callbacks);
    });
    return Array.from(grouped.entries()).map(([label, callbacks]) => ({
      label,
      clear: () => callbacks.forEach(callback => callback()),
    }));
  }

  private applyReviewCardRevert(card: ReviewChangeCard, dependencies: ReviewRevertDependency[]): void {
    if (this.revertingCardId) return;
    this.revertingCardId = card.id;
    const backup = this.serializeWorkflow(this.wf) as Workflow;
    const danglingBefore = this.workflowDanglingReferenceKeys();
    const cardElements = Array.from(document.querySelectorAll<HTMLElement>('[data-review-card-id]'));
    const previousIndex = Math.max(0, cardElements.findIndex(element => element.dataset['reviewCardId'] === card.id));
    try {
      dependencies.forEach(dependency => dependency.clear());
      this.revertReviewCardValue(card);
      const introducedDanglingReference = Array.from(this.workflowDanglingReferenceKeys())
        .some(reference => !danglingBefore.has(reference));
      if (introducedDanglingReference) throw new Error('Revert introduced an invalid reference');
      this.clearReviewPreview(false);
      this.markDirty();
      void this.flushRecoveryCopy();
      this.changeDetector.detectChanges();
      setTimeout(() => {
        const remaining = Array.from(document.querySelectorAll<HTMLElement>('[data-review-card-id]'));
        const nearbyCard = remaining[Math.min(previousIndex, Math.max(0, remaining.length - 1))];
        (nearbyCard?.querySelector<HTMLElement>('.review-card-main') ||
          document.querySelector<HTMLElement>('.review-close'))?.focus();
      });
    } catch {
      this.wf = this.reviveWorkflow(backup);
      this.wf.dirty = this.currentWorkflowJson() !== this.serverBaselineJson;
      this.refreshReviewChanges();
      this.showToast('This change could not be reverted. Please try again.', 'alert-triangle');
    } finally {
      this.revertingCardId = null;
    }
  }

  private revertReviewCardValue(card: ReviewChangeCard): void {
    switch (card.target.kind) {
      case 'field':
        this.revertFieldReviewCard(card);
        return;
      case 'action':
        this.revertActionReviewCard(card);
        return;
      case 'stage':
        this.revertStageReviewCard(card);
        return;
      case 'form':
        this.restoreSavedFieldLayout(card.target.stage || 1);
        return;
      case 'actions':
        this.restoreSavedActionOrder(card.target.stage || 1);
        return;
      case 'hidden-fields':
        this.revertHiddenFieldsReviewCard(card);
        return;
      default:
        card.changes.forEach(change => this.restoreWorkflowPath(change.path, change.before));
    }
  }

  private revertFieldReviewCard(card: ReviewChangeCard): void {
    const stageNumber = card.target.stage || 1;
    const key = card.target.entityKey || '';
    if (card.kind === 'added') {
      const fieldId = this.reviewRevertFieldId(card);
      this.removeFieldEntryByKey(stageNumber, key);
      if (stageNumber === 1 && fieldId) this.onFieldDeletedFromStage1(fieldId);
      return;
    }
    if (card.kind === 'removed') {
      const restored = this.savedFieldEntryForTarget(card.target) || this.reviewEntryFromEntity(card.target.beforeEntity);
      if (!restored) throw new Error('Saved field is unavailable');
      this.insertReviewEntry(stageNumber, restored, card.target.beforePosition);
      this.restoreSavedFieldLayout(stageNumber);
      const fieldId = this.reviewRevertFieldId(card);
      if (stageNumber === 1 && fieldId) this.restoreSavedMirrorsForField(fieldId);
      return;
    }

    if (card.target.beforeEntity?.id && card.target.afterEntity?.id &&
      card.target.beforeEntity.id !== card.target.afterEntity.id) {
      const draftFieldId = card.target.afterEntity.id;
      this.removeFieldEntryByKey(stageNumber, key);
      if (stageNumber === 1) this.onFieldDeletedFromStage1(draftFieldId);
      const restored = this.savedFieldEntryForTarget(card.target) || this.reviewEntryFromEntity(card.target.beforeEntity);
      if (!restored) throw new Error('Saved field is unavailable');
      this.insertReviewEntry(stageNumber, restored, card.target.beforePosition || card.target.afterPosition);
      this.restoreSavedFieldLayout(stageNumber);
      const savedFieldId = this.reviewEntityFieldId(card.target.beforeEntity);
      if (stageNumber === 1 && savedFieldId) this.restoreSavedMirrorsForField(savedFieldId);
      return;
    }

    const field = this.workingFieldForTarget(card.target);
    if (!field) throw new Error('Draft field is unavailable');
    const prefix = `stages.${stageNumber}.rows.${key}.`;
    card.changes.forEach(change => {
      const relative = change.path.startsWith(prefix) ? change.path.slice(prefix.length) : '';
      if (relative) this.restoreNestedValue(field, relative.split('.'), change.before);
    });
  }

  private revertActionReviewCard(card: ReviewChangeCard): void {
    const stage = this.wf.stages[card.target.stage || 1];
    if (!stage) throw new Error('Draft stage is unavailable');
    const id = card.target.entityKey?.replace(/^action:/, '') || '';
    const index = stage.actions.findIndex(action => action.id === id);
    if (card.kind === 'added') {
      if (index >= 0) stage.actions.splice(index, 1);
      return;
    }
    if (card.kind === 'removed') {
      const action = this.savedActionForTarget(card.target) ||
        this.cloneReviewValue(card.target.beforeEntity) as ActionBtn;
      const position = card.target.beforePosition?.column ?? stage.actions.length;
      stage.actions.splice(Math.min(position, stage.actions.length), 0, action);
      return;
    }
    if (card.target.beforeEntity?.id && card.target.afterEntity?.id &&
      card.target.beforeEntity.id !== card.target.afterEntity.id) {
      if (index >= 0) stage.actions.splice(index, 1);
      const action = this.savedActionForTarget(card.target) ||
        this.cloneReviewValue(card.target.beforeEntity) as ActionBtn;
      const position = card.target.beforePosition?.column ?? card.target.afterPosition?.column ?? stage.actions.length;
      stage.actions.splice(Math.min(position, stage.actions.length), 0, action);
      this.restoreSavedActionOrder(card.target.stage || 1);
      return;
    }
    if (index < 0) throw new Error('Draft action is unavailable');
    const prefix = `stages.${card.target.stage || 1}.actions.action:${id}.`;
    card.changes.forEach(change => {
      const relative = change.path.startsWith(prefix) ? change.path.slice(prefix.length) : '';
      if (relative) this.restoreNestedValue(stage.actions[index], relative.split('.'), change.before);
    });
  }

  private revertStageReviewCard(card: ReviewChangeCard): void {
    const stageNumber = card.target.stage;
    if (!stageNumber) throw new Error('Stage number is unavailable');
    if (card.kind === 'added') {
      delete this.wf.stages[stageNumber];
      this.wf.stageCount = Math.max(1, stageNumber - 1);
      if (this.wf.activeStage > this.wf.stageCount) this.wf.activeStage = this.wf.stageCount;
      this.repairRoutingAfterStageRemoval();
      return;
    }
    if (card.kind === 'removed') {
      const saved = this.savedReviewSnapshot();
      const savedStage = saved?.stages?.[stageNumber] ?? saved?.stages?.[String(stageNumber)];
      const stage = this.cloneReviewValue(savedStage || card.target.beforeEntity) as any;
      stage.hiddenInherited = new Set(Array.isArray(stage.hiddenInherited) ? stage.hiddenInherited : []);
      this.wf.stages[stageNumber] = stage;
      this.wf.stageCount = Math.max(this.wf.stageCount, stageNumber);
      return;
    }
    card.changes.forEach(change => this.restoreWorkflowPath(change.path, change.before));
  }

  private removeFieldEntryByKey(stageNumber: number, key: string): void {
    const stage = this.wf.stages[stageNumber];
    if (!stage) return;
    stage.rows = stage.rows
      .map(row => row.filter(entry => this.reviewEntryKey(entry, stageNumber) !== key))
      .filter(row => row.length > 0);
    if (key.startsWith('mirror:')) stage.hiddenInherited.add(key.slice('mirror:'.length));
  }

  private insertReviewEntry(stageNumber: number, entry: StageEntry, position?: ReviewPosition): void {
    const stage = this.wf.stages[stageNumber];
    if (!stage) throw new Error('Draft stage is unavailable');
    const rowIndex = position?.row ?? stage.rows.length;
    while (stage.rows.length <= rowIndex) stage.rows.push([]);
    let targetRow = stage.rows[rowIndex];
    if (targetRow.length >= 3) {
      targetRow = stage.rows.slice(rowIndex + 1).find(row => row.length < 3) ||
        stage.rows.slice(0, rowIndex).find(row => row.length < 3) || [];
      if (!stage.rows.includes(targetRow)) {
        if (stage.rows.length >= 10) throw new Error('The saved field cannot fit in this stage');
        stage.rows.splice(Math.min(rowIndex + 1, stage.rows.length), 0, targetRow);
      }
    }
    targetRow.splice(Math.min(position?.column ?? targetRow.length, targetRow.length), 0, entry);
    if ((entry as MirrorEntry).kind === 'mirror') stage.hiddenInherited.delete((entry as MirrorEntry).refId);
  }

  private reviewEntryFromEntity(entity: any): StageEntry | null {
    if (!entity) return null;
    if (entity.kind === 'mirror') return { kind: 'mirror', refId: entity.refId };
    if (entity.kind === 'native') {
      const cloned = this.cloneReviewValue(entity);
      if (cloned.field) return { kind: 'native', field: cloned.field as Field };
      const { kind: _kind, ...field } = cloned;
      return { kind: 'native', field: field as Field };
    }
    const { kind: _kind, ...field } = this.cloneReviewValue(entity);
    return field as Field;
  }

  private reviewEntityFieldId(entity: any): string | null {
    if (!entity) return null;
    if (entity.kind === 'mirror') return entity.refId || null;
    return entity.field?.id || entity.id || null;
  }

  private savedFieldEntryForTarget(target: ReviewTarget): StageEntry | null {
    const stageNumber = target.stage || 1;
    const saved = this.savedReviewSnapshot();
    const stage = saved?.stages?.[stageNumber] ?? saved?.stages?.[String(stageNumber)];
    if (!stage?.rows) return null;
    const savedId = this.reviewEntityFieldId(target.beforeEntity);
    const savedKey = target.beforeEntity?.kind === 'mirror'
      ? `mirror:${savedId}`
      : target.beforeEntity?.kind === 'native'
        ? `native:${savedId}`
        : `field:${savedId}`;
    for (const row of stage.rows as StageEntry[][]) {
      for (const entry of row || []) {
        if (this.reviewEntryKey(entry, stageNumber) === savedKey) return this.reviewEntryFromEntity(entry);
      }
    }
    return null;
  }

  private savedActionForTarget(target: ReviewTarget): ActionBtn | null {
    const stageNumber = target.stage || 1;
    const saved = this.savedReviewSnapshot();
    const stage = saved?.stages?.[stageNumber] ?? saved?.stages?.[String(stageNumber)];
    const savedId = target.beforeEntity?.id || target.entityKey?.replace(/^action:/, '');
    const action = stage?.actions?.find((candidate: ActionBtn) => candidate.id === savedId);
    return action ? this.cloneReviewValue(action) : null;
  }

  private workingFieldForTarget(target: ReviewTarget): Field | null {
    const stageNumber = target.stage || 1;
    const stage = this.wf.stages[stageNumber];
    if (!stage) return null;
    for (const row of stage.rows) {
      for (const entry of row) {
        if (this.reviewEntryKey(entry, stageNumber) !== target.entityKey) continue;
        if ((entry as NativeEntry).kind === 'native') return (entry as NativeEntry).field;
        if ((entry as MirrorEntry).kind === 'mirror') return null;
        return entry as Field;
      }
    }
    return null;
  }

  private reviewEntryKey(entry: StageEntry, stageNumber: number): string {
    if ((entry as MirrorEntry).kind === 'mirror') return `mirror:${(entry as MirrorEntry).refId}`;
    if ((entry as NativeEntry).kind === 'native') return `native:${(entry as NativeEntry).field.id}`;
    return `field:${(entry as Field).id}`;
  }

  private restoreSavedMirrorsForField(fieldId: string): void {
    const saved = this.savedReviewSnapshot();
    for (let stageNumber = 2; stageNumber <= this.wf.stageCount; stageNumber++) {
      const savedStage = saved?.stages?.[stageNumber] ?? saved?.stages?.[String(stageNumber)];
      const stage = this.wf.stages[stageNumber];
      if (!stage) continue;
      const savedHidden = new Set<string>(Array.isArray(savedStage?.hiddenInherited) ? savedStage.hiddenInherited : []);
      const savedPosition = savedStage
        ? this.findFieldPosition(savedStage.rows || [], `mirror:${fieldId}`)
        : { row: stage.rows.length, column: 0 };
      if (savedHidden.has(fieldId)) {
        this.removeFieldEntryByKey(stageNumber, `mirror:${fieldId}`);
        stage.hiddenInherited.add(fieldId);
      } else if (savedPosition && !stage.rows.flat().some(entry =>
        (entry as MirrorEntry).kind === 'mirror' && (entry as MirrorEntry).refId === fieldId)) {
        this.insertReviewEntry(stageNumber, { kind: 'mirror', refId: fieldId }, savedPosition);
      }
    }
  }

  private revertHiddenFieldsReviewCard(card: ReviewChangeCard): void {
    const stageNumber = card.target.stage || 1;
    const stage = this.wf.stages[stageNumber];
    if (!stage) throw new Error('Draft stage is unavailable');
    card.changes.forEach(change => {
      const savedHidden = new Set<string>(Array.isArray(change.before) ? change.before : []);
      const currentHidden = new Set<string>(Array.isArray(change.after) ? change.after : []);
      const changedIds = Array.from(new Set([...savedHidden, ...currentHidden]))
        .filter(id => savedHidden.has(id) !== currentHidden.has(id));
      stage.hiddenInherited = new Set(savedHidden);
      changedIds.forEach(fieldId => {
        if (savedHidden.has(fieldId)) {
          this.removeFieldEntryByKey(stageNumber, `mirror:${fieldId}`);
          stage.hiddenInherited.add(fieldId);
        } else {
          const saved = this.savedReviewSnapshot();
          const savedStage = saved?.stages?.[stageNumber] ?? saved?.stages?.[String(stageNumber)];
          const position = this.findFieldPosition(savedStage?.rows || [], `mirror:${fieldId}`);
          if (position && !stage.rows.flat().some(entry =>
            (entry as MirrorEntry).kind === 'mirror' && (entry as MirrorEntry).refId === fieldId)) {
            this.insertReviewEntry(stageNumber, { kind: 'mirror', refId: fieldId }, position);
          }
        }
      });
    });
  }

  private restoreSavedFieldLayout(stageNumber: number): void {
    const stage = this.wf.stages[stageNumber];
    const saved = this.savedReviewSnapshot();
    const savedStage = saved?.stages?.[stageNumber] ?? saved?.stages?.[String(stageNumber)];
    if (!stage || !savedStage?.rows) throw new Error('Saved layout is unavailable');
    const working = new Map<string, StageEntry>();
    const originalRows = stage.rows.map(row => [...row]);
    originalRows.flat().forEach(entry => working.set(this.reviewEntryKey(entry, stageNumber), entry));
    const used = new Set<string>();
    const restoredRows: StageEntry[][] = savedStage.rows.map((row: StageEntry[]) =>
      row.map(entry => {
        const key = this.reviewEntryKey(entry, stageNumber);
        const current = working.get(key);
        if (current) used.add(key);
        return current;
      }).filter(Boolean) as StageEntry[]
    );
    originalRows.forEach((row, rowIndex) => row.forEach(entry => {
      const key = this.reviewEntryKey(entry, stageNumber);
      if (used.has(key)) return;
      while (restoredRows.length <= rowIndex && restoredRows.length < 10) restoredRows.push([]);
      let targetRow = restoredRows[rowIndex]?.length < 3 ? restoredRows[rowIndex] : undefined;
      targetRow ||= restoredRows.slice(rowIndex + 1).find(candidate => candidate.length < 3);
      targetRow ||= restoredRows.slice(0, rowIndex).find(candidate => candidate.length < 3);
      if (!targetRow) {
        if (restoredRows.length >= 10) throw new Error('The saved field layout cannot fit in this stage');
        targetRow = [];
        restoredRows.splice(Math.min(rowIndex + 1, restoredRows.length), 0, targetRow);
      }
      targetRow.push(entry);
      used.add(key);
    }));
    stage.rows = restoredRows.filter(row => row.length > 0);
  }

  private restoreSavedActionOrder(stageNumber: number): void {
    const stage = this.wf.stages[stageNumber];
    const saved = this.savedReviewSnapshot();
    const savedStage = saved?.stages?.[stageNumber] ?? saved?.stages?.[String(stageNumber)];
    if (!stage || !savedStage?.actions) throw new Error('Saved action order is unavailable');
    const currentActions = [...stage.actions];
    const working = new Map(currentActions.map(action => [action.id, action]));
    const savedIds = new Set<string>(savedStage.actions.map((action: ActionBtn) => action.id));
    const restored = savedStage.actions
      .map((action: ActionBtn) => working.get(action.id))
      .filter(Boolean) as ActionBtn[];
    currentActions.forEach((action, currentIndex) => {
      if (savedIds.has(action.id)) return;
      restored.splice(Math.min(currentIndex, restored.length), 0, action);
    });
    stage.actions = restored;
  }

  private restoreWorkflowPath(path: string, value: any): void {
    if (!path || path.endsWith('.layout')) return;
    this.restoreNestedValue(this.wf as any, path.split('.'), value);
  }

  private restoreNestedValue(target: any, parts: string[], value: any): void {
    if (!target || parts.length === 0) return;
    let cursor = target;
    for (let index = 0; index < parts.length - 1; index++) {
      const part = parts[index];
      if (cursor[part] == null || typeof cursor[part] !== 'object') cursor[part] = {};
      cursor = cursor[part];
    }
    const key = parts[parts.length - 1];
    if (value === undefined) delete cursor[key];
    else if (key === 'hiddenInherited') cursor[key] = new Set(Array.isArray(value) ? value : []);
    else cursor[key] = this.cloneReviewValue(value);
  }

  private cloneReviewValue<T>(value: T): T {
    return value === undefined ? value : JSON.parse(JSON.stringify(this.cleanForSnapshot(value)));
  }

  private workflowDanglingReferenceKeys(): Set<string> {
    const fieldIds = new Set<string>();
    const stageNumbers = new Set<number>();
    for (let stageNumber = 1; stageNumber <= this.wf.stageCount; stageNumber++) {
      this.collectSavedStageReferences(this.wf.stages[stageNumber], fieldIds, stageNumbers);
    }
    const dangling = new Set<string>();
    fieldIds.forEach(id => {
      if (!this.workflowHasField(id)) dangling.add(`field:${id}`);
    });
    stageNumbers.forEach(stage => {
      if (!this.wf.stages[stage]) dangling.add(`stage:${stage}`);
    });
    return dangling;
  }

  get reviewChangeCount(): number {
    return this.reviewGroups.reduce((count, group) => count + group.cards.length, 0);
  }

  reviewPropertyLabel(change: AuditChange, card: ReviewChangeCard): string {
    if (change.kind === 'added' || change.kind === 'removed') return this.reviewCategoryLabel(card.target);
    const key = card.target.entityKey;
    if (key) {
      const marker = `.${key}.`;
      const markerIndex = change.path.indexOf(marker);
      if (markerIndex >= 0) return this.humanizePath(change.path.slice(markerIndex + marker.length));
    }
    const stagePrefix = card.target.stage ? `stages.${card.target.stage}.` : '';
    const relative = stagePrefix && change.path.startsWith(stagePrefix)
      ? change.path.slice(stagePrefix.length)
      : change.path;
    return REVIEW_PROPERTY_LABELS[relative] || this.humanizePath(relative || 'value');
  }

  private reviewCategoryLabel(target: ReviewTarget): string {
    switch (target.kind) {
      case 'field':
      case 'hidden-fields':
        return 'Input field';
      case 'action':
      case 'actions':
        return 'Action';
      case 'routing':
        return 'Routing';
      case 'final-output':
        return 'Output';
      case 'stage':
      case 'stages':
        return 'Stage';
      case 'form':
        return 'Form layout';
      default:
        return 'Component';
    }
  }

  resolveReviewValue(path: string, rawValue: any, side: 'saved' | 'draft'): string {
    const relative = this.reviewRelativePath(path);
    if (REVIEW_FIELD_REF_PROPERTIES.has(relative)) {
      if (rawValue === null || rawValue === undefined || rawValue === '') return 'Not selected';
      const snapshot = side === 'saved' ? this.savedReviewSnapshot() : this.wf;
      return this.resolveFieldDisplay(snapshot, this.reviewPathStage(path), String(rawValue)) || 'Removed field';
    }
    if (relative === 'routing.approvalTarget.type') {
      return this.formatApprovalTarget({ type: rawValue }) || this.emptyReviewValue(relative);
    }
    if (relative === 'routing.rejectionTarget.type') {
      return this.formatRejectionTarget({ type: rawValue }) || this.emptyReviewValue(relative);
    }
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return this.emptyReviewValue(relative);
    }
    return this.formatReviewValue(rawValue);
  }

  private emptyReviewValue(relative: string): string {
    if (REVIEW_FIELD_REF_PROPERTIES.has(relative)) return 'Not selected';
    if (relative === 'routing.escalationTarget') return 'Not selected';
    if (relative === 'routing.approvalTarget.type' || relative === 'routing.rejectionTarget.type') return 'Not selected';
    if (relative.endsWith('actorRoles') || relative.endsWith('recipients') || relative.endsWith('rules')) return 'None configured';
    return 'Not provided';
  }

  private reviewRelativePath(path: string): string {
    return path.replace(/^stages\.\d+\./, '');
  }

  private reviewPathStage(path: string): number {
    const match = path.match(/^stages\.(\d+)\./);
    return match ? Number(match[1]) : (this.wf?.stageCount || 1);
  }

  private savedReviewSnapshotCache: { json: string; snapshot: any } | null = null;

  private savedReviewSnapshot(): any {
    if (!this.serverBaselineSnapshotJson) return null;
    if (this.savedReviewSnapshotCache?.json !== this.serverBaselineSnapshotJson) {
      let snapshot: any = null;
      try {
        snapshot = JSON.parse(this.serverBaselineSnapshotJson);
      } catch {
        snapshot = null;
      }
      this.savedReviewSnapshotCache = { json: this.serverBaselineSnapshotJson, snapshot };
    }
    return this.savedReviewSnapshotCache.snapshot;
  }

  private snapshotFieldsForStage(snapshot: any, stageNumber: number): any[] {
    if (!snapshot?.stages) return [];
    const stage = snapshot.stages[stageNumber] ?? snapshot.stages[String(stageNumber)];
    if (!stage?.rows) return [];
    const stageOne = snapshot.stages[1] ?? snapshot.stages['1'];
    const stageOneById = new Map<string, any>();
    (stageOne?.rows || []).forEach((row: any) =>
      (Array.isArray(row) ? row : []).forEach((field: any) => {
        if (field?.id) stageOneById.set(field.id, field);
      })
    );
    const fields: any[] = [];
    stage.rows.forEach((row: any) =>
      (Array.isArray(row) ? row : []).forEach((entry: any) => {
        if (!entry) return;
        if (entry.kind === 'mirror') {
          const source = stageOneById.get(entry.refId);
          if (source) fields.push(source);
        } else if (entry.kind === 'native') {
          if (entry.field) fields.push(entry.field);
        } else {
          fields.push(entry);
        }
      })
    );
    return fields;
  }

  private resolveFieldDisplay(snapshot: any, stageNumber: number, fieldId: string): string | null {
    if (!snapshot) return null;
    const fields = this.snapshotFieldsForStage(snapshot, stageNumber);
    const match = fields.find((field) => field && field.id === fieldId);
    if (!match) return null;
    const label = match.label || this.reviewFieldTypeLabel(match) || 'Field';
    if (match.label) {
      const duplicate = fields.filter((field) => (field.label || '') === match.label).length > 1;
      if (duplicate) {
        const typeLabel = this.reviewFieldTypeLabel(match);
        if (typeLabel) return `${label} · ${typeLabel}`;
      }
    }
    return label;
  }

  onReviewCardEnter(card: ReviewChangeCard): void {
    this.reviewHoverCardId = card.id;
    this.previewReviewCard(card);
  }

  onReviewCardLeave(card: ReviewChangeCard): void {
    if (this.reviewHoverCardId === card.id) this.reviewHoverCardId = null;
    this.restoreReviewPreviewFromActiveCard();
  }

  onReviewCardFocus(card: ReviewChangeCard): void {
    this.reviewFocusCardId = card.id;
    this.previewReviewCard(card);
  }

  activateReviewCard(card: ReviewChangeCard): void {
    this.previewReviewCard(card);
    if (this.openRoutingForReviewCard(card)) return;
    this.closeReviewOpenedRoutingPanel();
    if (!window.matchMedia('(max-width: 1100px)').matches) return;
    this.reviewPreviewPinned = true;
    this.reviewHoverCardId = null;
    this.reviewFocusCardId = null;
    this.reviewPanelOpen = false;
    this.changeDetector.detectChanges();
    const target = document.getElementById(this.reviewTargetDomId(card.target));
    target?.setAttribute('tabindex', '-1');
    target?.focus();
  }

  private routingOpenedFromReview = false;
  private routingEditedInReview = false;

  private openRoutingForReviewCard(card: ReviewChangeCard): boolean {
    if (card.target.kind !== 'routing' && card.target.kind !== 'final-output') return false;
    this.reviewPreviewPinned = true;
    this.reviewHoverCardId = null;
    this.reviewFocusCardId = null;
    this.routingOpenedFromReview = true;
    this.routingEditedInReview = false;
    this.openRouting();
    this.changeDetector.detectChanges();
    const anchorId = this.routingAnchorForPath(card.changes[0]?.path || '');
    if (anchorId) {
      const el = document.getElementById(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.remove('rt-flash');
        void el.offsetWidth;
        el.classList.add('rt-flash');
      }
    }
    return true;
  }

  private closeReviewOpenedRoutingPanel(): void {
    if (this.routingPanelOpen && this.routingOpenedFromReview && !this.routingEditedInReview) {
      this.routingPanelOpen = false;
      this.routingOpenedFromReview = false;
      this.reviewPreviewPinned = false;
    }
  }

  private routingAnchorForPath(path: string): string | null {
    const rel = path.replace(/^stages\.\d+\./, '');
    const rules: Array<[RegExp, string]> = [
      [/^routing\.notifications\.transition/, 'rt-notifications-transition'],
      [/^routing\.notifications\.(perItem|perItemRecipientField|perItemEventLabel)/, 'rt-notifications-perItem'],
      [/^routing\.notifications\.(reminder|reminderSchedule|reminderTime)/, 'rt-notifications-reminder'],
      [/^routing\.notifications\.escalation/, 'rt-notifications-escalation'],
      [/^routing\.notifications\.rules/, 'rt-notifications-rules'],
      [/^routing\.timeBudget/, 'rt-timeBudget'],
      [/^routing\.escalationTarget/, 'rt-escalationTarget'],
      [/^routing\.approvalTarget/, 'rt-approvalTarget'],
      [/^routing\.rejectionTarget/, 'rt-rejectionTarget'],
      [/^routing\.conditionalRoute/, 'rt-conditionalRoute'],
      [/^routing\.secondApprover/, 'rt-secondApprover'],
      [/^routing\.immutable/, 'rt-immutable'],
      [/^routing\.retention/, 'rt-retention'],
      [/^finalOutputs\.pdf/, 'rt-finalOutputs-pdf'],
      [/^finalOutputs\.email/, 'rt-finalOutputs-email'],
      [/^finalOutputs/, 'rt-finalOutputs'],
    ];
    for (const [re, id] of rules) if (re.test(rel)) return id;
    return null;
  }

  onReviewCardBlur(card: ReviewChangeCard): void {
    if (this.reviewFocusCardId === card.id) this.reviewFocusCardId = null;
    this.restoreReviewPreviewFromActiveCard();
  }

  private restoreReviewPreviewFromActiveCard(): void {
    const activeId = this.reviewHoverCardId || this.reviewFocusCardId;
    if (!activeId) {
      if (!this.reviewPreviewPinned) this.clearReviewPreview();
      return;
    }
    const activeCard = this.reviewGroups
      .flatMap((group) => group.cards)
      .find((card) => card.id === activeId);
    if (activeCard) this.previewReviewCard(activeCard);
    else this.clearReviewPreview();
  }

  @HostListener('document:keydown.escape')
  closeReviewOnEscape(): void {
    if (this.reviewPanelOpen) this.closeReview();
    else if (this.reviewPreviewPinned) this.clearReviewPreview();
  }

  previewReviewCard(card: ReviewChangeCard): void {
    if (!this.wf) return;
    if (!this.reviewPreview) this.reviewStageBeforePreview = this.wf.activeStage;
    this.reviewPreview = card;
    const stage = card.target.stage;
    if (stage && this.wf.stages[stage]) this.wf.activeStage = stage;
    this.changeDetector.detectChanges();
    const element = document.getElementById(this.reviewTargetDomId(card.target));
    element?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }

  clearReviewPreview(restoreStage = true): void {
    if (restoreStage && this.wf && this.reviewStageBeforePreview && this.wf.stages[this.reviewStageBeforePreview]) {
      this.wf.activeStage = this.reviewStageBeforePreview;
    }
    this.reviewPreview = null;
    this.reviewPreviewPinned = false;
    this.reviewStageBeforePreview = null;
    this.reviewHoverCardId = null;
    this.reviewFocusCardId = null;
  }

  private reviewHoverCardId: string | null = null;
  private reviewFocusCardId: string | null = null;

  private reviewTargetDomId(target: ReviewTarget): string {
    if (target.kind === 'field') return this.reviewFieldDomId(target.stage || 1, target.entityKey || '');
    if (target.kind === 'action') return this.reviewActionDomId(target.stage || 1, target.entityKey || '');
    if (target.kind === 'routing') return 'wf-routing-trigger';
    if (target.kind === 'stages') return 'wf-stage-strip';
    if (target.kind === 'hidden-fields') {
      return document.getElementById('wf-hidden-fields') ? 'wf-hidden-fields' : 'wf-form-rows';
    }
    if (target.kind === 'form') return 'wf-form-rows';
    if (target.kind === 'actions') return 'wf-actions-area';
    if (target.kind === 'stage' || target.kind === 'final-output') return `wf-stage-pill-${target.stage || 1}`;
    return 'wf-workflow-identity';
  }

  reviewFieldDomId(stage: number, entityKey: string): string {
    return `wf-field-${stage}-${this.safeReviewDomKey(entityKey)}`;
  }

  reviewActionDomId(stage: number, entityKey: string): string {
    return `wf-action-${stage}-${this.safeReviewDomKey(entityKey)}`;
  }

  private safeReviewDomKey(value: string): string {
    return value.replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  reviewEntryEntityKey(entry: StageEntry): string {
    if (this.wf.activeStage === 1) return `field:${(entry as Field).id}`;
    if ((entry as MirrorEntry).kind === 'mirror') return `mirror:${(entry as MirrorEntry).refId}`;
    return `native:${(entry as NativeEntry).field.id}`;
  }

  isReviewFieldTarget(entry: StageEntry): boolean {
    const target = this.reviewPreview?.target;
    return !!target && target.kind === 'field' && target.stage === this.wf.activeStage &&
      target.entityKey === this.reviewEntryEntityKey(entry);
  }

  isReviewActionTarget(action: ActionBtn): boolean {
    const target = this.reviewPreview?.target;
    return !!target && target.kind === 'action' && target.stage === this.wf.activeStage &&
      target.entityKey === `action:${action.id}`;
  }

  isReviewTarget(kind: ReviewTargetKind, stage?: number): boolean {
    const target = this.reviewPreview?.target;
    return !!target && target.kind === kind && (stage == null || target.stage === stage);
  }

  isReviewHiddenFieldsFallback(stage: number): boolean {
    return this.isReviewTarget('hidden-fields', stage) && this.hiddenPoolForStage(stage).length === 0;
  }

  reviewSavedField(): Field | null {
    const target = this.reviewPreview?.target;
    if (!target || target.kind !== 'field' || !target.beforeEntity) return null;
    const entity = target.beforeEntity;
    if (entity.kind === 'mirror') return this.savedStageOneField(entity.refId);
    const { kind: _kind, ...field } = entity;
    return field as Field;
  }

  reviewSavedAction(): ActionBtn | null {
    const target = this.reviewPreview?.target;
    if (!target || target.kind !== 'action' || !target.beforeEntity) return null;
    return target.beforeEntity as ActionBtn;
  }

  reviewRemovedStageNumber(): number | null {
    const target = this.reviewPreview?.target;
    if (this.reviewPreview?.kind !== 'removed' || target?.kind !== 'stage' || !target.stage) return null;
    return this.wf.stages[target.stage] ? null : target.stage;
  }

  reviewSavedStageName(): string {
    const target = this.reviewPreview?.target;
    return target?.beforeEntity?.name || `Stage ${target?.stage || ''}`.trim();
  }

  reviewSavedFieldIsMirror(): boolean {
    return this.reviewPreview?.target.beforeEntity?.kind === 'mirror';
  }

  reviewFieldTypeLabel(field: any): string {
    return this.fieldTypes[field?.type as FieldType]?.label || 'Field';
  }

  reviewFieldTypeIsAuto(field: any): boolean {
    return !!this.fieldTypes[field?.type as FieldType]?.auto;
  }

  savedWorkflowName(): string {
    if (!this.serverBaselineJson) return this.wf?.name || 'workflow';
    try {
      return JSON.parse(this.serverBaselineJson)?.name || this.wf?.name || 'workflow';
    } catch {
      return this.wf?.name || 'workflow';
    }
  }

  reviewSavedPosition(): string {
    const position = this.reviewPreview?.target.beforePosition;
    if (!position) return 'Saved version';
    if (this.reviewPreview?.target.kind === 'action') return `Saved position ${position.column + 1}`;
    return `Saved row ${position.row + 1}, column ${position.column + 1}`;
  }

  private savedStageOneField(id: string): Field | null {
    if (!this.serverBaselineJson) return null;
    try {
      const saved = JSON.parse(this.serverBaselineJson);
      const rows = saved?.stages?.[1]?.rows || saved?.stages?.['1']?.rows || [];
      for (const row of rows) {
        const field = Array.isArray(row) ? row.find((entry: any) => entry?.id === id) : null;
        if (field) return field as Field;
      }
    } catch {
      return null;
    }
    return null;
  }

  async discardUnsavedChanges(): Promise<void> {
    if (!this.wf) return;
    this.showConfirm({
      title: 'Discard changes?',
      body: 'Your unsaved changes will be permanently removed, and the workflow will return to the last saved version.',
      okLabel: 'Discard changes',
      okClass: 'danger',
      onConfirm: () => void this.performDiscardUnsavedChanges(),
    });
  }

  private async performDiscardUnsavedChanges(): Promise<void> {
    void this.clearRecoveryCopy();
    if (this.workflowTemplateId != null) {
      await this.refreshFromServer();
    } else if (this.wf.sku) {
      await this.loadWorkflowBySku(this.wf.sku);
      this.showToast('Unsaved changes discarded.');
    }
    this.reviewChanges = [];
    this.reviewGroups = [];
    this.closeReview();
  }

  /* ============================================================
     Stage / entry resolution helpers (template-facing)
     ============================================================ */
  get activeStage(): Stage {
    return this.wf.stages[this.wf.activeStage];
  }
  get stageIndices(): number[] {
    return Array.from({ length: this.wf.stageCount }, (_, i) => i + 1);
  }

  private stageIndexOf(stage: Stage): number | null {
    for (let i = 1; i <= this.wf.stageCount; i++) {
      if (this.wf.stages[i] === stage) return i;
    }
    return null;
  }
  private findStage1FieldById(id: string): Field | null {
    const s1 = this.wf.stages[1];
    if (!s1) return null;
    for (const row of s1.rows) {
      for (const f of row) {
        if (f && (f as Field).id === id && !('kind' in (f as any))) return f as Field;
      }
    }
    return null;
  }

  isMirror(entry: StageEntry): boolean {
    return this.wf.activeStage > 1 && (entry as MirrorEntry).kind === 'mirror';
  }
  entryField(entry: StageEntry): Field | null {
    if (this.wf.activeStage === 1) return entry as Field;
    if ((entry as MirrorEntry).kind === 'mirror') {
      return this.findStage1FieldById((entry as MirrorEntry).refId);
    }
    return (entry as NativeEntry).field;
  }
  entryKey(entry: StageEntry): string {
    if ((entry as MirrorEntry).kind === 'mirror') return 'm_' + (entry as MirrorEntry).refId;
    if ((entry as NativeEntry).kind === 'native') return 'n_' + (entry as NativeEntry).field.id;
    return (entry as Field).id;
  }
  private entryKeyAt(entry: StageEntry, stageIdx: number): string {
    if (stageIdx === 1) return (entry as Field).id;
    if ((entry as MirrorEntry).kind === 'mirror') return 'm_' + (entry as MirrorEntry).refId;
    return 'n_' + (entry as NativeEntry).field.id;
  }

  allFieldsInStage(stage: Stage): Field[] {
    const idx = this.stageIndexOf(stage);
    if (idx === 1 || idx === null) return stage.rows.flat().filter(Boolean) as Field[];
    return stage.rows
      .flat()
      .map((e) =>
        (e as MirrorEntry).kind === 'mirror'
          ? this.findStage1FieldById((e as MirrorEntry).refId)
          : (e as NativeEntry).field
      )
      .filter(Boolean) as Field[];
  }

  /* ---- mirror propagation (Stage 1 fields cascade to later stages) ---- */
  private onFieldAddedToStage1(field: Field): void {
    for (let i = 2; i <= this.wf.stageCount; i++) {
      const s = this.wf.stages[i];
      if (s.hiddenInherited.has(field.id)) continue;
      const exists = s.rows
        .flat()
        .some((e) => (e as MirrorEntry).kind === 'mirror' && (e as MirrorEntry).refId === field.id);
      if (!exists) this.insertMirrorIntoStage(s, field.id);
    }
  }

  private insertMirrorIntoStage(s: Stage, refId: string): void {
    const entry: MirrorEntry = { kind: 'mirror', refId };
    const sourceRow = (this.wf.stages[1].rows as Field[][]).find((row) =>
      row.some((f) => f?.id === refId)
    );
    const order = (sourceRow ?? []).map((f) => f.id);
    const targetIdx = s.rows.findIndex((row) =>
      row.some(
        (e) => (e as MirrorEntry).kind === 'mirror' && order.includes((e as MirrorEntry).refId)
      )
    );
    if (targetIdx === -1 || s.rows[targetIdx].length >= 3) {
      s.rows.push([entry]);
      return;
    }
    const pos = order.indexOf(refId);
    const before = s.rows[targetIdx].findIndex((e) => {
      const m = e as MirrorEntry;
      return m.kind === 'mirror' && order.indexOf(m.refId) > pos;
    });
    if (before === -1) s.rows[targetIdx].push(entry);
    else s.rows[targetIdx].splice(before, 0, entry);
  }
  private onFieldDeletedFromStage1(fieldId: string): void {
    for (let i = 2; i <= this.wf.stageCount; i++) {
      const s = this.wf.stages[i];
      s.rows = s.rows
        .map((row) =>
          row.filter(
            (e) => !((e as MirrorEntry).kind === 'mirror' && (e as MirrorEntry).refId === fieldId)
          )
        )
        .filter((r) => r.length > 0);
      s.hiddenInherited.delete(fieldId);
    }
  }
  removeMirrorFromStage(stageIdx: number, refId: string): void {
    const s = this.wf.stages[stageIdx];
    if (!s) return;
    s.rows = s.rows
      .map((row) =>
        row.filter(
          (e) => !((e as MirrorEntry).kind === 'mirror' && (e as MirrorEntry).refId === refId)
        )
      )
      .filter((r) => r.length > 0);
    s.hiddenInherited.add(refId);
    if (stageIdx === 2 && this.wf.stageCount >= 3) this.removeMirrorFromStage(3, refId);
    this.markDirty();
  }
  readdMirrorToStage(stageIdx: number, refId: string): void {
    const s = this.wf.stages[stageIdx];
    s.hiddenInherited.delete(refId);
    const exists = s.rows
      .flat()
      .some((e) => (e as MirrorEntry).kind === 'mirror' && (e as MirrorEntry).refId === refId);
    if (!exists) this.insertMirrorIntoStage(s, refId);
    this.markDirty();
  }
  hiddenPoolForStage(stageIdx: number): Field[] {
    const s1 = this.wf.stages[1];
    if (!s1) return [];
    const visible = new Set(
      this.wf.stages[stageIdx].rows
        .flat()
        .filter((e) => (e as MirrorEntry).kind === 'mirror')
        .map((e) => (e as MirrorEntry).refId)
    );
    return (s1.rows.flat() as Field[]).filter((f) => !visible.has(f.id));
  }
  private mirrorAllStage1Into(stageIdx: number): void {
    const s = this.wf.stages[stageIdx];
    s.rows = (this.wf.stages[1].rows as Field[][])
      .map((row) =>
        row.filter(Boolean).map((f) => ({ kind: 'mirror', refId: f.id } as MirrorEntry))
      )
      .filter((row) => row.length > 0);
    s.hiddenInherited = new Set();
  }

  /* ============================================================
     Top bar — stage tabs + name + lifecycle
     ============================================================ */
  selectStage(i: number): void {
    if (this.reviewPreview) this.clearReviewPreview();
    this.wf.activeStage = i;
  }

  get canModifyStageCount(): boolean {
    return !this.isActive && !this.isInactive && !this.saving;
  }

  canRemoveStage(i: number): boolean {
    return this.canModifyStageCount && this.wf.stageCount > 1 && i === this.wf.stageCount;
  }

  canAddStage(): boolean {
    return this.canModifyStageCount && this.wf.stageCount < 3;
  }

  addStage(ev?: Event): void {
    ev?.stopPropagation();
    if (!this.canAddStage()) return;

    const beforeCount = this.wf.stageCount;
    const newStageNumber = beforeCount + 1;
    const previousFinalStage = this.wf.stages[beforeCount];

    this.wf.stageCount = newStageNumber;
    this.wf.stages[newStageNumber] = blankStage(newStageNumber);
    this.mirrorAllStage1Into(newStageNumber);
    this.repairRoutingAfterStageAddition(previousFinalStage);
    this.wf.activeStage = newStageNumber;
    this.markDirty();
  }

  confirmRemoveStage(i: number, ev?: Event): void {
    ev?.stopPropagation();
    if (!this.canRemoveStage(i)) return;

    this.showConfirm({
      title: `Remove Stage ${i}?`,
      body: `Stage ${i} configuration will be discarded. Continue?`,
      okLabel: 'Remove Stage',
      okClass: 'danger',
      onConfirm: () => this.removeLastStage(i),
    });
  }

  private repairRoutingAfterStageAddition(previousFinalStage?: Stage): void {
    const target = previousFinalStage?.routing?.approvalTarget;
    if (!target || !previousFinalStage || !this.stageHasApprove(previousFinalStage)) return;

    if (target.type === 'close') {
      target.type = 'next';
      target.stage = null;
    }
  }

  private removeLastStage(stageNumber: number): void {
    if (!this.canRemoveStage(stageNumber)) return;

    const beforeCount = this.wf.stageCount;

    delete this.wf.stages[stageNumber];
    this.wf.stageCount = beforeCount - 1;
    if (this.wf.activeStage > this.wf.stageCount) this.wf.activeStage = this.wf.stageCount;
    this.repairRoutingAfterStageRemoval();
    this.markDirty();
  }

  private repairRoutingAfterStageRemoval(): void {
    for (let i = 1; i <= this.wf.stageCount; i++) {
      const target = this.wf.stages[i]?.routing?.approvalTarget;
      if (!target) continue;

      if (target.type === 'stage' && (target.stage == null || target.stage > this.wf.stageCount)) {
        target.type = i < this.wf.stageCount ? 'next' : 'close';
        target.stage = null;
      }

      if (target.type === 'next' && i >= this.wf.stageCount) {
        target.type = 'close';
        target.stage = null;
      }
    }
  }

  onNameBlur(ev: Event): void {
    const newName = (ev.target as HTMLElement).textContent?.trim() || 'Untitled';
    if (newName !== this.wf.name) {
      this.wf.name = newName;
      this.markDirty();
    }
  }
  get statusLabel(): string {
    if (this.requiresReview) return 'Requires Review';
    return this.wf.status.charAt(0) + this.wf.status.slice(1).toLowerCase();
  }
  get statusClass(): string {
    if (this.requiresReview) return 'status-review';
    return this.wf.status === 'PUBLISHED'
      ? 'status-active'
      : this.wf.status === 'INACTIVE'
      ? 'status-inactive'
      : 'status-draft';
  }

  /** True when an assigned actor role has been deactivated upstream (US-05 AC-04). */
  isRoleDeactivated(role: string): boolean {
    // Don't flag anything until the registry has loaded, otherwise every role
    // looks deactivated for a frame and the status flashes "Requires Review".
    return this.roleRegistry.isLoaded() && !this.roleRegistry.isActive(role);
  }
  stageHasDeactivatedRole(i: number): boolean {
    const s = this.wf.stages[i];
    return !!s && s.routing.actorRoles.some((role) => this.isRoleDeactivated(role));
  }
  /** Workflow-wide referential-integrity state: any stage referencing a dead role. */
  get requiresReview(): boolean {
    if (!this.wf) return false;
    for (let i = 1; i <= this.wf.stageCount; i++) {
      if (this.stageHasDeactivatedRole(i)) return true;
    }
    return false;
  }
  get isActive(): boolean {
    return this.wf.status === 'PUBLISHED';
  }
  get isInactive(): boolean {
    return this.wf.status === 'INACTIVE';
  }
  get canPublish(): boolean {
    return !this.validationServiceDown && this.lastValidationPassed && !this.hasUnsavedChanges();
  }
  get publishTitle(): string {
    return !this.canPublish
      ? (this.hasUnsavedChanges() ? 'Save & validate first' : 'Run Validate first')
      : 'Publish template';
  }

  stageDisplayName(i: number): string {
    const name = this.wf.stages[i]?.name?.trim();
    return name ? name : `Stage ${i}`;
  }

  private reachableStages(): Set<number> {
    const reached = new Set<number>([1]);
    const queue = [1];
    while (queue.length) {
      const i = queue.shift()!;
      const s = this.wf.stages[i];
      if (!s) continue;
      const targets: number[] = [];
      const advancesImplicitly = s.actions.some((a) => a.type === 'submit' || a.type === 'comment');
      if (advancesImplicitly && i < this.wf.stageCount) targets.push(i + 1);
      if (this.stageHasApprove(s)) {
        const t = s.routing.approvalTarget;
        if (t.type === 'next' && i < this.wf.stageCount) targets.push(i + 1);
        else if (t.type === 'stage' && t.stage != null) targets.push(t.stage);
      }
      if (this.stageHasReject(s) && s.routing.rejectionTarget.type === 'previous' && i > 1) {
        targets.push(i - 1);
      }
      for (const next of targets) {
        if (!reached.has(next) && this.wf.stages[next]) {
          reached.add(next);
          queue.push(next);
        }
      }
    }
    return reached;
  }

  private hasClosingPath(reachable: Set<number>): boolean {
    for (const i of reachable) {
      const s = this.wf.stages[i];
      if (!s) continue;
      if (this.stageHasApprove(s) && (s.routing.approvalTarget.type === 'close' || s.routing.approvalTarget.type === 'output')) return true;
      if (this.stageHasReject(s) && s.routing.rejectionTarget.type === 'close') return true;
    }
    return false;
  }

  stageHasApprove(stage: Stage): boolean {
    return stage.actions.some((a) => a.type === 'approve' || a.type === 'accept');
  }
  stageHasReject(stage: Stage): boolean {
    return stage.actions.some((a) => a.type === 'reject');
  }
  stageHasEscalate(stage: Stage): boolean {
    return stage.actions.some((a) => a.type === 'escalate');
  }

  /**
   * The runtime only auto-escalates on SLA breach when notifications.escalation is true. The
   * Escalate action + escalation target express exactly that intent, so keep the runtime switch
   * in sync — otherwise a stage configured to escalate silently never does.
   */
  private syncEscalationFlags(): void {
    for (let i = 1; i <= this.wf.stageCount; i++) {
      const s = this.wf.stages[i];
      if (s && this.stageHasEscalate(s) && s.routing.escalationTarget) {
        s.routing.notifications.escalation = true;
      }
    }
  }
  laterStageIndices(): number[] {
    const out: number[] = [];
    for (let i = this.wf.activeStage + 1; i <= this.wf.stageCount; i++) out.push(i);
    return out;
  }
  onApprovalTypeChange(routing: { approvalTarget: { type: string; stage: number | null } }): void {
    if (routing.approvalTarget.type !== 'stage') routing.approvalTarget.stage = null;
    this.markDirty();
  }

  toggleActorRole(routing: { actorRoles: string[] }, role: string): void {
    const i = routing.actorRoles.indexOf(role);
    if (i >= 0) routing.actorRoles.splice(i, 1);
    else routing.actorRoles.push(role);
    this.markDirty();
  }

  // #2: a stage takes a single actor role. Storage stays an array (runtime/serialization
  // compatibility), but the UI is a single-select dropdown holding at most one role.
  private static readonly ACTOR_ROLE_ORDER = ['initiator', 'first approver', 'second approver'];

  /** Active roles ordered Initiator → First Approver → Second Approver, then the rest. */
  orderedRoles(): string[] {
    const rank = (r: string) => {
      const i = WorkflowBuilderComponent.ACTOR_ROLE_ORDER.indexOf(r.trim().toLowerCase());
      return i === -1 ? WorkflowBuilderComponent.ACTOR_ROLE_ORDER.length : i;
    };
    return [...this.roles].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }

  getActorRole(routing: { actorRoles: string[] }): string {
    return routing.actorRoles[0] || '';
  }

  setActorRole(routing: { actorRoles: string[] }, role: string): void {
    routing.actorRoles = role ? [role] : [];
    this.markDirty();
  }

  /** Dropdown options — ordered active roles, plus the current role if it was deactivated. */
  actorRoleOptions(routing: { actorRoles: string[] }): string[] {
    const ordered = this.orderedRoles();
    const current = this.getActorRole(routing);
    return current && !ordered.includes(current) ? [current, ...ordered] : ordered;
  }

  /** Display label for the workflow tier — 'Managed' is shown to users as "Manual". */
  get tierLabel(): string {
    return this.wf?.tier === 'Managed' ? 'Manual' : this.wf?.tier;
  }

  roleModalOpen = false;
  openRoleModal(): void {
    this.roleModalOpen = true;
  }
  closeRoleModal(): void {
    this.roleModalOpen = false;
    // Pick up any roles added / reactivated so the checkbox list reflects them.
    this.roles = this.roleRegistry.getActiveRoles();
  }

  onTimeBudgetChange(routing: { timeBudget: { value: number } }): void {
    const raw = routing.timeBudget.value;
    // A cleared / non-numeric number input reports null; keep it invalid (NaN)
    // instead of coercing to 0 so validation can flag it.
    if (raw === null || raw === undefined || (raw as unknown as string) === '') {
      routing.timeBudget.value = NaN;
    } else {
      const num = Number(raw);
      routing.timeBudget.value = Number.isFinite(num) ? Math.floor(num) : NaN;
    }
    this.markDirty();
  }

  isTimeBudgetInvalid(value: number | null | undefined): boolean {
    return value === null || value === undefined || value < 0 || !Number.isFinite(value);
  }

  isStageNameDuplicate(index: number): boolean {
    const name = (this.wf.stages[index]?.name || '').trim().toLowerCase();
    if (!name) return false;
    for (let j = 1; j <= this.wf.stageCount; j++) {
      if (j === index) continue;
      if ((this.wf.stages[j]?.name || '').trim().toLowerCase() === name) return true;
    }
    return false;
  }

  /** Stage name must be non-blank and at least 3 characters once trimmed. */
  isStageNameInvalid(index: number): boolean {
    return (this.wf.stages[index]?.name || '').trim().length < 3;
  }
  private firstInvalidStageName(): number | null {
    for (let i = 1; i <= this.wf.stageCount; i++) {
      if (this.isStageNameInvalid(i)) return i;
    }
    return null;
  }
  private trimStageNames(): void {
    for (let i = 1; i <= this.wf.stageCount; i++) {
      const s = this.wf.stages[i];
      if (s && typeof s.name === 'string') s.name = s.name.trim();
    }
  }
  /** Trim the active stage name on blur so leading/trailing spaces never persist. */
  onStageNameBlur(): void {
    const s = this.activeStage;
    if (s && typeof s.name === 'string') {
      const trimmed = s.name.trim();
      if (trimmed !== s.name) {
        s.name = trimmed;
        this.markDirty();
      }
    }
  }

  /* stage-tab badge helpers */
  stageHasConditional(i: number): boolean {
    return this.wf.stages[i].routing.conditionalRoute.enabled && i > 1;
  }
  stageImmutable(i: number): boolean {
    return this.wf.stages[i].routing.immutable;
  }
  stageTimeBudget(i: number): string | null {
    const tb = this.wf.stages[i].routing.timeBudget;
    return tb.value > 0 ? `${tb.value}${tb.unit[0]}` : null;
  }

  /* ---- canvas header ---- */
  get stageSubtitle(): string {
    const r = this.activeStage.routing;
    const actors = r.actorRoles.length ? r.actorRoles.join(', ') : '— no actor —';
    return `Actor: ${actors}.${r.timeBudget.value > 0 ? ' Time budget ' + r.timeBudget.value + ' ' + r.timeBudget.unit + '.' : ''}`;
  }

  /* ============================================================
     Drag & drop (native HTML5) + tap-to-add fallback
     ============================================================ */
  onPaletteDragStart(ev: DragEvent, kind: 'field' | 'action', type: FieldType | ActionType): void {
    this.drag = { source: 'palette', kind, type };
    ev.dataTransfer?.setData('text/plain', type);
    if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'copy';
  }
  onCardDragStart(ev: DragEvent, key: string): void {
    this.drag = { source: 'canvas', entryKey: key };
    ev.dataTransfer?.setData('text/plain', key);
    if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
  }
  onDragEnd(): void {
    this.drag = null;
    this.hover = null;
  }
  allowDrop(ev: DragEvent, zone: string): void {
    ev.preventDefault();
    this.hover = zone;
  }
  clearHover(zone: string): void {
    if (this.hover === zone) this.hover = null;
  }

  dropNewRow(ev: DragEvent, rowIndex: number): void {
    ev.preventDefault();
    this.hover = null;
    this.handleDrop({ mode: 'newRowAt', rowIndex });
  }
  dropIntoRow(ev: DragEvent, rowIndex: number): void {
    ev.preventDefault();
    this.hover = null;
    this.handleDrop({ mode: 'intoRow', rowIndex });
  }
  dropActions(ev: DragEvent): void {
    ev.preventDefault();
    this.hover = null;
    if (this.drag && this.drag.source === 'palette' && this.drag.kind === 'action') {
      this.activeStage.actions.push(makeAction(this.drag.type as ActionType));
      this.markDirty();
    }
    this.drag = null;
  }

  private handleDrop(target: { mode: 'newRowAt' | 'intoRow'; rowIndex: number }): void {
    const data = this.drag;
    this.drag = null;
    if (!data) return;
    const stage = this.activeStage;
    const idx = this.wf.activeStage;

    if (data.source === 'palette' && data.kind === 'field') {
      if (stage.rows.length >= 10 && target.mode === 'newRowAt') {
        this.showToast('Max 10 rows per stage', 'alert-triangle');
        return;
      }
      const f = makeField(data.type as FieldType);
      const entry: StageEntry = idx === 1 ? f : ({ kind: 'native', field: f } as NativeEntry);
      if (target.mode === 'newRowAt') {
        stage.rows.splice(target.rowIndex, 0, [entry]);
      } else {
        if (stage.rows[target.rowIndex].length >= 3) {
          this.showToast('Max 3 elements per row', 'alert-triangle');
          return;
        }
        stage.rows[target.rowIndex].push(entry);
      }
      if (idx === 1) this.onFieldAddedToStage1(f);
      this.markDirty();
      return;
    }

    if (data.source === 'palette' && data.kind === 'action') {
      stage.actions.push(makeAction(data.type as ActionType));
      this.markDirty();
      return;
    }

    if (data.source === 'canvas') {
      this.moveEntry(data.entryKey, target);
      this.markDirty();
    }
  }

  private moveEntry(key: string, target: { mode: 'newRowAt' | 'intoRow'; rowIndex: number }): void {
    const stage = this.activeStage;
    const idx = this.wf.activeStage;
    let from: { ri: number; fi: number } | null = null;
    for (let ri = 0; ri < stage.rows.length && !from; ri++) {
      for (let fi = 0; fi < stage.rows[ri].length; fi++) {
        if (this.entryKeyAt(stage.rows[ri][fi], idx) === key) {
          from = { ri, fi };
          break;
        }
      }
    }
    if (!from) return;
    const [e] = stage.rows[from.ri].splice(from.fi, 1);
    if (stage.rows[from.ri].length === 0) stage.rows.splice(from.ri, 1);

    if (target.mode === 'newRowAt') {
      let i = target.rowIndex;
      if (from.ri < i) i--;
      stage.rows.splice(i, 0, [e]);
    } else {
      let i = Math.min(target.rowIndex, stage.rows.length - 1);
      if (i < 0) i = 0;
      if (!stage.rows[i] || stage.rows[i].length >= 3) {
        stage.rows.splice(from.ri, 0, [e]);
        return;
      }
      stage.rows[i].push(e);
    }
  }

  addFromPalette(kind: 'field' | 'action', type: FieldType | ActionType): void {
    const stage = this.activeStage;
    const idx = this.wf.activeStage;
    if (kind === 'action') {
      stage.actions.push(makeAction(type as ActionType));
      this.markDirty();
      this.paletteOpen = false;
      return;
    }
    if (stage.rows.length >= 10) {
      this.showToast('Max 10 rows per stage', 'alert-triangle');
      return;
    }
    const f = makeField(type as FieldType);
    const entry: StageEntry = idx === 1 ? f : ({ kind: 'native', field: f } as NativeEntry);
    // Try to fill the last row if it has space, otherwise add a new row.
    const last = stage.rows[stage.rows.length - 1];
    if (last && last.length < 3) last.push(entry);
    else stage.rows.push([entry]);
    if (idx === 1) this.onFieldAddedToStage1(f);
    this.markDirty();
    this.paletteOpen = false;
  }

  deleteEntry(key: string): void {
    const stage = this.activeStage;
    const idx = this.wf.activeStage;
    let removed: StageEntry | null = null;
    stage.rows.forEach((row, ri) => {
      stage.rows[ri] = row.filter((e) => {
        if (this.entryKeyAt(e, idx) === key) {
          removed = e;
          return false;
        }
        return true;
      });
    });
    stage.rows = stage.rows.filter((r) => r.length > 0);
    if (idx === 1 && removed) this.onFieldDeletedFromStage1((removed as Field).id);
    this.markDirty();
  }

  /* ---- field card inline controls ---- */
  toggleRequired(field: Field): void {
    field.required = !field.required;
    this.markDirty();
  }
  onLabelBlur(field: Field, ev: Event): void {
    const v = (ev.target as HTMLElement).textContent?.trim();
    field.label = v || this.fieldTypes[field.type].defaultLabel;
    this.markDirty();
  }
  onActionLabelBlur(action: ActionBtn, ev: Event): void {
    const v = (ev.target as HTMLElement).textContent?.trim();
    action.label = v || this.actionTypes[action.type].label;
    this.markDirty();
  }
  toggleRequireComment(action: ActionBtn): void {
    this.openActionComment(action);
  }
  openActionComment(action: ActionBtn): void {
    this.actionCommentTarget = action;
    this.actionCommentText = action.commentPrompt || '';
    this.actionCommentModalOpen = true;
  }
  closeActionComment(): void {
    this.actionCommentModalOpen = false;
    this.actionCommentTarget = null;
    this.actionCommentText = '';
  }
  isActionCommentSubmitEnabled(): boolean {
    return !!this.actionCommentTarget && this.actionCommentText.trim().length >= 2;
  }
  saveActionComment(): void {
    if (!this.actionCommentTarget || !this.isActionCommentSubmitEnabled()) return;
    this.actionCommentTarget.requireComment = true;
    this.actionCommentTarget.commentPrompt = this.actionCommentText.trim();
    this.markDirty();
    this.closeActionComment();
    this.showToast('Action comment saved.');
  }
  clearActionComment(): void {
    if (!this.actionCommentTarget) return;
    this.actionCommentTarget.requireComment = false;
    this.actionCommentTarget.commentPrompt = '';
    this.markDirty();
    this.closeActionComment();
    this.showToast('Action comment removed.');
  }
  deleteAction(action: ActionBtn): void {
    this.activeStage.actions = this.activeStage.actions.filter((a) => a.id !== action.id);
    this.markDirty();
  }
  actionColorClass(type: ActionType): string {
    const c = this.actionTypes[type].color;
    return c === 'green' ? 'act-green' : c === 'red' ? 'act-red' : 'act-grey';
  }
  mirrorRefId(entry: StageEntry): string {
    return (entry as MirrorEntry).refId;
  }
  refnumSample(field: Field): string {
    const pad = Math.max(0, (field.refnumCfg?.padding || 1) - 1);
    return (field.refnumCfg?.prefix || '') + '0'.repeat(pad) + '1';
  }

  /* ============================================================
     Field Config panel
     ============================================================ */
  openFieldConfig(field: Field): void {
    this.cfgField = field;
    this.fieldPanelOpen = true;
  }
  closeFieldConfig(): void {
    this.fieldPanelOpen = false;
    this.cfgField = null;
  }
  addOption(field: Field): void {
    field.options = field.options || [];
    field.options.push('Option ' + (field.options.length + 1));
    this.markDirty();
  }
  removeOption(field: Field, i: number): void {
    field.options?.splice(i, 1);
    this.markDirty();
  }
  reorderOption(field: Field, event: CdkDragDrop<string[]>): void {
    if (!field.options || event.previousIndex === event.currentIndex) return;
    moveItemInArray(field.options, event.previousIndex, event.currentIndex);
    this.markDirty();
  }
  /** Single-select / multi-select / dropdown variant for a dropdown field. */
  setDropdownVariant(field: Field, variant: DropdownVariant): void {
    field.dropdownVariant = variant;
    this.markDirty();
  }
  /** Bulk-upload dropdown options from a CSV (first column). Only the dropdown variant offers this. */
  onDropdownCsvSelected(event: Event, field: Field): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.showToast('Only CSV files are allowed.', 'alert-triangle');
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const values = (reader.result as string)
        .split(/\r?\n/)
        .map((line) => line.split(',')[0].trim())
        .filter((v) => v.length > 0);
      if (values.length < 2) {
        this.showToast('CSV must have at least 2 options.', 'alert-triangle');
        return;
      }
      field.options = values;
      field.label = values[0]; // card name defaults to first option
      this.markDirty();
    };
    reader.onerror = () => this.showToast('Error reading CSV file.', 'alert-triangle');
    reader.readAsText(file);
    input.value = '';
  }
  /** date / daterange fields available as duration sources within the active stage */
  dateFieldsInStage(): Field[] {
    return this.allFieldsInStage(this.activeStage).filter(
      (f) => f.type === 'date' || f.type === 'daterange'
    );
  }
  visibilitySourceFields(field: Field): Field[] {
    return this.allFieldsInStage(this.activeStage).filter((f) => f.id !== field.id);
  }
  guardKeyFields(): Field[] {
    return this.allFieldsInStage(this.activeStage);
  }
  toggleGuardKey(field: Field, id: string): void {
    const i = field.guard.keys.indexOf(id);
    if (i >= 0) field.guard.keys.splice(i, 1);
    else field.guard.keys.push(id);
    this.markDirty();
  }
  // Enabling a submission guard auto-matches (checks) the field's own key so the config is
  // meaningful immediately instead of starting with an empty selection.
  toggleGuard(field: Field): void {
    field.guard.enabled = !field.guard.enabled;
    if (field.guard.enabled && field.guard.keys.length === 0) {
      field.guard.keys = [field.id];
    } else if (
      !field.guard.enabled &&
      field.guard.keys.length === 1 &&
      field.guard.keys[0] === field.id
    ) {
      // Remove the key that was auto-added for a default off → on → off cycle,
      // while preserving an existing composite-key setup for a later re-enable.
      field.guard.keys = [];
    }
    this.markDirty();
  }
  addCondition(list: Condition[]): void {
    list.push({ join: 'AND', fieldId: '', op: 'equals', value: '' });
    this.markDirty();
  }
  removeCondition(list: Condition[], i: number): void {
    list.splice(i, 1);
    this.markDirty();
  }

  /* ============================================================
     Stage Routing panel
     ============================================================ */
  openRouting(): void {
    this.routingPanelOpen = true;
    this.checkNotificationTemplates();
    void this.refreshUnresolvableRoles();
  }

  /** Re-check email recipient roles so an unresolved-role warning persists (e.g. after Save Anyway). */
  private async refreshUnresolvableRoles(): Promise<void> {
    this.unresolvableEmailRoles = new Set(await this.findUnresolvableEmailRoles());
  }

  /** Degrade gracefully — any failure reaching the template service shows the banner. */
  private checkNotificationTemplates(): void {
    this.workflowService.notificationTemplatesHealth().subscribe({
      next: (res) => (this.notificationTemplatesUnavailable = !res?.available),
      error: () => (this.notificationTemplatesUnavailable = true),
    });
  }
  closeRouting(): void {
    this.routingPanelOpen = false;
    this.routingEditedInReview = false;
    if (this.routingOpenedFromReview) {
      this.routingOpenedFromReview = false;
      this.clearReviewPreview();
    }
  }

  /* ============================================================
     Assign workflow modal
     ============================================================ */
  openAssign(): void {
    this.initAssignPickers();
    this.assignModalOpen = true;
  }
  openSchedule(): void {
    this.activeGroup = null;
    this.initAssignPickers();
    this.scheduleModalOpen = true;
  }
  /** Loads account holders / clients / groups shared by both the Assign and Schedule modals. */
  private initAssignPickers(): void {
    this.closeMenus();
    this.assignSaving = false;
    this.groupSchedules.clear();
    this.groupSearch = '';
    this.groupDropdownOpen = false;
    this.accountHolderId = null;
    this.clientPlantId = null;
    this.clients = [];
    this.assignGroups = [];
    this.selectedGroupKeys.clear();
    this.existingAssignments = [];

    // Load existing assignments first so that when an account-holder/client pair
    // is selected, its already-assigned groups pre-fill the selection (the user
    // adds/removes from there). A re-save of the same pair replaces it; a new
    // pair adds another, so multiple account holder groups can coexist.
    if (this.workflowTemplateId != null) {
      this.workflowService.getAssignments(this.workflowTemplateId).subscribe({
        next: (list) => {
          this.existingAssignments = list || [];
          this.loadCompanies();
        },
        error: () => this.loadCompanies(),
      });
    } else {
      this.loadCompanies();
    }
  }
  closeAssign(): void {
    this.assignModalOpen = false;
    this.groupDropdownOpen = false;
  }
  closeSchedule(): void {
    this.scheduleModalOpen = false;
  }

  private loadCompanies(): void {
    this.companiesLoading = true;
    this.companyLoadError = '';
    this.companyService.retrieveCompanies().subscribe({
      next: (companies) => {
        this.companies = (companies || []).filter((c) => !c.isDeleted);
        // Default to the first company; selecting a pair pre-fills its saved groups.
        if (this.companies.length) {
          this.accountHolderId = this.companies[0].companyId;
        }
        this.companiesLoading = false;
        if (this.accountHolderId != null) this.loadClientsForAccountHolder(this.accountHolderId);
      },
      error: () => {
        this.companyLoadError = 'Unable to load companies.';
        this.companiesLoading = false;
      },
    });
  }

  /** Account Holder (company) → Client (its plants). Reloads the client list on change. */
  onAccountHolderChange(): void {
    this.clientPlantId = null;
    this.clients = [];
    this.assignGroups = [];
    this.selectedGroupKeys.clear();
    if (this.accountHolderId != null) this.loadClientsForAccountHolder(this.accountHolderId);
  }

  private loadClientsForAccountHolder(companyId: number): void {
    this.clientsLoading = true;
    this.clientsError = '';
    this.plantService.retrieveCompanyPlants(String(companyId)).subscribe({
      next: (plants) => {
        this.clients = (plants || []).filter((p) => !p.isDeleted);
        this.clientsLoading = false;
        // Default to the first client and load its groups (pre-selecting any saved).
        if (this.clients.length) {
          this.clientPlantId = this.clients[0].plantId;
          this.loadGroupsForClient(this.clientPlantId);
        }
      },
      error: () => {
        this.clientsError = 'Unable to load clients.';
        this.clientsLoading = false;
      },
    });
  }

  /** Client (plant) → its groups. Reloads on change. */
  onClientChange(): void {
    this.assignGroups = [];
    this.selectedGroupKeys.clear();
    if (this.clientPlantId != null) this.loadGroupsForClient(this.clientPlantId);
  }

  private loadGroupsForClient(plantId: number): void {
    this.groupsLoading = true;
    this.groupsError = '';
    forkJoin([
      this.groupService.retrieveActiveGroupPlant(plantId).pipe(
        map((gs) => gs.map((g) => ({ id: g.groupId!, name: g.groupName, type: 'USER_GROUP' as const }))),
        catchError(() => of([] as AssignGroup[]))
      ),
      this.groupService.retrieveActiveTempGroupPlant(plantId).pipe(
        map((gs) => gs.map((g) => ({ id: g.tempUserGroupId!, name: g.groupName ?? '', type: 'TEMP_USER_GROUP' as const }))),
        catchError(() => of([] as AssignGroup[]))
      ),
    ]).subscribe({
      next: ([userGroups, tempGroups]) => {
        this.assignGroups = [...userGroups, ...tempGroups];
        // Pre-select the groups already assigned for this account-holder/client pair,
        // so the user sees what's assigned and can add or remove from here.
        const existing = this.existingAssignments.find(
          (a) =>
            a.accountHolderCompanyId === this.accountHolderId &&
            a.clientPlantId === plantId
        );
        const available = new Set(this.assignGroups.map((g) => this.groupKey(g)));
        const savedKeys = (existing?.groups || []).map(
          (g) => `${g.groupType}:${g.groupId}`
        );
        this.selectedGroupKeys = new Set(savedKeys.filter((k) => available.has(k)));

        // Hydrate each saved group's schedule so the editor shows what's already set.
        for (const g of existing?.groups || []) {
          const key = `${g.groupType}:${g.groupId}`;
          if (!available.has(key)) continue;
          this.hydrateSchedule(key, g.schedule ?? null);
        }
        this.groupsLoading = false;

        // Default the schedule group so the model is set (a native select won't fire change for its first option).
        if (this.scheduleModalOpen && !this.activeGroup && this.assignGroups.length) {
          this.activeGroup = this.assignGroups[0];
          this.onScheduleGroupChange();
        }
      },
      error: () => {
        this.groupsError = 'Unable to load groups.';
        this.groupsLoading = false;
      },
    });
  }

  openGroupDropdown(): void {
    this.groupDropdownOpen = true;
  }

  groupKey(g: AssignGroup): string {
    return `${g.type}:${g.id}`;
  }

  get filteredGroups(): AssignGroup[] {
    const q = this.groupSearch.trim().toLowerCase();
    if (!q) return this.assignGroups;
    return this.assignGroups.filter((g) => g.name.toLowerCase().includes(q));
  }

  get selectedGroups(): AssignGroup[] {
    return this.assignGroups.filter((g) => this.selectedGroupKeys.has(this.groupKey(g)));
  }

  isGroupSelected(g: AssignGroup): boolean {
    return this.selectedGroupKeys.has(this.groupKey(g));
  }
  toggleGroup(g: AssignGroup): void {
    const k = this.groupKey(g);
    if (this.selectedGroupKeys.has(k)) this.selectedGroupKeys.delete(k);
    else this.selectedGroupKeys.add(k);
    // Close the dropdown on selection; it reopens when the input is focused.
    this.groupDropdownOpen = false;
    this.groupSearch = '';
  }
  removeGroup(g: AssignGroup, event?: Event): void {
    event?.stopPropagation();
    this.selectedGroupKeys.delete(this.groupKey(g));
  }

  // ---- Per-group scheduling (each initiator starts their own when the schedule is met) ----
  scheduleFrequencyUnits = ['Hour(s)', 'Day(s)', 'Week(s)', 'Month(s)'];
  daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  private groupSchedules = new Map<string, GroupScheduleUi>();

  // Option lists in the shape app-dropdown expects (mirrors the form-schedule screen).
  frequencyUnitOptions = [
    { id: 1, unit: 'Hour(s)' },
    { id: 2, unit: 'Day(s)' },
    { id: 3, unit: 'Week(s)' },
    { id: 4, unit: 'Month(s)' },
  ];
  daysOfTheWeekOptions = this.daysOfWeek.map((day, i) => ({ id: i + 1, day }));
  firstOrLastOptions = [
    { id: 1, day: 'First' },
    { id: 2, day: 'Last' },
  ];
  specificDayOptions = Array.from({ length: 28 }, (_, i) => ({ id: i + 1, unit: String(i + 1) }));
  overdueUnitOptions = [
    { id: 1, unit: 'Minute(s)' },
    { id: 2, unit: 'Hour(s)' },
    { id: 3, unit: 'Day(s)' },
  ];

  // Schedule mode: one group at a time (account holder → client → group → schedule).
  activeGroup: AssignGroup | null = null;
  // Inline validation shown on Save (mirrors the form-schedule screen — button isn't disabled).
  scheduleErrors: { startDate: string; endDate: string; interval: string; days: string } = { startDate: '', endDate: '', interval: '', days: '' };
  get activeSchedule(): GroupScheduleUi | null {
    return this.activeGroup ? this.scheduleFor(this.activeGroup) : null;
  }
  onScheduleGroupChange(): void {
    // Selecting a group hydrates its saved schedule (or a fresh enabled one) into the editor.
    this.scheduleErrors = { startDate: '', endDate: '', interval: '', days: '' };
    if (!this.activeGroup) return;
    const key = this.groupKey(this.activeGroup);
    const existing = this.existingAssignments.find(
      (a) => a.accountHolderCompanyId === this.accountHolderId && a.clientPlantId === this.clientPlantId
    );
    const saved = (existing?.groups || []).find((g) => `${g.groupType}:${g.groupId}` === key);
    this.hydrateSchedule(key, saved?.schedule ?? null);
  }

  scheduleFor(g: AssignGroup): GroupScheduleUi {
    const k = this.groupKey(g);
    let s = this.groupSchedules.get(k);
    if (!s) {
      s = {
        // The Schedule modal is all about scheduling, so a fresh editor starts enabled + open.
        enabled: true,
        expanded: true,
        unit: 'Day(s)',
        interval: '1',
        days: [],
        monthMode: 'specific',
        specificDay: '1',
        firstOrLast: 'First',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        overdueInterval: '1',
        overdueUnit: 'Hour(s)',
      };
      this.groupSchedules.set(k, s);
    }
    return s;
  }

  /** Reverse of toSchedulePayload: load a saved schedule into the editor (key = groupType:groupId). */
  private hydrateSchedule(key: string, schedule: WorkflowGroupSchedule | null): void {
    const [startDate, startTime] = (schedule?.startTime || '').split('T');
    const [endDate, endTime] = (schedule?.endTime || '').split('T');
    const on = schedule ? !!schedule.isScheduled : true;
    // owl's timer wants a Date; rebuild one from the saved "HH:mm" so it re-displays.
    const toTime = (hm: string): Date | '' => {
      const m = /^(\d{1,2}):(\d{2})/.exec(hm || '');
      if (!m) return '';
      const d = new Date();
      d.setHours(+m[1], +m[2], 0, 0);
      return d;
    };
    this.groupSchedules.set(key, {
      enabled: on,
      expanded: on,
      unit: schedule?.recurrenceFrequencyUnit || 'Day(s)',
      interval: schedule?.frequencyDay || '1',
      days: schedule?.selectedDaysOfTheWeek ? [...schedule.selectedDaysOfTheWeek] : [],
      monthMode: schedule?.recurrenceOnSpecificDay === false ? 'firstlast' : 'specific',
      specificDay: schedule?.specificDay || '1',
      firstOrLast: schedule?.firstOrLastDay || 'First',
      startDate: startDate || '',
      startTime: toTime((startTime || '').slice(0, 5)),
      endDate: endDate || '',
      endTime: toTime((endTime || '').slice(0, 5)),
      overdueInterval: schedule?.overdueInterval != null ? String(schedule.overdueInterval) : '1',
      overdueUnit: schedule?.overdueUnit || 'Hour(s)',
    });
  }

  /** A group's already-saved schedule for the current pair, so Assign re-saves don't wipe it. */
  private existingScheduleFor(g: AssignGroup): WorkflowGroupSchedule | undefined {
    const key = this.groupKey(g);
    const existing = this.existingAssignments.find(
      (a) => a.accountHolderCompanyId === this.accountHolderId && a.clientPlantId === this.clientPlantId
    );
    const saved = (existing?.groups || []).find((x) => `${x.groupType}:${x.groupId}` === key);
    return saved?.schedule ?? undefined;
  }

  private toSchedulePayload(s: GroupScheduleUi): WorkflowGroupSchedule {
    if (!s.enabled) return { isScheduled: false };
    // app-custom-date can emit various date strings — normalise to YYYY-MM-DD before combining.
    const isoDay = (d: string): string | null => {
      const dt = this.parseLocalDate(d);
      if (isNaN(dt.getTime())) return null;
      const p = (n: number) => String(n).padStart(2, '0');
      return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
    };
    // owl-date-time emits a Date; also tolerate a plain "HH:mm" string.
    const hhmm = (t: any): string => {
      if (t instanceof Date && !isNaN(t.getTime())) {
        const p = (n: number) => String(n).padStart(2, '0');
        return `${p(t.getHours())}:${p(t.getMinutes())}`;
      }
      const dt = new Date(t);
      if (!isNaN(dt.getTime())) {
        const p = (n: number) => String(n).padStart(2, '0');
        return `${p(dt.getHours())}:${p(dt.getMinutes())}`;
      }
      return typeof t === 'string' && /^\d{1,2}:\d{2}/.test(t) ? t.slice(0, 5) : '00:00';
    };
    const combine = (d: string, t: any) => {
      const day = isoDay(d);
      return day ? `${day}T${hhmm(t)}:00` : null;
    };
    return {
      isScheduled: true,
      recurrenceFrequencyUnit: s.unit,
      frequencyDay: s.interval,
      selectedDaysOfTheWeek: s.unit === 'Week(s)' ? s.days : null,
      specificDay: s.unit === 'Month(s)' && s.monthMode === 'specific' ? s.specificDay : null,
      recurrenceOnSpecificDay: s.unit === 'Month(s)' ? s.monthMode === 'specific' : null,
      firstOrLastDay: s.unit === 'Month(s)' && s.monthMode === 'firstlast' ? s.firstOrLast : null,
      startTime: combine(s.startDate, s.startTime),
      endTime: combine(s.endDate, s.endTime),
      overdueInterval: s.overdueInterval && +s.overdueInterval > 0 ? +s.overdueInterval : null,
      overdueUnit: s.overdueInterval && +s.overdueInterval > 0 ? s.overdueUnit : null,
    };
  }

  get canAssign(): boolean {
    return (
      this.accountHolderId != null &&
      this.clientPlantId != null &&
      this.selectedGroupKeys.size > 0
    );
  }

  // ---- Schedule modal field handlers (mirror the form-schedule screen) ----
  /** Parse a date string as LOCAL — a bare "YYYY-MM-DD" is otherwise read as UTC and shifts a day. */
  private parseLocalDate(value: string): Date {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((value || '').trim());
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(value);
  }
  formatScheduleDate(item: string): string {
    if (!item) return '';
    const d = this.parseLocalDate(item);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-us', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  setScheduleStartDate(v: string): void { if (this.activeSchedule) this.activeSchedule.startDate = v; }
  setScheduleEndDate(v: string): void { if (this.activeSchedule) this.activeSchedule.endDate = v; }
  setScheduleFrequencyUnit(opt: any): void { if (this.activeSchedule) this.activeSchedule.unit = opt.unit; }
  setScheduleSpecificDay(opt: any): void { if (this.activeSchedule) this.activeSchedule.specificDay = opt.unit; }
  setScheduleFirstOrLast(opt: any): void { if (this.activeSchedule) this.activeSchedule.firstOrLast = opt.day; }
  setScheduleOverdueUnit(opt: any): void { if (this.activeSchedule) this.activeSchedule.overdueUnit = opt.unit; }
  toggleActiveScheduleDay(day: string): void {
    const s = this.activeSchedule;
    if (!s) return;
    const i = s.days.indexOf(day);
    if (i >= 0) s.days.splice(i, 1);
    else s.days.push(day);
  }

  renderScheduleSummary(): string {
    const s = this.activeSchedule;
    if (!s || !s.interval || +s.interval <= 0) return '';
    const start = s.startDate ? ` starting ${this.formatScheduleDate(s.startDate)}` : '';
    if (s.unit === 'Week(s)') {
      const order = this.daysOfWeek;
      const days = [...s.days].sort((a, b) => order.indexOf(a) - order.indexOf(b)).join(', ');
      return days ? `Occurs every ${s.interval} ${s.unit} on ${days}${start}` : '';
    }
    if (s.unit === 'Month(s)') {
      const on = s.monthMode === 'specific'
        ? `on day ${s.specificDay}`
        : `on the ${s.firstOrLast} day`;
      return `Occurs every ${s.interval} ${s.unit} ${on}${start}`;
    }
    return `Occurs every ${s.interval} ${s.unit}${start}`;
  }

  /** Blocked while saving, before a group is picked, or until the workflow itself is saved. */
  /** Only a published workflow can be scheduled (a draft can't run). */
  get isWorkflowSchedulable(): boolean {
    return this.workflowTemplateId != null && this.wf?.status === 'PUBLISHED';
  }
  get canSaveSchedule(): boolean {
    return !this.assignSaving && !!this.activeGroup && this.isWorkflowSchedulable;
  }

  /** Combine an app-custom-date string with an owl time value into a Date. */
  private toDateTime(dateStr: string, timeVal: any): Date | null {
    if (!dateStr) return null;
    const d = this.parseLocalDate(dateStr);
    if (isNaN(d.getTime())) return null;
    const t = timeVal instanceof Date ? timeVal : timeVal ? new Date(timeVal) : null;
    d.setHours(t && !isNaN(t.getTime()) ? t.getHours() : 0, t && !isNaN(t.getTime()) ? t.getMinutes() : 0, 0, 0);
    return d;
  }

  /** Validate the active schedule; populates scheduleErrors and returns true when valid. */
  private validateSchedule(): boolean {
    this.scheduleErrors = { startDate: '', endDate: '', interval: '', days: '' };
    const s = this.activeSchedule;
    if (!s || !s.enabled) return true; // disabling/clearing a schedule is always valid
    if (!s.startDate) this.scheduleErrors.startDate = 'Start date is required';
    if (!s.interval || +s.interval <= 0) this.scheduleErrors.interval = 'Enter how often it repeats';
    if (s.unit === 'Week(s)' && s.days.length === 0) this.scheduleErrors.days = 'Pick at least one day';

    const start = this.toDateTime(s.startDate, s.startTime);
    if (start && start.getTime() < Date.now()) {
      this.scheduleErrors.startDate = 'Start date and time cannot be in the past';
    }
    if (s.endDate) {
      const end = this.toDateTime(s.endDate, s.endTime);
      if (start && end && end.getTime() < start.getTime()) {
        this.scheduleErrors.endDate = 'End date and time must be after the start';
      }
    }
    return !this.scheduleErrors.startDate && !this.scheduleErrors.endDate
      && !this.scheduleErrors.interval && !this.scheduleErrors.days;
  }

  saveSchedule(): void {
    if (this.assignSaving || this.activeGroup == null) return;
    if (this.workflowTemplateId == null) {
      this.showToast('Save the workflow before scheduling it.', 'alert-triangle');
      return;
    }
    if (!this.validateSchedule()) return;
    this.assignSaving = true;
    const key = this.groupKey(this.activeGroup);
    const existing = this.existingAssignments.find(
      (a) => a.accountHolderCompanyId === this.accountHolderId && a.clientPlantId === this.clientPlantId
    );
    // Preserve the assignment's other groups; only update the selected group's schedule.
    const groups = (existing?.groups || []).map((g) => ({
      groupId: g.groupId,
      groupType: g.groupType,
      groupName: g.groupName,
      schedule: g.schedule,
    }));
    let target = groups.find((g) => `${g.groupType}:${g.groupId}` === key);
    if (!target) {
      target = { groupId: this.activeGroup.id, groupType: this.activeGroup.type, groupName: this.activeGroup.name, schedule: null };
      groups.push(target);
    }
    target.schedule = this.toSchedulePayload(this.scheduleFor(this.activeGroup));

    this.workflowService.createAssignment(this.workflowTemplateId, {
      accountHolderCompanyId: this.accountHolderId!,
      clientPlantId: this.clientPlantId!,
      groups,
    }).subscribe({
      next: () => {
        this.assignSaving = false;
        this.scheduleModalOpen = false;
        this.showToast('Schedule saved', 'check-circle-2');
      },
      error: (err) => {
        this.assignSaving = false;
        this.showToast(this.assignErrorMessage(err), 'alert-triangle');
      },
    });
  }

  assignWorkflow(): void {
    if (!this.canAssign || this.assignSaving) return;
    if (this.workflowTemplateId == null) {
      this.showToast('Save the workflow before assigning it.', 'alert-triangle');
      return;
    }
    this.assignSaving = true;
    const request: CreateWorkflowAssignmentRequest = {
      accountHolderCompanyId: this.accountHolderId!,
      clientPlantId: this.clientPlantId!,
      groups: this.selectedGroups.map((g) => ({
        groupId: g.id,
        groupType: g.type,
        groupName: g.name,
        // Assign never edits schedules — carry each group's saved one through untouched.
        schedule: this.existingScheduleFor(g),
      })),
    };
    const workflowId = this.workflowTemplateId;
    // The API upserts by account-holder/client pair: re-saving the same pair updates
    // that assignment in place (preserving its role→user records), while a different
    // pair creates a new one — so multiple account holder groups coexist safely.
    this.workflowService.createAssignment(workflowId, request).subscribe({
      next: () => {
        this.assignSaving = false;
        this.workflowAssigned = true;
        this.assignModalOpen = false;
        this.groupDropdownOpen = false;
        this.showToast('Workflow Assigned', 'check-circle-2');
      },
      error: (err) => {
        this.assignSaving = false;
        console.error('Assign workflow failed', err);
        this.showToast(this.assignErrorMessage(err), 'alert-triangle');
      },
    });
  }

  /** Pull the most useful message out of an HTTP error (our 400s, ProblemDetails, or status). */
  private assignErrorMessage(err: any): string {
    if (typeof err?.error === 'string' && err.error.trim()) return err.error.trim();
    const body = err?.error;
    const msg = body?.message || body?.detail || body?.title;
    if (msg) return msg;
    if (err?.status === 401 || err?.status === 403) return 'You are not authorized to assign this workflow.';
    if (err?.status === 0) return 'Could not reach the server. Check your connection.';
    return err?.status ? `Unable to assign workflow (HTTP ${err.status}).` : 'Unable to assign workflow.';
  }
  perItemRecipientFields(): Field[] {
    return this.allFieldsInStage(this.activeStage);
  }
  /** source fields for conditional routing come from earlier stages */
  earlierStageFields(): Field[] {
    const out: Field[] = [];
    for (let i = 1; i < this.wf.activeStage; i++) out.push(...this.allFieldsInStage(this.wf.stages[i]));
    return out;
  }
  toggleEmailRecipient(role: string): void {
    const list = this.wf.finalOutputs.email.recipients;
    const i = list.indexOf(role);
    if (i >= 0) list.splice(i, 1);
    else list.push(role);
    this.markDirty();
  }
  get isFinalStage(): boolean {
    return this.wf.activeStage === this.wf.stageCount;
  }

  /* ============================================================
     Validation engine
     ============================================================ */
  runValidation(): ValidationResult {
    const wf = this.wf;
    const rules: ValidationRule[] = [];
    const goto = (i: number, routing = false) => () => {
      this.wf.activeStage = i;
      if (routing) this.openRouting();
    };

    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      rules.push({
        stage: i,
        label: `${this.stageDisplayName(i)} has a name`,
        pass: (s.name || '').trim().length >= 3,
        failMessage: `Stage name is required (minimum 3 characters)`,
        target: goto(i, true),
      });
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      rules.push({
        stage: i,
        label: `Stage ${i} name is unique`,
        pass: !this.isStageNameDuplicate(i),
        failMessage: `A stage with this name already exists`,
        target: goto(i, true),
      });
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      rules.push({
        stage: i,
        label: `${this.stageDisplayName(i)} has a valid time budget`,
        pass: !this.isTimeBudgetInvalid(s.routing.timeBudget.value),
        failMessage: `Time budget must be a positive number`,
        target: goto(i, true),
      });
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      rules.push({
        stage: i,
        label: `${this.stageDisplayName(i)} has at least one actor role assigned`,
        pass: s.routing.actorRoles.length > 0,
        failMessage: `${this.stageDisplayName(i)} has no actor assigned`,
        target: goto(i, true),
      });
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      const dead = s.routing.actorRoles.filter((role) => this.isRoleDeactivated(role));
      rules.push({
        stage: i,
        label: `${this.stageDisplayName(i)} actor roles are all active`,
        pass: dead.length === 0,
        failMessage: `${this.stageDisplayName(i)} references a deactivated role (${dead.join(', ')}) — replace it before publishing`,
        target: goto(i, true),
      });
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      rules.push({
        stage: i,
        label: `${this.stageDisplayName(i)} has at least one form field`,
        pass: s.rows.flat().filter(Boolean).length > 0,
        failMessage: `${this.stageDisplayName(i)} has no form fields — add at least one field from the palette`,
        target: goto(i),
      });
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      rules.push({
        stage: i,
        label: `Stage ${i} has at least one allowed action`,
        pass: s.actions.length > 0,
        failMessage: `At least one action must be allowed at each stage`,
        target: goto(i),
      });
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      if (s.actions.some((a) => a.type === 'escalate')) {
        rules.push({
          stage: i,
          label: `Stage ${i} escalation target is configured`,
          pass: !!s.routing.escalationTarget,
          failMessage: `Escalation target is required when Escalate is enabled`,
          target: goto(i, true),
        });
      }
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      if (!this.stageHasApprove(s)) continue;
      const t = s.routing.approvalTarget;
      const validNext = i < wf.stageCount;
      const validStage = t.type === 'stage' && t.stage != null && t.stage > i && t.stage <= wf.stageCount;
      const validClose = t.type === 'close' || t.type === 'output';
      let pass = false;
      if (t.type === 'next') pass = validNext;
      else if (t.type === 'stage') pass = validStage;
      else pass = validClose;
      rules.push({
        stage: i,
        label: `${this.stageDisplayName(i)} approval path is set`,
        pass,
        failMessage: `Approval path is required for stages with Approve action`,
        target: goto(i, true),
      });
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      if (!this.stageHasReject(s)) continue;
      const t = s.routing.rejectionTarget;
      const pass = t.type === 'close' || (t.type === 'previous' && i > 1);
      rules.push({
        stage: i,
        label: `${this.stageDisplayName(i)} rejection path is set`,
        pass,
        failMessage:
          i === 1 && t.type === 'previous'
            ? `Stage 1 cannot reject to a previous stage. Choose Close as Rejected instead.`
            : `Stage ${i} is missing a rejection target`,
        target: goto(i, true),
      });
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      if (s.routing.notifications.escalation) {
        rules.push({
          stage: i,
          label: `Stage ${i} auto-escalation has a target`,
          pass: !!s.routing.escalationTarget,
          failMessage: `Auto-escalation requires an escalation target`,
          target: goto(i, true),
        });
      }
    }
    for (let i = 2; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      const orphans = s.rows
        .flat()
        .filter(
          (e) =>
            (e as MirrorEntry).kind === 'mirror' &&
            !this.findStage1FieldById((e as MirrorEntry).refId)
        );
      rules.push({
        stage: i,
        label: `Stage ${i} mirror references all resolve to live Stage 1 fields`,
        pass: orphans.length === 0,
        failMessage: `Stage ${i} has ${orphans.length} orphaned mirror reference(s)`,
        target: goto(i),
      });
    }
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      const fields = this.allFieldsInStage(s);
      const fieldIds = new Set(fields.map((f) => f.id));
      let broken = 0;
      fields.forEach((f) => {
        if (f.visibility && f.visibility.enabled) {
          f.visibility.conditions.forEach((c) => {
            if (c.fieldId && !fieldIds.has(c.fieldId)) broken++;
          });
        }
      });
      if (broken > 0 || fields.some((f) => f.visibility && f.visibility.enabled)) {
        rules.push({
          stage: i,
          label: `Stage ${i} conditional visibility rules reference live fields`,
          pass: broken === 0,
          failMessage: `Stage ${i} has ${broken} broken conditional visibility reference(s)`,
          target: goto(i),
        });
      }
    }
    const finalIdx = wf.stageCount;
    const finalStage = wf.stages[finalIdx];
    if (finalStage.actions.some((a) => a.type === 'approve' || a.type === 'accept')) {
      const outOk = wf.finalOutputs.pdf.enabled || wf.finalOutputs.email.enabled;
      rules.push({
        stage: finalIdx,
        label: 'Final stage produces at least one output (PDF or email)',
        pass: outOk,
        failMessage: 'Final stage approval requires at least one output (PDF or email)',
        target: goto(finalIdx, true),
      });
    }
    if (wf.finalOutputs.email.enabled) {
      rules.push({
        stage: finalIdx,
        label: 'Email output has at least one recipient role',
        pass: wf.finalOutputs.email.recipients.length > 0,
        failMessage: 'Email recipient is required when Send Email is enabled',
        target: goto(finalIdx, true),
      });
    }

    const reachable = this.reachableStages();
    for (let i = 2; i <= wf.stageCount; i++) {
      rules.push({
        stage: i,
        label: `${this.stageDisplayName(i)} is reachable from Stage 1`,
        pass: reachable.has(i),
        failMessage: `Stage ${i} approval path leads to an unreachable target`,
        target: goto(i, true),
      });
    }
    rules.push({
      stage: finalIdx,
      label: 'Workflow has at least one path to completion',
      pass: this.hasClosingPath(reachable),
      failMessage: 'No stage has a Close-as-Approved / Close-as-Rejected outcome — workflow cannot terminate',
      target: goto(finalIdx, true),
    });
    for (let i = 1; i <= wf.stageCount; i++) {
      const s = wf.stages[i];
      const dateFieldIdsInStage = new Set(
        this.allFieldsInStage(s)
          .filter((ff) => ff.type === 'date' || ff.type === 'daterange')
          .map((ff) => ff.id)
      );
      this.allFieldsInStage(s).forEach((f) => {
        if (f.type === 'duration') {
          const cfg = f.durationCfg;
          const hasBoth = !!(cfg && cfg.startFieldId && cfg.endFieldId);
          // Selecting both isn't enough — if the referenced Start/End Date field was since
          // deleted and recreated (new id) the reference silently stops resolving and the
          // Duration will always compute empty at runtime with no visible error otherwise.
          const resolves = hasBoth && dateFieldIdsInStage.has(cfg!.startFieldId) && dateFieldIdsInStage.has(cfg!.endFieldId);
          rules.push({
            stage: i,
            label: `Duration field "${f.label}" has Start and End sources`,
            pass: resolves,
            failMessage: !hasBoth
              ? `Duration field "${f.label}" requires both Start and End date sources`
              : `Duration field "${f.label}" references a Start/End date field that no longer exists in this stage — it will always be blank`,
            target: () => {
              this.wf.activeStage = i;
              this.openFieldConfig(f);
            },
          });
        }
      });
    }

    const passed = rules.every((r) => r.pass);
    this.wf.lastValidation = { passed, ts: this.nowISO(), rules };
    this.recordValidationResult(passed);
    return this.wf.lastValidation;
  }

  async validateAndOpen(): Promise<void> {
    // AC-06: validation relies on backend reference data (role registry,
    // recipient resolution). Probe reachability first; if the service is
    // unreachable we cannot validate, so block and prompt a retry. A successful
    // response — even an "unhealthy" one — means the service is reachable.
    try {
      await firstValueFrom(this.workflowService.notificationTemplatesHealth());
    } catch {
      this.validationServiceDown = true;
      this.wf.lastValidation = { passed: false, ts: this.nowISO(), rules: [] };
      this.recordValidationResult(false);
      this.showToast('Validation service is temporarily unavailable. Please try again.', 'alert-triangle');
      return;
    }
    this.validationServiceDown = false;
    this.runValidation();
    this.validationPanelOpen = true;
  }
  closeValidation(): void {
    this.validationPanelOpen = false;
  }
  get failedRuleCount(): number {
    return this.wf.lastValidation ? this.wf.lastValidation.rules.filter((r) => !r.pass).length : 0;
  }
  gotoRule(rule: ValidationRule): void {
    this.closeValidation();
    rule.target && rule.target();
  }

  /* ============================================================
     Save / Publish / Edit / Deactivate
     ============================================================ */
  async saveDraft(skipRecipientCheck = false): Promise<void> {
    if (this.saving) return;
    if (!navigator.onLine) {
      await this.flushRecoveryCopy();
      this.showToast('You’re offline. You can keep editing, but your changes have not been saved yet.', 'alert-triangle');
      return;
    }

    this.trimStageNames();
    this.syncEscalationFlags();
    const invalidStage = this.firstInvalidStageName();
    if (invalidStage !== null) {
      this.wf.activeStage = invalidStage;
      this.openRouting();
      this.showToast('Each stage needs a name of at least 3 characters.', 'alert-triangle');
      return;
    }

    // Hard block: an email notification with no recipient role is invalid and can never be saved
    // (even via "Save Anyway"). Fix the rule first.
    if (this.hasIncompleteEmailRule()) {
      this.showToast('Email notifications need a recipient role before saving.', 'alert-triangle');
      return;
    }

    if (this.hasIncompleteEscalation()) {
      this.showToast('Escalation target is required when Escalate is enabled.', 'alert-triangle');
      return;
    }

    // Warn before saving email notifications whose recipient role has no users.
    if (!skipRecipientCheck) {
      const unresolved = await this.findUnresolvableEmailRoles();
      if (unresolved.length > 0) {
        this.showConfirm({
          title: 'Email recipients cannot be resolved — review configuration',
          body: `No users are assigned to: ${unresolved.join(', ')}. Save anyway?`,
          okLabel: 'Save Anyway',
          okClass: 'primary',
          onConfirm: () => void this.saveDraft(true),
        });
        return;
      }
    }

    this.saving = true;
    try {
      const response = this.workflowTemplateId
        ? await firstValueFrom(this.workflowService.saveDraft(this.workflowTemplateId, this.wf, this.concurrencyToken))
        : await firstValueFrom(this.workflowService.create(this.wf));
      const savedRecoveryIdentity = this.recoveryIdentity();
      const savedRecoveryKey = this.workflowRecovery.buildKey(
        savedRecoveryIdentity.userId,
        savedRecoveryIdentity.tenantId,
        savedRecoveryIdentity.workflowId,
        savedRecoveryIdentity.baselineVersion
      );
      this.applyWorkflowResponse(response);
      this.recoveryStatus = 'none';
      this.recoveryRecord = null;
      void this.workflowRecovery.remove(savedRecoveryKey).catch(() => undefined);
      this.showToast('Draft saved.');
    } catch (err) {
      if (this.isConflict(err)) {
        this.handleConflict('Draft was not saved — workflow was modified by another user.');
      } else if (this.isNetworkFailure(err)) {
        this.showToast(
          'Your changes could not be saved. Check your connection and try again.',
          'alert-triangle',
          { label: 'Retry', run: () => this.saveDraft() }
        );
      } else {
        this.showToast(
          this.extractApiError(err, 'Draft was not saved.'),
          'alert-triangle',
          { label: 'Retry', run: () => this.saveDraft() }
        );
      }
    } finally {
      this.saving = false;
    }
  }

  /** True when any stage has Escalate enabled but no escalation target selected. */
  private hasIncompleteEscalation(): boolean {
    for (let i = 1; i <= this.wf.stageCount; i++) {
      const s = this.wf.stages[i];
      if (s && this.stageHasEscalate(s) && !s.routing.escalationTarget) return true;
    }
    return false;
  }

  /** True when any stage has an email notification with no recipient role selected. */
  private hasIncompleteEmailRule(): boolean {
    for (let i = 1; i <= this.wf.stageCount; i++) {
      const rules = this.wf.stages[i]?.routing?.notifications?.rules ?? [];
      if (rules.some((rule) => rule.channel === 'email' && !rule.recipientRole)) return true;
    }
    return false;
  }

  /** Returns recipient roles used by email notifications that resolve to no users. */
  private async findUnresolvableEmailRoles(): Promise<string[]> {
    const roles = new Set<string>();
    for (let i = 1; i <= this.wf.stageCount; i++) {
      const rules = this.wf.stages[i]?.routing?.notifications?.rules ?? [];
      for (const rule of rules) {
        if (rule.channel === 'email' && rule.recipientRole) roles.add(rule.recipientRole);
      }
    }

    const unresolved: string[] = [];
    for (const role of roles) {
      try {
        const res = await firstValueFrom(this.workflowService.previewRecipients(role));
        if (!res?.resolvable) unresolved.push(role);
      } catch {
        // If the preview can't be reached, don't block the save on it.
      }
    }
    return unresolved;
  }

  private isNetworkFailure(err: any): boolean {
    return err?.status === 0;
  }

  canDeactivateWorkflowBuilder(): boolean | Promise<boolean> {
    if (!this.hasUnsavedChanges()) return true;
    if (this.pendingLeave) return this.pendingLeave.promise;

    this.leaveChangeCount = this.unsavedChanges().length;
    this.leaveModalOpen = true;

    let resolvePending!: (leave: boolean) => void;
    const promise = new Promise<boolean>((resolve) => (resolvePending = resolve));
    this.pendingLeave = { promise, resolve: resolvePending };

    // Browser popstate can invoke the guard without scheduling a view refresh.
    // Render the choice immediately so Back never appears to do nothing.
    this.changeDetector.detectChanges();
    return promise;
  }

  private resolveLeave(leave: boolean): void {
    this.leaveModalOpen = false;
    const pending = this.pendingLeave;
    this.pendingLeave = null;
    pending?.resolve(leave);
  }

  leaveStay(): void {
    this.resolveLeave(false);
  }

  leaveWithoutSaving(): void {
    void this.clearRecoveryCopy();
    this.resolveLeave(true);
  }

  reviewFromLeaveModal(): void {
    this.resolveLeave(false);
    this.openReview();
  }

  discardFromLeaveModal(): void {
    void this.clearRecoveryCopy();
    this.resolveLeave(true);
  }

  @HostListener('window:offline')
  onOffline(): void {
    this.isOffline = true;
    if (this.wf?.dirty) void this.flushRecoveryCopy();
  }

  @HostListener('window:online')
  onOnline(): void {
    this.isOffline = false;
    if (this.wf?.dirty) this.showToast('You’re back online. Save your draft to keep these changes.', 'check-circle-2');
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') void this.flushRecoveryCopy();
  }

  @HostListener('window:pagehide')
  onPageHide(): void {
    if (this.hasUnsavedChanges()) void this.clearRecoveryCopy();
  }

  /**
   * Browser-controlled reload/close protection. Modern browsers intentionally
   * ignore custom text and display their own Reload/Cancel confirmation.
   * IndexedDB recovery remains the fallback if the user proceeds.
   */
  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges()) return;
    // If the user confirms Reload/Leave, do not restore this abandoned copy on
    // the next visit. Cancel keeps the current in-memory editor untouched.
    void this.clearRecoveryCopy();
    event.preventDefault();
    event.returnValue = '';
  }

  private isConflict(err: any): boolean {
    return err?.status === 409;
  }

  private handleConflict(reason: string): void {
    this.showToast(reason, 'alert-triangle', {
      label: 'Refresh',
      run: () => this.refreshFromServer(),
    });
  }

  private async refreshFromServer(): Promise<void> {
    if (!this.workflowTemplateId) return;
    try {
      const response = await firstValueFrom(this.workflowService.getById(this.workflowTemplateId));
      this.applyWorkflowResponse(response);
      this.showToast('Latest version loaded.');
    } catch (err) {
      this.showToast(this.extractApiError(err, 'Could not refresh workflow.'), 'alert-triangle');
    }
  }

  private extractApiError(err: any, fallback: string): string {
    const body = err?.error;
    if (typeof body === 'string' && body.trim()) return body;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (body?.errors && typeof body.errors === 'object') {
      const firstKey = Object.keys(body.errors)[0];
      const arr = firstKey ? body.errors[firstKey] : null;
      if (Array.isArray(arr) && arr.length) return String(arr[0]);
    }
    if (err?.status === 401 || err?.status === 403) return 'Not authorised. You need Super Admin to do this.';
    if (err?.status === 0) return 'Could not reach the server.';
    return fallback;
  }

  confirmPublish(): void {
    if (this.hasUnsavedChanges()) {
      this.showToast('Save & re-validate first.', 'alert-triangle');
      return;
    }
    if (this.validationServiceDown || !this.lastValidationPassed) {
      this.showToast('Run Validate first.', 'alert-triangle');
      return;
    }
    this.showConfirm({
      title: `Publish "${this.wf.name}"?`,
      body: `This will set status from DRAFT to PUBLISHED (v${this.wf.version}) and make the template available for tenant configuration.`,
      okLabel: 'Publish',
      okClass: 'success',
      onConfirm: () => this.doPublish(),
    });
  }
  private async doPublish(): Promise<void> {
    if (this.saving) return;
    if (!this.workflowTemplateId) {
      await this.saveDraft();
      if (!this.workflowTemplateId) return;
    }

    const before = { status: this.wf.status, version: this.wf.version };
    this.wf.status = 'PUBLISHED';
    this.wf.inFlightCount = Math.max(this.wf.inFlightCount, 3);
    this.saving = true;
    try {
      const response = await firstValueFrom(this.workflowService.publish(this.workflowTemplateId, this.wf, this.concurrencyToken));
      this.applyWorkflowResponse(response);
      this.showToast(`Published as v${this.wf.version}.`);
    } catch (err) {
      this.wf.status = before.status as any;
      if (this.isConflict(err)) {
        this.handleConflict('Publish blocked — workflow was modified by another user.');
      } else {
        this.showToast(this.extractApiError(err, 'Workflow was not published.'), 'alert-triangle');
      }
    } finally {
      this.saving = false;
    }
  }

  editActive(): void {
    this.showConfirm({
      title: 'Create a new draft revision?',
      body: `This will create draft v${this.wf.version + 1}. The current PUBLISHED v${this.wf.version} remains available until you publish the new version.`,
      okLabel: 'Create draft',
      okClass: 'primary',
      onConfirm: () => {
        this.wf.version += 1;
        this.wf.status = 'DRAFT';
        this.lastValidationPassed = false;
        this.showToast(`Editing draft v${this.wf.version}.`);
      },
    });
  }

  confirmDeactivate(): void {
    this.deactivateReason = '';
    this.confirm = {
      open: true,
      title: `Deactivate "${this.wf.name}"?`,
      body: 'No new items can be submitted against this workflow after deactivation. This can be reversed only by creating a new version.',
      okLabel: 'Deactivate',
      okClass: 'danger',
      kind: 'deactivate',
      inFlight: this.wf.inFlightCount,
      details: [],
      onConfirm: async () => {
        if (!this.workflowTemplateId || this.saving) return;
        const reason = this.deactivateReason.trim() || undefined;
        const before = { status: this.wf.status };
        this.wf.status = 'INACTIVE';
        this.saving = true;
        try {
          const response = await firstValueFrom(
            // RT-30: confirming this modal — which already surfaces the in-flight warning — is the
            // admin's acknowledgement, so proceed past the API's in-flight guard.
            this.workflowService.deactivate(this.workflowTemplateId, reason, this.concurrencyToken, true)
          );
          this.applyWorkflowResponse(response);
          this.showToast('Workflow deactivated.');
        } catch (err) {
          this.wf.status = before.status as any;
          if (this.isConflict(err)) {
            this.handleConflict('Deactivate blocked — workflow was modified by another user.');
          } else {
            this.showToast(this.extractApiError(err, 'Workflow was not deactivated.'), 'alert-triangle');
          }
        } finally {
          this.saving = false;
        }
      },
    };
    this.overflowMenuOpen = false;
  }

  /* ============================================================
     History / Audit log
     ============================================================ */
  openHistory(): void {
    if (!this.canViewAuditLog) {
      this.overflowMenuOpen = false;
      return;
    }

    this.clearHistFilters(false);
    this.historyPanelOpen = true;
    this.overflowMenuOpen = false;
    void this.loadAuditLog();
  }

  async loadAuditLog(): Promise<void> {
    if (!this.workflowTemplateId || !this.canViewAuditLog) {
      this.serverAuditLog = [];
      this.auditLoadError = '';
      return;
    }
    this.auditLoading = true;
    this.auditLoadError = '';
    try {
      const filters = this.auditLogFilters();
      const request = filters
        ? this.workflowService.getAuditLog(this.workflowTemplateId, filters)
        : this.workflowService.getAuditLog(this.workflowTemplateId);
      this.serverAuditLog = await firstValueFrom(request);
      this.syncHistoryUserOptions();
    } catch (err) {
      this.auditLoadError = this.extractApiError(err, 'Could not load audit log.');
      this.serverAuditLog = [];
    } finally {
      this.auditLoading = false;
    }
  }
  closeHistory(): void {
    this.historyPanelOpen = false;
  }
  setHistFilter(f: 'ALL' | AuditAction): void {
    this.histFilter = f;
    void this.loadAuditLog();
  }

  onHistoryFilterChanged(): void {
    void this.loadAuditLog();
  }

  get historyEntries(): AuditEntry[] {
    const from = this.histFrom ? new Date(this.histFrom + 'T00:00:00').getTime() : null;
    const to = this.histTo ? new Date(this.histTo + 'T23:59:59.999').getTime() : null;
    return this.serverAuditLog
      .filter((e) => {
        if (this.histFilter !== 'ALL' && e.action !== this.histFilter) return false;
        if (this.histUser && (e.userEmail || '') !== this.histUser) return false;
        const ts = new Date(e.timestampUtc).getTime();
        if (from != null && ts < from) return false;
        if (to != null && ts > to) return false;
        return true;
      })
      .map((e) => this.toAuditEntry(e));
  }

  get canViewAuditLog(): boolean {
    if (localStorage.getItem('role') === 'Super Admin') return true;

    return this.storedPermissions().some((permission) =>
      this.normalizedPermission(permission) === this.normalizedPermission(this.viewAuditLogPermission)
    );
  }

  get historyUsers(): string[] {
    if (this.historyUserOptions.length === 0) {
      return Array.from(new Set(this.serverAuditLog.map((e) => e.userEmail || '').filter((u) => !!u))).sort();
    }

    return this.historyUserOptions;
  }

  private toAuditEntry(e: WorkflowAuditLogEntry): AuditEntry {
    return {
      id: e.auditLogId,
      action: e.action as AuditAction,
      entityType: e.entityType,
      entityId: e.entityId,
      userId: e.userEmail || (e.userId != null ? `user#${e.userId}` : 'system'),
      ts: e.timestampUtc,
      before: e.beforeSnapshot,
      after: e.afterSnapshot,
      reason: e.reason,
      sessionId: e.sessionId || '',
      ip: e.ipAddress || '',
    };
  }

  clearHistFilters(reload = true): void {
    this.histFilter = 'ALL';
    this.histUser = '';
    this.histFrom = '';
    this.histTo = '';
    if (reload) void this.loadAuditLog();
  }

  private syncHistoryUserOptions(): void {
    const users = this.serverAuditLog.map((e) => e.userEmail || '').filter((user) => !!user);
    if (this.histUser) users.push(this.histUser);
    this.historyUserOptions = Array.from(new Set(users)).sort();
  }

  private auditLogFilters(): { action?: string; user?: string; from?: string; to?: string } | undefined {
    const filters: { action?: string; user?: string; from?: string; to?: string } = {};

    if (this.histFilter !== 'ALL') filters.action = this.histFilter;
    if (this.histUser) filters.user = this.histUser;
    if (this.histFrom) filters.from = `${this.histFrom}T00:00:00`;
    if (this.histTo) filters.to = `${this.histTo}T23:59:59.999`;

    return Object.keys(filters).length ? filters : undefined;
  }

  private storedPermissions(): string[] {
    const raw = localStorage.getItem('permissions') || localStorage.getItem('permission') || '';
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((value) => String(value));
      if (typeof parsed === 'string') return [parsed];
    } catch {
      // Fall through to delimiter parsing for simple storage formats.
    }

    return raw.split(/[;,]/).map((value) => value.trim()).filter((value) => !!value);
  }

  private normalizedPermission(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  diffJson(e: AuditEntry): string {
    return JSON.stringify({ before: e.before, after: e.after }, null, 2);
  }

  entityTypeLabel(t: string): string {
    return AUDIT_ENTITY_LABELS[t] || this.humanizePath(t);
  }

  hasAuditChanges(e: AuditEntry): boolean {
    return !(e.before == null && e.after == null);
  }

  auditSummary(e: AuditEntry): string {
    if (e.action === 'CREATE') return 'Workflow created';
    if (e.action === 'PUBLISH') {
      const v = e.after?.version;
      return v ? `Published v${v}` : 'Published';
    }
    if (e.action === 'DEACTIVATE') return 'Workflow deactivated';
    if (e.action === 'EXPORT_AUDIT_LOG') {
      const fmt = e.after?.format || 'CSV';
      const count = e.after?.count;
      return count != null ? `Exported ${count} ${count === 1 ? 'entry' : 'entries'} (${fmt})` : `Exported audit log (${fmt})`;
    }
    const changes = this.auditChanges(e);
    const real = changes.filter((c) => c.kind !== 'snapshot');
    if (real.length === 0 && changes.length === 0) return 'No changes recorded';
    if (real.length === 0) return 'State snapshot';
    if (real.length === 1) return real[0].label + (real[0].kind === 'changed' ? ' updated' : real[0].kind === 'added' ? ' added' : ' removed');
    return `${real.length} fields changed`;
  }

  auditChanges(entry: AuditEntry): AuditChange[] {
    if (entry.before == null && entry.after == null) return [];
    if (entry.before == null) return this.snapshotChanges(entry.after);
    if (entry.after == null) return this.snapshotChanges(entry.before, true);
    return this.diffObjects('', entry.before, entry.after);
  }

  private snapshotChanges(obj: any, removed = false): AuditChange[] {
    if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
      return [
        {
          path: '',
          label: 'Value',
          kind: removed ? 'removed' : 'snapshot',
          before: removed ? obj : undefined,
          after: removed ? undefined : obj,
        },
      ];
    }
    return Object.keys(obj).map((k) => ({
      path: k,
      label: this.auditPathLabel(k),
      kind: removed ? ('removed' as const) : ('snapshot' as const),
      before: removed ? obj[k] : undefined,
      after: removed ? undefined : obj[k],
    }));
  }

  private diffObjects(prefix: string, a: any, b: any, depth = 0): AuditChange[] {
    if (depth > 6) return this.emitChange(prefix, a, b);
    if (a === b) return [];
    const bothObjects =
      a != null && b != null && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b);
    if (!bothObjects) {
      if (this.deepEq(a, b)) return [];
      return this.emitChange(prefix, a, b);
    }
    const out: AuditChange[] = [];
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    keys.forEach((k) => {
      const path = prefix ? `${prefix}.${k}` : k;
      const av = a[k];
      const bv = b[k];
      if (!(k in a)) {
        out.push({ path, label: this.auditPathLabel(path), kind: 'added', before: undefined, after: bv });
      } else if (!(k in b)) {
        out.push({ path, label: this.auditPathLabel(path), kind: 'removed', before: av, after: undefined });
      } else if (k === 'rows' && Array.isArray(av) && Array.isArray(bv)) {
        out.push(...this.diffStageRows(path, av, bv));
      } else if (k === 'actions' && Array.isArray(av) && Array.isArray(bv)) {
        out.push(...this.diffStageActions(path, av, bv));
      } else if (av && bv && typeof av === 'object' && typeof bv === 'object' && !Array.isArray(av) && !Array.isArray(bv)) {
        out.push(...this.diffObjects(path, av, bv, depth + 1));
      } else if (!this.deepEq(av, bv)) {
        out.push({ path, label: this.auditPathLabel(path), kind: 'changed', before: av, after: bv });
      }
    });
    return out;
  }

  /**
   * Field-level diff for a stage's `rows` array so the audit reads
   * "Field "X" added / removed / label changed / required toggled" instead of a
   * single opaque "Form fields changed". Matches fields by id across before/after.
   */
  private diffStageRows(path: string, before: any[], after: any[]): AuditChange[] {
    const b = this.flattenRowsToFields(before);
    const a = this.flattenRowsToFields(after);
    const out: AuditChange[] = [];
    const keys = new Set<string>([...Array.from(b.keys()), ...Array.from(a.keys())]);
    keys.forEach((key) => {
      const bf = b.get(key);
      const af = a.get(key);
      const fieldPath = `${path}.${key}`;
      const stage = this.stageNumberFromPath(path);
      const target: ReviewTarget = {
        kind: 'field',
        stage,
        entityKey: key,
        beforeEntity: bf,
        afterEntity: af,
        beforePosition: this.findFieldPosition(before, key),
        afterPosition: this.findFieldPosition(after, key),
      };
      if (!bf) {
        out.push({
          path: fieldPath,
          label: this.fieldAuditLabel(af),
          kind: 'added',
          before: undefined,
          after: this.fieldSummary(af),
          target,
        });
      } else if (!af) {
        out.push({
          path: fieldPath,
          label: this.fieldAuditLabel(bf),
          kind: 'removed',
          before: this.fieldSummary(bf),
          after: undefined,
          target,
        });
      } else {
        const fieldChanges = this.diffObjects(fieldPath, bf, af);
        fieldChanges.forEach((change) => {
          change.target = target;
          const relativePath = change.path.startsWith(`${fieldPath}.`)
            ? change.path.slice(fieldPath.length + 1)
            : '';
          if (relativePath === 'label') change.label = `${this.fieldAuditLabel(af)} label`;
          else if (relativePath === 'required') change.label = `${this.fieldAuditLabel(af)} required`;
          else if (relativePath) change.label = `${this.fieldAuditLabel(af)} → ${this.humanizePath(relativePath)}`;
        });
        out.push(...fieldChanges);
      }
    });

    const beforeKeys = Array.from(b.keys()).sort();
    const afterKeys = Array.from(a.keys()).sort();
    const beforeLayout = this.rowLayout(before);
    const afterLayout = this.rowLayout(after);
    if (
      this.deepEq(beforeKeys, afterKeys) &&
      !this.deepEq(this.rowKeyLayout(before), this.rowKeyLayout(after))
    ) {
      out.push({
        path: `${path}.layout`,
        label: `${this.auditPathLabel(path)} layout`,
        kind: 'changed',
        before: beforeLayout,
        after: afterLayout,
        target: { kind: 'form', stage: this.stageNumberFromPath(path) },
      });
    }

    // Never let the specialized field view hide a structural difference it
    // does not yet understand (for example, a legacy row entry shape).
    if (out.length === 0 && !this.deepEq(before, after)) {
      out.push({
        path,
        label: this.auditPathLabel(path),
        kind: 'changed',
        before,
        after,
        target: { kind: 'form', stage: this.stageNumberFromPath(path) },
      });
    }
    return out;
  }

  private rowKeyLayout(rows: any[]): string[][] {
    return rows.map((row) =>
      (Array.isArray(row) ? row : []).map((entry) => {
        if (entry?.kind === 'mirror') return `mirror:${entry.refId ?? ''}`;
        if (entry?.kind === 'native') return `native:${entry.field?.id ?? ''}`;
        return `field:${entry?.id ?? ''}`;
      })
    );
  }

  private diffStageActions(path: string, before: any[], after: any[]): AuditChange[] {
    const beforeById = new Map(before.filter((action) => action?.id).map((action) => [action.id, action]));
    const afterById = new Map(after.filter((action) => action?.id).map((action) => [action.id, action]));
    const stage = this.stageNumberFromPath(path);
    const out: AuditChange[] = [];
    const ids = new Set<string>([
      ...Array.from(beforeById.keys()) as string[],
      ...Array.from(afterById.keys()) as string[],
    ]);

    ids.forEach((id) => {
      const beforeAction = beforeById.get(id);
      const afterAction = afterById.get(id);
      const actionPath = `${path}.action:${id}`;
      const target: ReviewTarget = {
        kind: 'action',
        stage,
        entityKey: `action:${id}`,
        beforeEntity: beforeAction,
        afterEntity: afterAction,
        beforePosition: beforeAction ? { row: 0, column: before.indexOf(beforeAction) } : undefined,
        afterPosition: afterAction ? { row: 0, column: after.indexOf(afterAction) } : undefined,
      };
      if (!beforeAction) {
        out.push({
          path: actionPath,
          label: `Action "${afterAction?.label || '(untitled)'}"`,
          kind: 'added',
          before: undefined,
          after: afterAction?.label,
          target,
        });
      } else if (!afterAction) {
        out.push({
          path: actionPath,
          label: `Action "${beforeAction?.label || '(untitled)'}"`,
          kind: 'removed',
          before: beforeAction?.label,
          after: undefined,
          target,
        });
      } else {
        const actionChanges = this.diffObjects(actionPath, beforeAction, afterAction);
        actionChanges.forEach((change) => {
          change.target = target;
          const relativePath = change.path.startsWith(`${actionPath}.`)
            ? change.path.slice(actionPath.length + 1)
            : '';
          change.label = relativePath
            ? `Action "${afterAction.label || '(untitled)'}" → ${this.humanizePath(relativePath)}`
            : `Action "${afterAction.label || '(untitled)'}"`;
        });
        out.push(...actionChanges);
      }
    });

    const beforeIds = before.map((action) => action?.id).filter(Boolean);
    const afterIds = after.map((action) => action?.id).filter(Boolean);
    if (this.deepEq([...beforeIds].sort(), [...afterIds].sort()) && !this.deepEq(beforeIds, afterIds)) {
      out.push({
        path: `${path}.layout`,
        label: `${this.auditPathLabel(path)} order`,
        kind: 'changed',
        // Include the stable identity so two same-label actions still produce
        // an unambiguous saved/draft order in the review drawer.
        before: before.map((action) => this.actionOrderLabel(action)),
        after: after.map((action) => this.actionOrderLabel(action)),
        target: { kind: 'actions', stage },
      });
    }

    if (out.length === 0 && !this.deepEq(before, after)) {
      out.push({
        path,
        label: this.auditPathLabel(path),
        kind: 'changed',
        before,
        after,
        target: { kind: 'actions', stage },
      });
    }
    return out;
  }

  private actionOrderLabel(action: any): string {
    const label = action?.label || '(untitled action)';
    const type = action?.type ? ` · ${this.humanizePath(action.type)}` : '';
    const id = action?.id ? ` [${action.id}]` : '';
    return `${label}${type}${id}`;
  }

  private stageNumberFromPath(path: string): number | undefined {
    const match = path.match(/^stages\.(\d+)/);
    return match ? Number(match[1]) : undefined;
  }

  private findFieldPosition(rows: any[], key: string): ReviewPosition | undefined {
    for (let row = 0; row < rows.length; row++) {
      const entries = Array.isArray(rows[row]) ? rows[row] : [];
      for (let column = 0; column < entries.length; column++) {
        const entry = entries[column];
        let entryKey = '';
        if (entry?.kind === 'mirror') entryKey = `mirror:${entry.refId}`;
        else if (entry?.kind === 'native') entryKey = `native:${entry.field?.id}`;
        else if (entry?.id) entryKey = `field:${entry.id}`;
        if (entryKey === key) return { row, column };
      }
    }
    return undefined;
  }

  private rowLayout(rows: any[]): string[] {
    return rows.map((row, index) => {
      const entries = (Array.isArray(row) ? row : []).map((entry) => {
        if (entry?.kind === 'mirror') return `Inherited ${entry.refId ?? '(unknown)'}`;
        if (entry?.kind === 'native') {
          const field = entry.field || {};
          return `${field.label || '(untitled field)'} [${field.id || 'unknown'}]`;
        }
        return `${entry?.label || '(untitled field)'} [${entry?.id || 'unknown'}]`;
      });
      return `Row ${index + 1}: ${entries.join(' | ') || '(empty)'}`;
    });
  }

  /** Flatten a stage's rows (Stage 1 fields, mirrors, native entries) into id -> field summary. */
  private flattenRowsToFields(rows: any): Map<string, any> {
    const map = new Map<string, any>();
    if (!Array.isArray(rows)) return map;
    for (const row of rows) {
      if (!Array.isArray(row)) continue;
      for (const e of row) {
        if (!e || typeof e !== 'object') continue;
        if (e.kind === 'mirror') {
          map.set('mirror:' + e.refId, { kind: 'mirror', refId: e.refId });
        } else if (e.kind === 'native') {
          const f = e.field || {};
          map.set('native:' + f.id, { kind: 'native', ...f });
        } else if (e.id) {
          map.set('field:' + e.id, { kind: 'field', ...e });
        }
      }
    }
    return map;
  }
  private fieldAuditLabel(f: any): string {
    if (!f) return 'Field';
    if (f.kind === 'mirror') return 'Inherited field';
    return `Field "${f.label || '(untitled)'}"`;
  }
  private fieldSummary(f: any): any {
    if (!f) return undefined;
    if (f.kind === 'mirror') return 'Inherited from Stage 1';
    return f.label || '(untitled)';
  }

  private emitChange(path: string, a: any, b: any): AuditChange[] {
    if (a === undefined) return [{ path, label: this.auditPathLabel(path), kind: 'added', before: undefined, after: b }];
    if (b === undefined) return [{ path, label: this.auditPathLabel(path), kind: 'removed', before: a, after: undefined }];
    return [{ path, label: this.auditPathLabel(path), kind: 'changed', before: a, after: b }];
  }

  private deepEq(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return a === b;
    if (typeof a !== typeof b) return false;
    if (typeof a !== 'object') return a === b;
    return JSON.stringify(this.sortObjectKeys(a)) === JSON.stringify(this.sortObjectKeys(b));
  }

  private auditPathLabel(path: string): string {
    if (!path) return 'Value';
    const stageMatch = path.match(/^stages\.(\d+)(?:\.(.+))?$/);
    if (stageMatch) {
      const stageIdx = stageMatch[1];
      const rest = stageMatch[2] || '';
      const tail = rest ? AUDIT_FIELD_LABELS[rest] || this.humanizePath(rest) : '';
      return tail ? `Stage ${stageIdx} → ${tail}` : `Stage ${stageIdx}`;
    }
    return AUDIT_FIELD_LABELS[path] || this.humanizePath(path);
  }

  private humanizePath(path: string): string {
    return path
      .split('.')
      .map((seg) =>
        seg
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .replace(/^./, (c) => c.toUpperCase())
          .trim()
      )
      .join(' → ');
  }

  formatReviewValue(value: any, depth = 0): string {
    if (value === undefined || value === null) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') return value === '' ? '(empty)' : value;
    if (typeof value === 'number') return String(value);
    if (depth > 3) return JSON.stringify(value);
    if (Array.isArray(value)) {
      if (value.length === 0) return '(none)';
      return value
        .map((item, index) => {
          const formatted = this.formatReviewValue(item, depth + 1);
          return item && typeof item === 'object' ? `${index + 1}. ${formatted}` : formatted;
        })
        .join(' · ');
    }
    if (typeof value === 'object') {
      return Object.keys(value)
        .map((key) => `${this.humanizePath(key)}: ${this.formatReviewValue(value[key], depth + 1)}`)
        .join('; ');
    }
    return String(value);
  }

  formatAuditValue(v: any): string {
    if (v === undefined || v === null) return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'string') return v === '' ? '(empty)' : v;
    if (typeof v === 'number') return String(v);
    if (Array.isArray(v)) {
      if (v.length === 0) return '(none)';
      const hasObjects = v.some((x) => x && typeof x === 'object');
      if (hasObjects) {
        const labels = v
          .map((x) => (x && typeof x === 'object' ? x.label || x.type || x.id || null : x))
          .filter((x) => x != null);
        if (labels.length === v.length) return labels.join(', ');
        return `[${v.length} ${v.length === 1 ? 'item' : 'items'}]`;
      }
      return v.map((x) => this.formatAuditValue(x)).join(', ');
    }
    if (typeof v === 'object') {
      const keys = Object.keys(v);
      if (keys.length === 0) return '{}';
      if (keys.length <= 3) return keys.map((k) => `${k}: ${this.formatAuditValue(v[k])}`).join('; ');
      return `{${keys.length} fields}`;
    }
    return String(v);
  }
  exportAudit(): void {
    const entries = this.historyEntries;
    if (entries.length === 0) {
      this.showToast('No audit entries to export.', 'alert-triangle');
      return;
    }

    const headers = [
      'Timestamp',
      'Action',
      'Entity Type',
      'Entity ID',
      'User ID',
      'Reason',
      'Before',
      'After',
    ];
    const rows = entries.map((e) => [
      e.ts,
      e.action,
      e.entityType,
      e.entityId,
      e.userId,
      e.reason || '',
      e.before == null ? '' : JSON.stringify(e.before),
      e.after == null ? '' : JSON.stringify(e.after),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => this.csvEscape(c)).join(',')).join('\r\n');

    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
    const suffix = this.histFilter === 'ALL' ? '' : '-' + this.histFilter.toLowerCase();
    link.href = url;
    link.download = `audit-${this.wf.sku || 'workflow'}${suffix}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    void this.recordExport('CSV', entries.length);
    this.showToast(`Exported ${entries.length} audit ${entries.length === 1 ? 'entry' : 'entries'} (CSV).`);
  }

  private async recordExport(format: 'CSV' | 'PDF', count: number): Promise<void> {
    if (!this.workflowTemplateId) return;
    try {
      await firstValueFrom(
        this.workflowService.recordAuditExport(this.workflowTemplateId, {
          format,
          count,
          filter: this.histFilter,
        })
      );
      await this.loadAuditLog();
    } catch {
      // Export still succeeded locally; server-side audit row failed silently.
    }
  }

  private csvEscape(value: string): string {
    const s = value == null ? '' : String(value);
    return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }

  exportAuditPdf(): void {
    const entries = this.historyEntries;
    if (entries.length === 0) {
      this.showToast('No audit entries to export.', 'alert-triangle');
      return;
    }

    const win = window.open('', '_blank');
    if (!win) {
      this.showToast('Pop-up blocked. Allow pop-ups to export PDF.', 'alert-triangle');
      return;
    }

    const esc = (s: any): string => {
      const str = s == null ? '' : String(s);
      return str.replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c)
      );
    };
    const rows = entries
      .map(
        (e) => `
        <tr>
          <td>${esc(new Date(e.ts).toLocaleString())}</td>
          <td><span class="act act-${e.action}">${esc(e.action)}</span></td>
          <td>${esc(e.entityType)}</td>
          <td>${esc(e.userId)}</td>
          <td>${esc(e.reason || '')}</td>
          <td><pre>${esc(JSON.stringify({ before: e.before, after: e.after }, null, 2))}</pre></td>
        </tr>`
      )
      .join('');

    const filterSummary: string[] = [];
    if (this.histFilter !== 'ALL') filterSummary.push(`Action: ${this.histFilter}`);
    if (this.histUser) filterSummary.push(`User: ${this.histUser}`);
    if (this.histFrom) filterSummary.push(`From: ${this.histFrom}`);
    if (this.histTo) filterSummary.push(`To: ${this.histTo}`);

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Audit log — ${esc(this.wf.sku)}</title>
      <style>
        body { font: 12px/1.4 -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; color: #111827; padding: 24px; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        .meta { color: #6b7280; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { text-align: left; vertical-align: top; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; }
        th { background: #f3f4f6; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
        pre { white-space: pre-wrap; word-break: break-word; margin: 0; font: 10px/1.35 'SFMono-Regular', Consolas, monospace; }
        .act { display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 600; font-size: 11px; }
        .act-CREATE { background: #dbeafe; color: #1e40af; }
        .act-UPDATE { background: #fef3c7; color: #92400e; }
        .act-PUBLISH { background: #dcfce7; color: #166534; }
        .act-DEACTIVATE { background: #fee2e2; color: #991b1b; }
        .act-EXPORT_AUDIT_LOG { background: #e5e7eb; color: #374151; }
        @media print { body { padding: 12px; } }
      </style></head><body>
        <h1>Audit log — ${esc(this.wf.name)} (${esc(this.wf.sku)})</h1>
        <div class="meta">${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}${filterSummary.length ? ' · ' + esc(filterSummary.join(' · ')) : ''} · Generated ${esc(new Date().toLocaleString())}</div>
        <table>
          <thead><tr><th>Timestamp</th><th>Action</th><th>Entity</th><th>User</th><th>Reason</th><th>Before / After</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <script>setTimeout(() => { window.focus(); window.print(); }, 250);</script>
      </body></html>`;

    // Load via a Blob URL (CSP-safe, no injection surface, no direct DOM string writes)
    win.location.href = URL.createObjectURL(new Blob([html], { type: 'text/html' }));

    void this.recordExport('PDF', entries.length);
    this.showToast(`PDF prepared for ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}.`);
  }

  /* ============================================================
     Load Example menu
     ============================================================ */
  private hasContent(): boolean {
    for (const k in this.wf.stages) if (this.wf.stages[k].rows.length > 0) return true;
    return false;
  }
  pickExample(which: 'leave' | 'security'): void {
    this.loadMenuOpen = false;
    if (this.hasUnsavedChanges() || this.hasContent()) {
      this.showConfirm({
        title: 'Replace current workflow?',
        body: 'Loading an example will replace your current workflow configuration. Continue?',
        okLabel: 'Load example',
        okClass: 'primary',
        onConfirm: () => this.loadExample(which),
      });
    } else {
      this.loadExample(which);
    }
  }
  private loadExample(which: 'leave' | 'security'): void {
    if (which === 'leave') this.loadLeaveExample();
    else this.loadSecurityExample();
  }

  private resetStages(count: number): void {
    this.wf.stageCount = count;
    this.wf.activeStage = 1;
    this.wf.stages = {};
    for (let i = 1; i <= count; i++) this.wf.stages[i] = blankStage(i);
  }

  private loadLeaveExample(): void {
    this.wf.name = 'SF-HR-004 Leave Workflow';
    this.wf.tier = 'Managed';
    this.resetStages(3);

    const s1 = this.wf.stages[1];
    const empName = Object.assign(makeField('dropdown'), {
      label: 'Employee Name',
      required: true,
      options: ['Alex Singh', 'Priya Sharma', 'Tom Lee', 'Mei Wong'],
    });
    const leaveTy = Object.assign(makeField('dropdown'), {
      label: 'Leave Type',
      required: true,
      options: ['Annual', 'Sick', 'Compassionate', 'Unpaid', 'Study'],
    });
    const startD = Object.assign(makeField('date'), { label: 'Start Date', required: true });
    const endD = Object.assign(makeField('date'), { label: 'End Date', required: true });
    const dur = Object.assign(makeField('duration'), { label: 'Duration (days)', required: true });
    dur.durationCfg = { startFieldId: startD.id, endFieldId: endD.id, unit: 'days' };
    empName.guard = { enabled: true, keys: [empName.id, startD.id, endD.id] };

    s1.rows = [[empName, leaveTy], [startD, endD, dur]];
    s1.actions = [makeAction('submit')];
    s1.routing.actorRoles = ['Initiator'];
    s1.routing.timeBudget = { value: 0, unit: 'minutes' };

    const s2 = this.wf.stages[2];
    this.mirrorAllStage1Into(2);
    s2.actions = [
      Object.assign(makeAction('approve'), { requireComment: false }),
      Object.assign(makeAction('reject'), { requireComment: true }),
      makeAction('escalate'),
    ];
    s2.routing.actorRoles = ['First Approver'];
    s2.routing.timeBudget = { value: 1, unit: 'days' };
    s2.routing.escalationTarget = 'Second Approver';
    s2.routing.rejectionTarget = { type: 'previous' };

    const s3 = this.wf.stages[3];
    this.mirrorAllStage1Into(3);
    s3.actions = [
      Object.assign(makeAction('approve'), { requireComment: false }),
      Object.assign(makeAction('reject'), { requireComment: true }),
    ];
    s3.routing.actorRoles = ['Second Approver'];
    s3.routing.timeBudget = { value: 2, unit: 'days' };
    s3.routing.secondApprover = true;
    s3.routing.rejectionTarget = { type: 'previous' };
    s3.routing.conditionalRoute = {
      enabled: true,
      conditions: [
        { join: 'AND', fieldId: dur.id, op: 'gt', value: '5' },
        { join: 'OR', fieldId: leaveTy.id, op: 'equals', value: 'Compassionate' },
        { join: 'OR', fieldId: leaveTy.id, op: 'equals', value: 'Unpaid' },
        { join: 'OR', fieldId: leaveTy.id, op: 'equals', value: 'Study' },
      ],
    };

    this.markDirty();
    this.showToast(`Loaded: ${this.wf.name}`);
  }

  private loadSecurityExample(): void {
    this.wf.name = 'SF-SEC-001 Security Evidence Log';
    this.wf.tier = 'Managed';
    this.resetStages(3);

    const s1 = this.wf.stages[1];
    const refn = Object.assign(makeField('refnum'), { label: 'Incident Reference', required: true });
    refn.refnumCfg = { prefix: 'INC-', padding: 4 };
    const officer = Object.assign(makeField('dropdown'), {
      label: 'Officer Name',
      required: true,
      options: ['Officer Daniels', 'Officer Khan', 'Officer Park'],
    });
    const ts = Object.assign(makeField('timestamp'), { label: 'Date & Time', required: true });
    const loc = Object.assign(makeField('text'), { label: 'Location / Scene', required: true });
    const itype = Object.assign(makeField('dropdown'), {
      label: 'Incident Type',
      required: true,
      options: ['Trespass', 'Theft', 'Vandalism', 'Medical', 'Suspicious Activity'],
    });
    const desc = Object.assign(makeField('longtext'), { label: 'Description', required: true });
    const evid = Object.assign(makeField('file'), { label: 'Evidence Items' });
    evid.fileCfg = { types: 'image,audio,video', maxFiles: 12, maxSizeMB: 100, perFileNotes: true };

    s1.rows = [[refn, officer, ts], [loc, itype], [desc], [evid]];
    s1.actions = [makeAction('submit')];
    s1.routing.actorRoles = ['Initiator'];
    s1.routing.immutable = true;
    s1.routing.notifications.perItem = true;
    s1.routing.notifications.perItemRecipientField = officer.id;
    s1.routing.notifications.perItemEventLabel = 'New evidence submitted for review';
    s1.routing.timeBudget = { value: 0, unit: 'minutes' };

    const s2 = this.wf.stages[2];
    this.mirrorAllStage1Into(2);
    s2.actions = [
      Object.assign(makeAction('accept'), { requireComment: false }),
      Object.assign(makeAction('reject'), { requireComment: true, label: 'Reject (returns to officer)' }),
      makeAction('escalate'),
    ];
    s2.routing.actorRoles = ['First Approver'];
    s2.routing.timeBudget = { value: 2, unit: 'hours' };
    s2.routing.escalationTarget = 'Second Approver';
    s2.routing.rejectionTarget = { type: 'previous' };

    const s3 = this.wf.stages[3];
    this.mirrorAllStage1Into(3);
    s3.actions = [Object.assign(makeAction('accept'), { label: 'Close & Archive' })];
    s3.routing.actorRoles = ['Second Approver'];
    s3.routing.timeBudget = { value: 24, unit: 'hours' };
    s3.routing.secondApprover = true;
    s3.routing.retention = { store: true, days: 1825, autoPurge: false };

    this.markDirty();
    this.showToast(`Loaded: ${this.wf.name}`);
  }

  /* ============================================================
     Menus / confirm modal / toast
     ============================================================ */
  toggleLoadMenu(ev: Event): void {
    ev.stopPropagation();
    this.loadMenuOpen = !this.loadMenuOpen;
    this.overflowMenuOpen = false;
    this.saveMenuOpen = false;
  }
  toggleOverflowMenu(ev: Event): void {
    ev.stopPropagation();
    this.overflowMenuOpen = !this.overflowMenuOpen;
    this.loadMenuOpen = false;
    this.saveMenuOpen = false;
  }
  toggleSaveMenu(ev: Event): void {
    ev.stopPropagation();
    this.saveMenuOpen = !this.saveMenuOpen;
    this.loadMenuOpen = false;
    this.overflowMenuOpen = false;
  }
  closeMenus(): void {
    this.loadMenuOpen = false;
    this.overflowMenuOpen = false;
    this.saveMenuOpen = false;
  }
  @HostListener('document:click')
  onDocumentClick(): void {
    this.loadMenuOpen = false;
    this.overflowMenuOpen = false;
    this.saveMenuOpen = false;
    this.groupDropdownOpen = false;
  }

  saveDraftFromMenu(): void {
    this.saveMenuOpen = false;
    void this.saveDraft();
  }

  savePublishFromMenu(): void {
    this.saveMenuOpen = false;
    void this.saveAndPublish();
  }

  private async saveAndPublish(): Promise<void> {
    if (this.saving) return;
    if (this.hasUnsavedChanges() || !this.lastValidationPassed) {
      const result = this.runValidation();
      if (!result.passed) {
        this.validationPanelOpen = true;
        this.showToast('Fix validation issues before publishing.', 'alert-triangle');
        return;
      }
    }
    await this.saveDraft();
    if (this.hasUnsavedChanges() || !this.workflowTemplateId) return;
    this.confirmPublish();
  }

  private emptyConfirm(): ConfirmState {
    return {
      open: false,
      title: '',
      body: '',
      okLabel: 'Confirm',
      okClass: 'danger',
      kind: 'plain',
      inFlight: 0,
      details: [],
      onConfirm: null,
    };
  }
  private showConfirm(opts: {
    title: string;
    body: string;
    okLabel?: string;
    okClass?: string;
    details?: string[];
    onConfirm: () => void;
  }): void {
    this.confirmOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.confirm = {
      open: true,
      title: opts.title,
      body: opts.body,
      okLabel: opts.okLabel || 'Confirm',
      okClass: opts.okClass || 'danger',
      kind: 'plain',
      inFlight: 0,
      details: opts.details || [],
      onConfirm: opts.onConfirm,
    };
    this.changeDetector.detectChanges();
    setTimeout(() => document.querySelector<HTMLElement>('[data-confirm-cancel]')?.focus());
  }
  confirmOk(): void {
    const cb = this.confirm.onConfirm;
    this.confirm = this.emptyConfirm();
    cb && cb();
  }
  confirmCancel(): void {
    const opener = this.confirmOpener;
    this.confirmOpener = null;
    this.confirm = this.emptyConfirm();
    setTimeout(() => opener?.focus());
  }

  showToast(msg: string, icon = '', action?: { label: string; run: () => void }): void {
    this.toastMsg = msg;
    this.toastIcon = icon;
    this.toastAction = action || null;
    this.toastShow = true;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toastShow = false;
      this.toastAction = null;
    }, action ? 6000 : 2400);
  }
  runToastAction(): void {
    const action = this.toastAction;
    this.toastShow = false;
    this.toastAction = null;
    clearTimeout(this.toastTimer);
    action?.run();
  }

  /* ---- notification rules ---- */
  /**
   * Gate the Add button: every existing rule must be complete (has a recipient role) AND not a
   * duplicate before another can be added — resolve issues first.
   */
  canAddNotificationRule(routing: Routing): boolean {
    return routing.notifications.rules.every(
      (rule) => !!rule.recipientRole && !this.isDuplicateNotificationRule(routing, rule)
    );
  }
  addNotificationRule(routing: Routing): void {
    if (!this.canAddNotificationRule(routing)) return;
    routing.notifications.rules.push(makeNotificationRule());
    this.markDirty();
  }
  removeNotificationRule(routing: Routing, ruleId: string): void {
    routing.notifications.rules = routing.notifications.rules.filter((rule) => rule.id !== ruleId);
    this.markDirty();
  }
  /** Flags an exact (channel + trigger + recipient role) duplicate of another rule on the stage. */
  isDuplicateNotificationRule(routing: Routing, rule: NotificationRule): boolean {
    if (!rule.recipientRole) return false;
    return routing.notifications.rules.some(
      (other) =>
        other.id !== rule.id &&
        other.channel === rule.channel &&
        other.trigger === rule.trigger &&
        other.recipientRole === rule.recipientRole
    );
  }
  trackByNotificationRule(_: number, rule: NotificationRule): string {
    return rule.id;
  }

  /* ---- trackBy helpers ---- */
  trackByIndex(i: number): number {
    return i;
  }
  trackByEntryKey = (_: number, entry: StageEntry): string => this.entryKey(entry);
  trackByActionId(_: number, a: ActionBtn): string {
    return a.id;
  }
  trackByAudit(_: number, e: AuditEntry): string {
    return e.id;
  }
}
